import { markReturnToCompanies } from '../../features/contacts/companies/companyRoutes';
import { useSI } from '../../contexts/SIContext';
import { usePageHeaderControls, dispatchPageHeaderControl } from './pageHeaderControls';
import { projectReportIdFromTab } from '../../features/reports/project/projectReportCatalog';
import { useReportHeaderState } from '../../features/reports/reportNavigation';
import { useHubSectionTitle } from './useHubSectionTitle';
import { useDesktopHeaderSearchLayout } from './useDesktopHeaderSearchLayout';
import { fitDesktopHeaderActions } from './headerActions/desktopHeaderFit';
import { GALLERY_FILTERS } from '../../features/takeoff/utils/galleryOrganisation';
import type { DesktopHeaderSearchFilter } from './DesktopHeaderSearch';
import { GlobalDesktopHeaderSearch } from './GlobalDesktopHeaderSearch';
import { DesktopHeaderSearch } from './DesktopHeaderSearch';
import { DesktopHistoryControls } from './DesktopHistoryControls';
import { isNativeShell } from '../../utils/nativeApp';
import { getHeaderSearchValue, type HeaderSearchChange } from './desktopHeaderSearchHistory';
import { useWorkspaceNavigation } from '../../hooks/useWorkspaceNavigation';
import { useAuth } from '../auth/AuthContext';
import { useRecentHeaderPlaces, type RecentHeaderPlace } from './useRecentHeaderPlaces';
import { Fragment, Suspense, useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import {
  Bell,
  MessageSquare,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CalendarClock,
  Camera,
  Search,
  Plus,
  SquarePen,
  Building2,
  CheckSquare,
  Check,
  Download,
  Upload,
  Sparkles,
  Loader2,
  SlidersHorizontal,
  RefreshCw,
  Trash2,
  Pencil,
  Pin,
  CheckCheck,
  Archive as ArchiveIcon,
  Reply,
  ReplyAll,
  Forward,
  MailOpen,
  X,
  CheckCircle2,
  AlertCircle,
  Table as TableIcon,
  Home,
  Crosshair,
  WandSparkles,
  Eye,
  Contrast,
  Map as MapIcon,
  Keyboard,
  Save,
  Percent,
  Send,
  Share2,
  Wand2,
  ChevronsDownUp,
  ChevronsUpDown,
  Settings2,
  ClipboardList,
  Layers,
  Star,
  ArrowUpDown,
  LayoutGrid,
  List,
  Shield,
  FolderOpen,
  MoreHorizontal,
  MoreVertical,
  FileText,
  LifeBuoy,
  Image,
  UserPlus,
} from '@/shared/ui/icons';
import {getBrowserHeaderBackAction, subscribeBrowserHeaderBackAction, type BrowserHeaderBackAction} from './browserHeaderBackAction';
import { parsePrettyProjectPricingPath } from '../../utils/prettyRoutes';
import { Button } from '../../shared/ui/button';
import { Input } from '../../shared/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../shared/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../shared/ui/command';
import { cn } from '../../shared/ui/utils';
import { useSidebar } from '../../shared/ui/sidebar';
import { useNotifications } from '../../features/notifications/NotificationContext';
import { useMessaging } from '../../features/messages/MessagingContext';
import { MessagesChatActionsPill } from './MessagesChatActionsPill';
import { MessagesMembersBreadcrumb } from './MessagesMembersBreadcrumb';
import { buildMessagesMembersHeaderActions, messagesMembersBackAction, messagesMembersTitle, type MessagesMembersHeaderState } from './headerActions/messagesMembers';
import { AddProjectEmployerPill } from './AddProjectEmployerPill';
import { BOQMoveSelectionPill } from './BOQMoveSelectionPill';
import { useNativeBridge } from '../../hooks/useNativeBridge';
import { NativeBridge } from '../../services/NativeBridge';
import { lazyChunk } from '../../utils/lazyChunk';
import { isWindowsDesktop, winStartDragging } from '../tauri/windowChrome';
import { getStageIcon } from '../../utils/stageUtils';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import {
  getNavigatorShellRuntimeInput,
  isTouchShellRuntime,
  nativeShellReplacesWebHeader,
  useTabletSidebarLayout,
} from '../../layout-system';
import { useDesktopFrame, useDesktopFrameTouch } from './desktopFrame';
import { useSectionRailHeaderState } from './useTabletSecondRail';
import { useSectionRailCollapseHeaderState } from './useSectionRailCollapsed';
import { useDictation } from '../../hooks/useDictation';
import type { Project, Company, RateLibrary } from '../../types/database';
import type { ProjectStage } from '../../types/projects';
import type { Workspace } from '../../types/workspace';
import { formatLibraryName } from '../../features/resource-library/utils';
import {
  buildProjectSidebarTabItems,
  getSidebarNavGroups,
  type ProjectSidebarTabContext,
  type ProjectSidebarPermissions,
} from './sidebarNavConfig';
import type { MobileHeaderAction, MobileHeaderSubAction } from './headerActions/types';
import { buildCalendarHeaderActions, type CalendarHeaderState, type TasksSelectionState } from './headerActions/calendar';
import { buildBOQHeaderActions, type BOQApprovalContext } from './headerActions/boq';
import { BOQ_EXPORT_HEADER_EVENTS, type BOQExportHeaderState } from '../../features/boq/hooks/useBOQExportHeaderBridge';
import { SidebarLeftIcon } from '../../shared/ui/SidebarLeftIcon';
import { buildTakeoffHeaderActions, isTakeoffGallerySearchAvailable, type TakeoffHeaderState, type TakeoffPanelState, type TakeoffUploaderDetails } from './headerActions/takeoff';
import { hasActiveVisibleMobileSearch } from './headerActions/mobileSearchPresentation';
import { buildDocumentsHeaderActions, type DocumentsHeaderState } from './headerActions/documents';
import { buildTasksHeaderActions } from './headerActions/tasks';
import { buildPlannerHeaderActions, type PlannerHeaderState } from './headerActions/planner';
import { buildProjectsHeaderActions } from './headerActions/projects';
import { buildEmployersHeaderActions } from './headerActions/employers';
import { buildPipelineHeaderActions, buildWorkspacePipelineHeaderActions } from './headerActions/pipeline';
import { buildEventsHeaderActions, buildEventEditorHeaderActions, type EventEditorHeaderState } from './headerActions/events';
import { buildValuationsCreateAction } from './headerActions/valuations';
import { withoutProjectWriteActions } from './headerActions/projectReadOnly';
import { buildSiteRegisterHeaderActions, SITE_REGISTER_DAY_PILL_ID } from './headerActions/siteRegister';
import { buildRfiFormHeaderActions, buildRfiRegisterHeaderActions } from './headerActions/rfis';
import { SiteRegisterDayPicker } from '../../features/projects/site-register/SiteRegisterDayPicker';
import { buildProjectOverviewHeaderActions } from './headerActions/projectOverview';
import {
  PROJECT_OVERVIEW_REQUEST_STATE_EVENT,
  PROJECT_OVERVIEW_STATE_EVENT,
  type ProjectOverviewHeaderState,
} from '../../features/projects/detail/overview/projectOverviewHeader';
import {
  SITE_REGISTER_REQUEST_STATE_EVENT,
  SITE_REGISTER_STATE_EVENT,
  requestSiteRegisterDay,
  type SiteRegisterHeaderState,
} from '../../features/projects/site-register/siteRegisterHeader';
import {
  RFI_FORM_CLOSE_EVENT,
  RFI_FORM_REQUEST_STATE_EVENT,
  RFI_FORM_STATE_EVENT,
  RFI_REGISTER_REQUEST_STATE_EVENT,
  RFI_REGISTER_STATE_EVENT,
  RFI_SET_QUERY_EVENT,
  type RfiFormHeaderState,
  type RfiRegisterHeaderState,
} from '../../features/templates/rfiRegister';
import {
  buildHSHeaderActions,
  HS_SECTION_LABELS,
  getHSSectionLabel,
  type HSDocumentHeaderState,
  type HSEditorHeaderState,
  type HSHeaderState,
} from './headerActions/hs';
import { buildEmailHeaderActions } from './headerActions/email';
import { buildMessagesHeaderActions } from './headerActions/messages';
import {
  buildResourceLibraryHeaderActions,
  type ResourceLibraryHeaderState,
} from './headerActions/resourceLibrary';
import { buildPlantHeaderActions } from './headerActions/plant';
import { MerchantFilterPill } from './headerActions/MerchantFilterPill';
import { HeaderPillSelect, HeaderPopoverPill } from './headerActions/HeaderPillPrimitives';
import {
  USER_ROLE_FILTER_ALL,
  USER_ROLE_FILTER_OPTIONS,
  WORKSPACE_USERS_CREATE_EVENT,
  WORKSPACE_USERS_INVITE_CANCEL_EVENT,
  WORKSPACE_USERS_INVITE_SUBMIT_EVENT,
  WORKSPACE_USERS_FILTER_EVENT,
  WORKSPACE_USERS_SEARCH_EVENT,
  WORKSPACE_USERS_STATE_EVENT,
  WORKSPACE_USER_FORM_CANCEL_EVENT,
  WORKSPACE_USER_FORM_SAVE_EVENT,
  WORKSPACE_USER_FORM_STATE_EVENT,
} from '../../features/users/userRoles';
import {
  EMPLOYERS_FILTER_EVENT,
  EMPLOYERS_SEARCH_EVENT,
  EMPLOYERS_STATE_EVENT,
  EMPLOYER_DETAIL_STATE_EVENT,
  EMPLOYER_FORM_STATE_EVENT,
  EMPLOYER_STATUS_FILTER_ALL,
  EMPLOYER_STATUS_FILTER_OPTIONS,
  type EmployerStatusFilter,
} from '../../features/employers/constants';
import {
  OUTREACH_APPROVALS_APPROVE_ALL_EVENT,
  OUTREACH_APPROVALS_BACK_EVENT,
  OUTREACH_APPROVALS_REJECT_ALL_EVENT,
  OUTREACH_APPROVALS_STATE_EVENT,
  OUTREACH_APPROVALS_SUBMIT_EVENT,
  PROSPECT_DETAIL_BACK_EVENT,
  PROSPECT_DETAIL_CANCEL_EVENT,
  PROSPECT_DETAIL_SAVE_EVENT,
  PROSPECT_DETAIL_STATE_EVENT,
  type OutreachApprovalsHeaderState,
} from '../../features/outreach/constants';
import {
  PLANT_FILTER_EVENT,
  PLANT_SEARCH_EVENT,
  PLANT_STATE_EVENT,
  PLANT_DETAIL_STATE_EVENT,
  PLANT_STATUS_FILTER_ALL,
  plantFilterOptions,
  type PlantDetailState,
  type PlantListState,
  type PlantStatusFilter,
} from '../../features/plant/constants';
import {
  WORKSPACE_BOTS_STATE_EVENT,
  WORKSPACE_BOTS_SEARCH_EVENT,
  WORKSPACE_BOTS_CREATE_EVENT,
  WORKSPACE_BOTS_SAVE_EVENT,
  WORKSPACE_BOTS_CANCEL_EVENT,
  WORKSPACE_BOTS_BACK_EVENT,
  type WorkspaceBotsHeaderState,
} from '../../features/workspaces/bots/constants';
import {
  WORKSPACE_ACTIVITY_CATEGORY_OPTIONS,
  WORKSPACE_ACTIVITY_EXPORT_EVENT,
  WORKSPACE_ACTIVITY_FILTER_EVENT,
  WORKSPACE_ACTIVITY_SEARCH_EVENT,
  WORKSPACE_ACTIVITY_STATE_EVENT,
} from '../../features/settings/activityHeaderEvents';
import { HeaderOverflowSheet } from './headerActions/HeaderOverflowSheet';
import {
  COMPACT_TITLE_RESERVE,
  HEADER_CLUSTER_GAP,
  HEADER_PILL_BASE,
  fitCluster,
  flattenOverflowActions,
  measureClusterFloor,
  measureFittedCluster,
  overflowBadgeTone,
  sumOverflowBadges,
} from './headerActions/headerOverflow';
import { headerActionName } from './headerActions/headerBadge';
import { SupportModal } from '../../features/support/SupportModal';
import {
  PROCUREMENT_HEADER_REQUEST_EVENT,
  PROCUREMENT_HEADER_STATE_EVENT,
  PROCUREMENT_SEARCH_EVENT,
  PROCUREMENT_SET_GROUP_EVENT,
  PROCUREMENT_SET_RESOURCE_EVENT,
  PROJECT_PROCUREMENT_SECTIONS,
  type ProjectProcurementHeaderState,
} from '../../features/projects/procurement/navigation';
import { isProjectAdminTab } from '../../features/projects/admin/navigation';
import {
  INTERIM_BREAKDOWN_GROUPS_REQUEST_STATE_EVENT,
  INTERIM_BREAKDOWN_GROUPS_STATE_EVENT,
  INTERIM_BREAKDOWN_TOGGLE_ALL_GROUPS_EVENT,
  INTERIM_BREAKDOWN_SET_VIEW_EVENT,
  type InterimBookingView,
  type InterimBreakdownGroupsHeaderState,
} from '../../features/interim/breakdownHeaderEvents';
import {
  TEMPLATES_BACK_EVENT,
  TEMPLATES_HEADER_REQUEST_EVENT,
  TEMPLATES_HEADER_STATE_EVENT,
  TEMPLATES_PREVIEW_EVENT,
  TEMPLATES_RESET_EVENT,
  TEMPLATES_SAVE_EVENT,
  TEMPLATES_SEARCH_EVENT,
  type TemplatesHeaderState,
} from '../../features/templates/navigation';

const ProjectBOQApprovalAction = lazyChunk(() => import('../../features/projects/ProjectBOQApprovalAction'), (m) => m.ProjectBOQApprovalAction);

interface UnifiedAppHeaderProps {
  pageTitle?: string;
  pageSubtitle?: string;
  currentView?: string;
  onNavigate?: (view: string, metadata?: any) => void;
  /** Which profile section is open ('overview' | 'details' | ...). Drives the
   *  profile back pill: a section walks back to Overview (the mobile Account
   *  list is the way in, so back must be the way out), Overview exits to the
   *  dashboard. */
  profileActiveTab?: string;
  onProfileTabChange?: (tab: string) => void;
  /** Open Preferences section; null is the phone's section list. A phone pane
   *  walks back to the list through onPreferencesTabChange(null). */
  preferencesActiveTab?: string | null;
  onPreferencesTabChange?: (section: string | null) => void;
  onToggleNotifications?: () => void;
  onToggleMessages?: (chatId?: string, messageId?: string | null) => void;
  onToggleSupport?: () => void;
  onToggleSI?: () => void;
  supportInspectorOpen?: boolean;
  /** Desktop only: when true, the right side of the header surfaces the
   *  notifications side-panel chrome (close, select, mark-read, delete,
   *  done) instead of the regular Messages/Notifications pills. */
  notificationOpen?: boolean;
  /** Desktop only: when true, the right side of the header surfaces the
   *  messages side-panel chrome (close, search, new, filter) instead of
   *  the regular Messages/Notifications pills. */
  messagePanelOpen?: boolean;
  /** Calendar only: opens / closes the Task Queue right-side panel. The
   *  pill paints active when `taskQueueOpen` is true. */
  taskQueueOpen?: boolean;
  onToggleTaskQueue?: () => void;
  /** Calendar only: opens / closes the Scheduler (booking link) right-side
   *  panel. Pill paints active when `bookingPanelOpen` is true. */
  bookingPanelOpen?: boolean;
  onToggleBooking?: () => void;
  // Project detail tabs
  isProjectDetailView?: boolean;
  projectActiveTab?: string;
  onProjectTabChange?: (tab: string) => void;
  projectTabProps?: ProjectSidebarTabContext & { internalUserIds?: string[] };
  projectPermissions?: ProjectSidebarPermissions | null;
  // Project list filter props
  isProjectListView?: boolean;
  projectListProps?: {
    searchQuery: string;
    setSearchQuery: (query: string) => void;
    searchOpen: boolean;
    setSearchOpen: (open: boolean) => void;
    matchingProjects: Project[];
    onViewProject: (projectId: string, projectName: string) => void;
    clientFilter: string;
    setClientFilter: (filter: string) => void;
    allClients: Company[];
    activeView: 'overview' | ProjectStage;
    setActiveView: (view: 'overview' | ProjectStage) => void;
    projectsByStage: Record<ProjectStage, Project[]>;
    currentProjectCount: number;
    onAddProject?: () => void;
    activeStages: ProjectStage[];
    sessionStageFilter: ProjectStage[] | null;
    onToggleSessionStage: (stage: ProjectStage) => void;
    onResetSessionFilter: () => void;
  };
  [key: string]: any;
}

/**
 * Visual cluster a header action belongs to. Render-time inserts a
 * small gap between consecutive actions whose group differs, so the
 * trailing strip reads as a curated set of tiers rather than one
 * undifferentiated battery of icons.
 *
 * Groups (left → right on the trailing edge):
 *   - `progress`  — informational pills (BOQ import progress)
 *   - `view`      — view-state toggles (Filter)
 *   - `page`      — generic page-scoped tools (default)
 *   - `create`    — additive actions (Add row, Import)
 *   - `modify`    — transform existing data (OH&P, Delete)
 *   - `output`    — push out (Export)
 *   - `app`       — app-wide always-on (Notifications)
 *
 * The group type and the MobileHeaderAction / MobileHeaderSubAction
 * interfaces are defined in ./headerActions/types and imported above.
 * They are re-exported here only for downstream consumers that import
 * directly from this file.
 */

/** Implementation statuses the Trigger Catalog filters by. Module-level so
 *  the header's filter menu and the page agree on the wording without the
 *  page having to publish a fixed list on every state change. */
const TRIGGER_CATALOG_STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'live', label: 'Live' },
  { value: 'partial', label: 'Partial' },
  { value: 'planned', label: 'Planned' },
];

/** Search actions that the desktop header renders as the single persistent
 *  centred field instead of their per-view morph pill. Module-level so it
 *  isn't rebuilt on every header render. */
const DESKTOP_GLOBAL_SEARCH_IDS = new Set([
  'messages-members-search',
  'page-controls-search',
  'takeoff-gallery-search',
  'trigger-catalog-search',
  'contacts-search',
  'resource-library-search',
  'workspaces-search',
  'workspace-bots-search',
  'workflows-search',
  'projects-search',
  'messages-search',
  'documents-search',
  'boq-search',
  'boq-importer-search',
  'project-members-search',
  'project-notes-search',
  'project-activity-search',
  'project-email-search',
  'employers-search',
  'plant-search',
]);

/** Status options for the workspace-detail Invoices sub-tab header filter —
 *  identical set/order to InvoiceList's own (now header-hosted) Select. */
const INVOICE_STATUS_FILTER_OPTIONS = [
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'all', label: 'All' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'due', label: 'Due' },
  { value: 'paid', label: 'Paid' },
];

// HeaderPillSelect and HeaderPopoverPill now live in
// ./headerActions/HeaderPillPrimitives so other header surfaces can render
// identical pills without importing this whole file.
// MobileHeaderSubAction and MobileHeaderAction are imported from
// ./headerActions/types so builders can share the same type without
// a circular dependency. The interfaces are identical to their
// original inline declarations.

/**
 * SF-Symbols-style `sidebar.trailing` glyph used as the
 * close-the-right-panel action. Rendered as a filled SVG (vs. the lucide
 * stroked panel-close icon) so the visual matches Apple's chrome
 * exactly. Accepts the same `style` / `strokeWidth` / `className` props
 * the rest of the header pills pass to lucide icons; `strokeWidth` is
 * accepted but ignored (this glyph is filled, not stroked) and
 * `style.color` is honoured via `fill="currentColor"`.
 */
const SIDEBAR_TRAILING_ASPECT = 23.3887 / 17.9785;
function SidebarTrailingIcon({
  style,
  className,
}: {
  style?: React.CSSProperties;
  strokeWidth?: number | string;
  className?: string;
}) {
  const heightVal = style?.height;
  const numericHeight =
    typeof heightVal === 'number'
      ? heightVal
      : typeof heightVal === 'string' && heightVal.endsWith('px')
        ? parseFloat(heightVal)
        : 17;
  const computedWidth = numericHeight * SIDEBAR_TRAILING_ASPECT;
  return (
    <svg
      viewBox="0 0 23.3887 17.9785"
      xmlns="http://www.w3.org/2000/svg"
      fill="currentColor"
      aria-hidden="true"
      style={{ ...style, width: computedWidth, height: numericHeight }}
      className={className}
    >
      <path d="M14.082 16.7188L15.6152 16.7188L15.6152 1.28906L14.082 1.28906ZM4.12109 17.9785L19.1504 17.9785C21.6113 17.9785 23.0273 16.4941 23.0273 13.8574L23.0273 4.13086C23.0273 1.49414 21.6113 0 19.1504 0L4.12109 0C1.49414 0 0 1.49414 0 4.13086L0 13.8574C0 16.4941 1.49414 17.9785 4.12109 17.9785ZM4.13086 16.4062C2.50977 16.4062 1.57227 15.4785 1.57227 13.8574L1.57227 4.13086C1.57227 2.50977 2.50977 1.57227 4.13086 1.57227L18.8965 1.57227C20.5176 1.57227 21.4551 2.50977 21.4551 4.13086L21.4551 13.8574C21.4551 15.4785 20.5176 16.4062 18.8965 16.4062ZM17.4902 5.20508L19.5801 5.20508C19.8828 5.20508 20.1367 4.94141 20.1367 4.6582C20.1367 4.36523 19.8828 4.11133 19.5801 4.11133L17.4902 4.11133C17.1973 4.11133 16.9336 4.36523 16.9336 4.6582C16.9336 4.94141 17.1973 5.20508 17.4902 5.20508ZM17.4902 7.73438L19.5801 7.73438C19.8828 7.73438 20.1367 7.4707 20.1367 7.17773C20.1367 6.88477 19.8828 6.64062 19.5801 6.64062L17.4902 6.64062C17.1973 6.64062 16.9336 6.88477 16.9336 7.17773C16.9336 7.4707 17.1973 7.73438 17.4902 7.73438ZM17.4902 10.2539L19.5801 10.2539C19.8828 10.2539 20.1367 10.0098 20.1367 9.7168C20.1367 9.42383 19.8828 9.16992 19.5801 9.16992L17.4902 9.16992C17.1973 9.16992 16.9336 9.42383 16.9336 9.7168C16.9336 10.0098 17.1973 10.2539 17.4902 10.2539Z" />
    </svg>
  );
}

/** Scroll distance (px) over which the iOS-style large title collapses into the
 *  compact inline nav-bar title. Matches the large-title div's rendered height
 *  (41px lineHeight + 11px paddingBottom = 52px). */
const LARGE_TITLE_HEIGHT = 52;

/** Leading-action IDs that suppress the mobile large title: back-chevrons
 *  without the `back-` prefix that `pushBackAction` stamps, plus edit-mode
 *  takeovers whose views don't reserve large-title clearance. */
const LARGE_TITLE_BACK_IDS = new Set(['messages-back', 'rate-item-back']);

/** Mobile main-row gutters. Also the fallback the overflow fit subtracts from
 *  the viewport before the ResizeObserver has reported a content-box width. */
const MOBILE_ROW_PADDING_LEFT = 16;
const MOBILE_ROW_PADDING_RIGHT = 20;

/** Pills `renderMobileHeaderAction` drops on mobile. The overflow fit has to
 *  filter these out too — left in, they'd spend row budget and push real pills
 *  into a sheet that then lists nothing. */
const MOBILE_HIDDEN_ACTION_IDS = new Set([
  // Notifications stay reachable via the bottom nav; support via the sheet.
  'notifications',
  'support',
]);

/** Leading pills that survive on mobile while a morph search owns the row —
 *  the search inputs themselves plus the compose morph. */
const MOBILE_SEARCH_ACTIVE_LEADING_IDS = new Set([
  'messages-members-search',
  'page-controls-search',
  'takeoff-gallery-search',
  'documents-search',
  'projects-search',
  'messages-search',
  'messages-new',
  'boq-search',
  'boq-importer-search',
  'contacts-search',
  'workspaces-search',
  'workspace-bots-search',
  'resource-library-search',
  'employers-search',
  'plant-search',
  'project-email-search',
]);

/** Leading search pills `renderLeadingAction` always returns null for on
 *  mobile, because each is replaced by a persistent search bar in the page body
 *  rather than a header morph. They stay in the action list for the desktop
 *  render path, so the overflow fit has to drop them — counted, each one burns
 *  44pt of budget on a pill that occupies no DOM space and needlessly pushes a
 *  real pill into the sheet. */
const MOBILE_HIDDEN_LEADING_SEARCH_IDS = new Set([
  'projects-search',
  'project-members-search',
  'messages-search',
]);

/** One shared glass capsule behind a run of adjacent icon-only pills, which is
 *  how Liquid Glass groups toolbar items: symbol buttons of the same function
 *  share a background, and a new container starts when the function changes.
 *  Geometry matches a single pill's fill exactly (36 tall, inset 4) so a run of
 *  one is indistinguishable from an ungrouped pill. */
function HeaderPillGroup({ children }: { children: React.ReactNode }) {
  return (
    <div data-slot="header-pill-group" className="relative flex items-center" style={{ pointerEvents: 'auto' }}>
      <span
        aria-hidden="true"
        className="pointer-events-none"
        style={{
          position: 'absolute',
          left: 4,
          right: 4,
          height: 36,
          top: 'calc(50% - 18px)',
          zIndex: 0,
          borderRadius: 296,
          background: 'var(--header-pill-fill)',
          mixBlendMode: 'var(--header-pill-blend)' as any,
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          boxShadow: '0px 8px 24px 0px rgba(0,0,0,0.08), var(--glass-inset, none)',
        }}
      />
      {children}
    </div>
  );
}

/** Split a cluster into runs that should share one capsule. A run is adjacent
 *  pills with the same effective `group` that are ALL icon-only — a labelled
 *  pill always stands alone, because Liquid Glass forbids mixing text and
 *  symbols in one background and gives text buttons their own container. */
function groupHeaderRuns(actions: MobileHeaderAction[]): MobileHeaderAction[][] {
  const runs: MobileHeaderAction[][] = [];
  for (const action of actions) {
    const solo = !!action.label;
    const prev = runs[runs.length - 1];
    const prevSolo = prev ? !!prev[0].label : false;
    const sameGroup = prev && (prev[0].group ?? 'page') === (action.group ?? 'page');
    if (prev && sameGroup && !solo && !prevSolo) prev.push(action);
    else runs.push([action]);
  }
  return runs;
}

/** Trailing pills whose rendering is id-intercepted inside `renderLeadingAction`
 *  — native `<select>` pills and the merchant filter's two-select panel. They
 *  live in `trailingActions` but must still be routed through that function or
 *  they degrade to a plain pill, and for the merchant filter (which carries
 *  neither `onClick` nor `subActions`) to a DEAD one.
 *
 *  Module-level and shared by BOTH render paths on purpose: this started life as
 *  an inline condition on the desktop path only, which left the merchant filter
 *  inert on mobile. One Set means adding an intercept can't half-land again. */
const TRAILING_INTERCEPT_IDS = new Set([
  'boq-view',
  'workspace-invoices-filter',
  'sales-invoices-filter',
  'workspace-users-filter',
  'workspace-activity-filter',
  'resource-library-merchant-filter',
  'employers-filter',
  'plant-filter',
  SITE_REGISTER_DAY_PILL_ID,
]);

/** Leading pills that draw their OWN chrome inside `renderLeadingAction` —
 *  the morph-search inputs and the native-select pills. They must keep going
 *  through that function and can never join a shared capsule (a morph that
 *  expands into a text field cannot sit inside a fixed pill background). */
const LEADING_SELF_CHROME_IDS = new Set([
  'page-controls-search',
  ...TRAILING_INTERCEPT_IDS,
  'takeoff-gallery-search',
  'boq-importer-search',
  'boq-search',
  'contacts-search',
  'documents-search',
  'employers-search',
  'plant-search',
  'messages-new',
  'messages-search',
  'project-members-search',
  'projects-search',
  'project-email-search',
  'resource-library-search',
  'trigger-catalog-search',
  'workflows-search',
  'workspaces-search',
  'workspace-bots-search',
]);

/** Leading pills the overflow sheet must never claim: the back chevron, so
 *  navigation stays one tap away, and the morph-search pills, which
 *  `renderSearchMorphPill` special-cases by id and a flat sheet row could not
 *  reproduce. */
function isPinnedLeadingAction(action: MobileHeaderAction): boolean {
  return action.id.startsWith('back-')
    // Dismiss controls for full-screen takeovers. Only checked for leading
    // actions, so this can't pin a trailing close. Without it the sole exit
    // from a takeover could be collapsed into the overflow sheet.
    || action.id.endsWith('-close')
    || LARGE_TITLE_BACK_IDS.has(action.id)
    || DESKTOP_GLOBAL_SEARCH_IDS.has(action.id)
    || action.id === 'messages-new';
}

/** Nothing in the trailing cluster is pinned by identity — only pills that
 *  can't become a sheet row at all, which `fitCluster` already keeps. */
function neverPinned(): boolean {
  return false;
}

export function UnifiedAppHeader({
  user,
  pageTitle,
  pageSubtitle,
  currentView,
  onNavigate,
  profileActiveTab,
  onProfileTabChange,
  preferencesActiveTab,
  onPreferencesTabChange,
  onToggleNotifications,
  onToggleMessages,
  onToggleSupport,
  onToggleSI,
  supportInspectorOpen,
  notificationOpen,
  messagePanelOpen,
  taskQueueOpen,
  onToggleTaskQueue,
  bookingPanelOpen,
  onToggleBooking,
  isProjectDetailView,
  projectActiveTab,
  onProjectTabChange,
  projectTabProps,
  projectPermissions,
  isProjectListView,
  projectListProps,
  systemSettingsActiveTab,
}: UnifiedAppHeaderProps) {
  const si = useSI();
  const { activeWorkspaceId } = useAuth();
  const { unreadCount: notificationsUnreadCount } = useNotifications();
  const { unreadCount: messagesUnreadCount } = useMessaging();
  const nativeBridge = useNativeBridge();
  const isDesktopViewport = useMediaQuery('(min-width: 1024px)');
  const hasRoomForCalendarLabels = useMediaQuery('(min-width: 1536px)');
  const hasRoomForCalendarPrimaryLabels = useMediaQuery('(min-width: 1280px)');
  const usesTabletSidebarLayout = useTabletSidebarLayout();
  const isDesktopFrame = useDesktopFrame();
  // On a tablet the main rail carries Alerts and Support, so the header leaves them out. Messages
  // steps aside in desktop-touch.css instead, and only while the rail still lists it.
  const railReplacesHeaderShortcuts = useDesktopFrameTouch();
  const isTouchRuntime = useMemo(
    () => !isDesktopFrame && isTouchShellRuntime(getNavigatorShellRuntimeInput(isDesktopViewport)),
    [isDesktopFrame, isDesktopViewport],
  );
  const usesMaterialMobileChrome = !isDesktopViewport && !nativeBridge.isNative;
  const { setOpenMobile: setSidebarOpenMobile } = useSidebar();
  const isClientUser = (user as { role?: string } | undefined)?.role === 'client';
  // Applications tab: only super_admin and internal can create or save.
  // workspace_owner/admin are read-only viewers. Derived from the `user`
  // prop that AuthenticatedWorkspace already passes here.
  const canManageApplications =
    (user as { role?: string } | undefined)?.role === 'super_admin' ||
    (user as { role?: string } | undefined)?.role === 'internal';

  /**
   * Initiates a native window drag from empty desktop top-header space.
   * Only fires in native shells, primary button only, and only when the pointer
   * is not over an interactive descendant.
   */
  const handleHeaderDrag = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const isWindowsShell = isWindowsDesktop();
    if (!NativeBridge.isMacShell() && !isWindowsShell) return;
    if (e.button !== 0) return;
    if ((e.target as HTMLElement).closest('button, a, input, textarea, select, [role="button"], [data-no-window-drag]')) return;
    if (isWindowsShell) {
      void winStartDragging();
      return;
    }
    window.QuantisNativeBridge?.send?.('beginWindowDrag', {});
  }, []);

  const [clientFilterOpen, setClientFilterOpen] = useState(false);

  // Listens for overdue-count broadcasts from the CalendarPage so the
  // mobile header can show a badge on its Task Queue button without
  // prop-drilling calendar state through the workspace.
  const [calendarOverdueCount, setCalendarOverdueCount] = useState(0);
  useEffect(() => {
    const onCountChange = (e: Event) => {
      const detail = (e as CustomEvent<{ count?: number }>).detail;
      setCalendarOverdueCount(detail?.count ?? 0);
    };
    window.addEventListener('calendar:overdue-count', onCountChange);
    return () => window.removeEventListener('calendar:overdue-count', onCountChange);
  }, []);
  // Calendar view + headerLabel bridge — the header now owns the
  // toolbar (Today, prev/next, view picker), so it needs to know which
  // view is active and what label to render in place of the page title.
  // Cleared on unmount.
  const [calendarHeaderState, setCalendarHeaderState] = useState<CalendarHeaderState | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<typeof calendarHeaderState>).detail;
      setCalendarHeaderState(detail ?? null);
    };
    window.addEventListener('calendar:state-change', onChange);
    return () => window.removeEventListener('calendar:state-change', onChange);
  }, []);

  // Ticked task rows; TasksView publishes the count and clears it on unmount.
  const [tasksSelection, setTasksSelection] = useState<TasksSelectionState | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      setTasksSelection((e as CustomEvent<TasksSelectionState | null>).detail ?? null);
    };
    window.addEventListener('tasks:selection-change', onChange);
    return () => window.removeEventListener('tasks:selection-change', onChange);
  }, []);

  // BOQ page state — same window-event bridge pattern as the calendar.
  // BOQBuilder broadcasts visibility flags, the header renders pills
  // accordingly. Pills dispatch click events back the other way.
  const [boqHeaderState, setBoqHeaderState] = useState<{
    canImport: boolean;
    /** Gates the "New Sheet" entry in the Create pill (hidden for clients). */
    canAddSheet?: boolean;
    /** True when the BOQ has at least one measured row that OH&P can be applied to. */
    canApplyOHP?: boolean;
    canExport: boolean;
    isExporting: boolean;
    selectedCount: number;
    bills: Array<{ id: string; label: string }>;
    /** Active bill / summary tab id. Used by the mobile title pull-down so
     *  the header can show "Measured Works ⌄" instead of duplicating a
     *  picker row beneath it. */
    activeBillId?: string | null;
    /** Display label for the current bill (e.g. "Measured Works") or the
     *  active summary tab (e.g. "Materials"). Drives the mobile title. */
    activeBillLabel?: string;
    /** Contextual placeholder for the persistent BOQ search field. */
    searchPlaceholder?: string;
    /** Allows project users with bill-management access to edit or delete the
     *  currently selected real bill. Synthetic summary views remain read-only. */
    canManageBills?: boolean;
    /** Synthetic roll-up tabs (Summary, Labour, Materials, Plant) appended
     *  below the bills in the mobile title menu. */
    summaryTabs?: Array<{ id: string; label: string }>;
    /** Active BOQ filter mode (the "N not linked" chip switches 'unlinked'
     *  on and off). 'all' = no filter. */
    filterMode?: 'all' | 'priced' | 'unpriced' | 'excluded' | 'unlinked';
    /** Measured lines in the bill on screen with no takeoff linked. Drives
     *  the "N not linked" chip, which is hidden at zero. */
    takeoffUnlinkedCount?: number;
    /** Project + user context for the Approve BOQ pill. Absent when the
     *  ids needed to render the pill aren't known yet. */
    approval?: BOQApprovalContext;
    /** Grand total for the active bill — shown in the desktop header title slot. */
    activeBillTotal?: number;
  } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<typeof boqHeaderState>).detail;
      setBoqHeaderState(detail ?? null);
    };
    window.addEventListener('boq:state-change', onChange);
    return () => window.removeEventListener('boq:state-change', onChange);
  }, []);

  const isBoqExportRoute = typeof window !== 'undefined'
    && parsePrettyProjectPricingPath(window.location.pathname)?.page === 'export';
  const [boqExportHeaderState, setBoqExportHeaderState] = useState<BOQExportHeaderState | null>(null);
  useEffect(() => {
    const onChange = (event: Event) => {
      setBoqExportHeaderState((event as CustomEvent<BOQExportHeaderState | null>).detail ?? null);
    };
    window.addEventListener(BOQ_EXPORT_HEADER_EVENTS.stateChange, onChange);
    window.dispatchEvent(new CustomEvent(BOQ_EXPORT_HEADER_EVENTS.requestState));
    return () => window.removeEventListener(BOQ_EXPORT_HEADER_EVENTS.stateChange, onChange);
  }, []);

  // Approval pill JSX, rendered identically in the mobile + desktop trailing
  // clusters. Approval success dispatches `boq:approved` so BOQBuilder can
  // do its optimistic project-stage / BOQ-status update. Notification
  // recipients come from the project's assigned staff (`projectTabProps`),
  // not the BOQ bridge — so the list reflects who's actually on the project.
  const approval = boqHeaderState?.approval;
  const approvalInternalUserIds = projectTabProps?.internalUserIds;
  const boqApprovalPill = useMemo(() => {
    if (!approval) return null;
    return (
      <Suspense fallback={null}>
        <ProjectBOQApprovalAction
          projectId={approval.projectId}
          projectName={approval.projectName}
          projectStage={approval.projectStage}
          boqStatus={approval.boqStatus}
          isClient={approval.isClient}
          currentUserId={approval.currentUserId}
          currentUserName={approval.currentUserName}
          internalUserIds={approvalInternalUserIds}
          onApproved={() => window.dispatchEvent(new CustomEvent('boq:approved'))}
        />
      </Suspense>
    );
  }, [approval, approvalInternalUserIds]);

  // Documents page state — same window-event bridge as BOQ. The Documents
  // toolbar lifts every action up here so the page itself stays free of
  // chrome (just the file grid + upload progress strip). Pills dispatch
  // click events back; DocumentManagementTab runs the actual handler.
  const [documentsHeaderState, setDocumentsHeaderState] = useState<DocumentsHeaderState | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<typeof documentsHeaderState>).detail;
      setDocumentsHeaderState(detail ?? null);
    };
    window.addEventListener('documents:state-change', onChange);
    return () => window.removeEventListener('documents:state-change', onChange);
  }, []);

  // Enquiries pipeline board state — same window-event bridge. The board lifts
  // its Filter (by service) and Sort controls up here so they live in the
  // global header / native toolbar; the ConsultancyPipelineView owns the
  // actual state and listens for pipeline:set-service / pipeline:set-sort.
  const [pipelineHeaderState, setPipelineHeaderState] = useState<{
    serviceFilter: string;
    sortKey: 'newest' | 'oldest' | 'value' | 'idle';
    services: [string, string][];
  } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<typeof pipelineHeaderState>).detail;
      setPipelineHeaderState(detail ?? null);
    };
    window.addEventListener('pipeline:state-change', onChange);
    // Cold-load into Enquiries: child (view) effects run before this ancestor
    // effect, so the view's mount broadcast can land before we're listening.
    // Ask it to re-broadcast now that the listener is attached.
    window.dispatchEvent(new CustomEvent('pipeline:request-state'));
    return () => window.removeEventListener('pipeline:state-change', onChange);
  }, []);

  // File-preview state — when EnhancedFileViewer is open it dispatches
  // `documents:preview-state-change` with the file name and available
  // actions. The header title morphs to the filename; trailing pills
  // replace the normal documents pills. Null when preview is closed.
  const [documentPreviewHeaderState, setDocumentPreviewHeaderState] = useState<{
    fileName: string;
    isSaving: boolean;
    lastSavedAt: Date | null;
    canSave: boolean;       // true for Adobe-rendered PDFs with a logged-in user
    proxyUrl: string | null; // non-Adobe types: "open in new tab" action
    usesAdobe: boolean;
  } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<typeof documentPreviewHeaderState>).detail;
      setDocumentPreviewHeaderState(detail ?? null);
    };
    window.addEventListener('documents:preview-state-change', onChange);
    return () => window.removeEventListener('documents:preview-state-change', onChange);
  }, []);

  // Takeoff page state — same window-event bridge as BOQ. TakeoffView
  // broadcasts visibility flags (sheet open, measurements present, AI
  // running, summary visible). Pills dispatch click events back; the
  // canvas-side handler runs the actual operation.
  const [takeoffSearchOpen, setTakeoffSearchOpen] = useState(false);
  const [takeoffSearchValue, setTakeoffSearchValue] = useState('');
  const takeoffSearchInputRef = useRef<HTMLInputElement>(null);
  const changeTakeoffSearch = (e: HeaderSearchChange) => {
    setTakeoffSearchValue(getHeaderSearchValue(e));
    window.dispatchEvent(new CustomEvent('quantis:searchQueryChanged', { detail: { query: getHeaderSearchValue(e) } }));
  };
  const clearTakeoffSearch = () => {
    setTakeoffSearchValue(''); setTakeoffSearchOpen(false);
    window.dispatchEvent(new CustomEvent('quantis:searchQueryChanged', { detail: { query: '' } }));
  };
  const [takeoffHeaderState, setTakeoffHeaderState] = useState<TakeoffHeaderState | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<typeof takeoffHeaderState>).detail;
      setTakeoffHeaderState(detail ?? null);
      if (!detail) {
        setTakeoffSearchValue('');
        setTakeoffSearchOpen(false);
      }
    };
    window.addEventListener('takeoff:state-change', onChange);
    return () => window.removeEventListener('takeoff:state-change', onChange);
  }, []);

  // Add Drawings uploader nav state: folder depth (0 = project root) and the
  // running selection count. Broadcast by TakeoffUploader so the header owns
  // both back-navigation and the Continue/Clear actions.
  const [takeoffUploaderDepth, setTakeoffUploaderDepth] = useState(0);
  const [takeoffUploaderSelected, setTakeoffUploaderSelected] = useState(0);
  // Whether the uploader is on its source (file-picking) step — the only step
  // where Browse makes sense. Broadcast alongside the selection count so the
  // gate lives in TakeoffUploader, not here.
  const [takeoffUploaderCanBrowse, setTakeoffUploaderCanBrowse] = useState(false);
  // Details-step (title-block region drawing) payload — field toggles + sheet
  // count for the header's field pills and Import action. Null outside the
  // details step.
  const [takeoffUploaderDetails, setTakeoffUploaderDetails] = useState<TakeoffUploaderDetails | null>(null);
  // Raw step string, kept alongside the details payload above so the back
  // chevron (below) can tell the review step apart from "source" without
  // re-deriving it from that payload.
  const [takeoffUploaderStep, setTakeoffUploaderStep] = useState<string | null>(null);
  useEffect(() => {
    const onNav = (e: Event) => {
      const detail = (e as CustomEvent<(Partial<TakeoffUploaderDetails> & { depth?: number; selectedCount?: number; canBrowse?: boolean }) | null>).detail;
      setTakeoffUploaderDepth(detail?.depth ?? 0);
      setTakeoffUploaderSelected(detail?.selectedCount ?? 0);
      setTakeoffUploaderCanBrowse(!!detail?.canBrowse);
      setTakeoffUploaderStep(detail?.step ?? null);
      setTakeoffUploaderDetails(
        detail?.step === 'details' && detail.activeField && detail.fieldsSet
          ? { step: detail.step, activeField: detail.activeField, fieldsSet: detail.fieldsSet, sheetCount: detail.sheetCount ?? 0 }
          : null,
      );
    };
    window.addEventListener('takeoff:upload-nav', onNav);
    return () => window.removeEventListener('takeoff:upload-nav', onNav);
  }, []);

  // Canvas-internal state (zoom %, sheet name, vector toggle, invert,
  // minimap, isolation) lifted out of the deleted in-canvas top toolbar.
  // The header is now the single home for all sheet/view chrome — same
  // event bridge as the takeoff state above.
  const [takeoffCanvasState, setTakeoffCanvasState] = useState<{
    sheetName: string | null;
    zoomPct: number;
    vectorViewing: boolean;
    invertSheet: boolean;
    showMinimap: boolean;
    isIsolated: boolean;
    canIsolate: boolean;
  } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<typeof takeoffCanvasState>).detail;
      setTakeoffCanvasState(detail ?? null);
    };
    window.addEventListener('takeoff:canvas-state-change', onChange);
    return () => window.removeEventListener('takeoff:canvas-state-change', onChange);
  }, []);

  // Left-panel state (which section is shown, whether it's collapsed) — drives
  // the leading "Panel" dropdown that replaced the in-panel activity rail.
  const [takeoffPanelState, setTakeoffPanelState] = useState<TakeoffPanelState | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<TakeoffPanelState | null>).detail;
      setTakeoffPanelState(detail ?? null);
    };
    window.addEventListener('takeoff:panel-state-change', onChange);
    return () => window.removeEventListener('takeoff:panel-state-change', onChange);
  }, []);

  // Employer form state — same window-event bridge. EmployerForm broadcasts
  // mode + submitting + dirty flags while it's mounted so the global header can
  // expose the Cancel/Save pair (and a back chevron that discards). The in-form
  // save bar is removed; the header is the only home for these actions.
  const [employerFormState, setEmployerFormState] = useState<{
    isEdit: boolean;
    isSubmitting: boolean;
    hasChanges: boolean;
    // Carried on the same channel for the sidebar's section rows. Nothing in the
    // header reads them, but the local type has to match the wire or the cast
    // below silently narrows the payload away for every consumer of this state.
    activeSection: string;
    sectionIds: readonly string[];
  } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<typeof employerFormState>).detail;
      setEmployerFormState(detail ?? null);
    };
    window.addEventListener(EMPLOYER_FORM_STATE_EVENT, onChange);
    return () => window.removeEventListener(EMPLOYER_FORM_STATE_EVENT, onChange);
  }, []);

  // A drilled-in employer profile. Separate channel from the form above: a
  // profile is always live (no edit mode), and its Email / Call plus the
  // Cancel/Save that appear once it is dirty all live here, not in the content.
  const [employerDetailState, setEmployerDetailState] = useState<{
    isSubmitting: boolean;
    hasChanges: boolean;
    email: string | null;
    phone: string | null;
  } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<typeof employerDetailState>).detail;
      setEmployerDetailState(detail ?? null);
    };
    window.addEventListener(EMPLOYER_DETAIL_STATE_EVENT, onChange);
    return () => window.removeEventListener(EMPLOYER_DETAIL_STATE_EVENT, onChange);
  }, []);

  // A drilled-in prospect (Outreach). Own channel from the employer one above
  // — ProspectDetail publishes PROSPECT_DETAIL_STATE_EVENT, never
  // OUTREACH_STATE_EVENT, which OutreachView already owns exclusively for
  // section navigation.
  const [prospectDetailState, setProspectDetailState] = useState<{
    hasChanges: boolean;
    saving: boolean;
    /** The company name, as the drill-in's title. */
    title?: string;
  } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<typeof prospectDetailState>).detail;
      setProspectDetailState(detail ?? null);
    };
    window.addEventListener(PROSPECT_DETAIL_STATE_EVENT, onChange);
    return () => window.removeEventListener(PROSPECT_DETAIL_STATE_EVENT, onChange);
  }, []);

  // A drilled-in approval batch (Outreach). Same bridge as the prospect above.
  const [outreachApprovalsState, setOutreachApprovalsState] = useState<OutreachApprovalsHeaderState | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      setOutreachApprovalsState((e as CustomEvent<OutreachApprovalsHeaderState | null>).detail ?? null);
    };
    window.addEventListener(OUTREACH_APPROVALS_STATE_EVENT, onChange);
    return () => window.removeEventListener(OUTREACH_APPROVALS_STATE_EVENT, onChange);
  }, []);

  // Interim Application form state — same window-event bridge. The
  // InterimApplication editor (project-detail Applications tab) broadcasts
  // its action capability + saving flag so we can surface Submit / Share /
  // Save as glass pills here. The in-page header on the form is now just
  // a thin meta strip — pills live exclusively in the global header.
  const [interimHeaderState, setInterimHeaderState] = useState<{
    applicationNo: number | null;
    status: string | null;
    periodEndingLabel: string | null;
    activeSection: string;
    invoiceMode: 'preview' | 'notes';
    canSubmit: boolean;
    canShare: boolean;
    canDelete: boolean;
    canManage: boolean;
    canSave: boolean;
    canEditClaim: boolean;
    isSaving: boolean;
    canExport: boolean;
    workflow: 'prepare' | 'assess' | 'view';
    saveLabel: string;
    completeLabel: string;
    statusLabel: string;
  } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<typeof interimHeaderState>).detail;
      setInterimHeaderState(detail ?? null);
    };
    window.addEventListener('interim:state-change', onChange);
    return () => window.removeEventListener('interim:state-change', onChange);
  }, []);
  const [interimListState, setInterimListState] = useState<{
    canCreate: boolean;
    createLabel: string;
    isCreating?: boolean;
  } | null>(null);
  useEffect(() => {
    const onChange = (event: Event) => {
      const detail = (event as CustomEvent<typeof interimListState>).detail;
      setInterimListState(detail ?? null);
    };
    window.addEventListener('interim:list-state-change', onChange);
    return () => window.removeEventListener('interim:list-state-change', onChange);
  }, []);

  const [interimBreakdownGroupsState, setInterimBreakdownGroupsState] =
    useState<InterimBreakdownGroupsHeaderState | null>(null);
  useEffect(() => {
    const onChange = (event: Event) => {
      const detail = (event as CustomEvent<InterimBreakdownGroupsHeaderState | null>).detail;
      setInterimBreakdownGroupsState(detail ?? null);
    };
    window.addEventListener(INTERIM_BREAKDOWN_GROUPS_STATE_EVENT, onChange);
    window.dispatchEvent(new CustomEvent(INTERIM_BREAKDOWN_GROUPS_REQUEST_STATE_EVENT));
    return () => window.removeEventListener(INTERIM_BREAKDOWN_GROUPS_STATE_EVENT, onChange);
  }, []);

  // Project → Admin tab. ProjectAdminTab broadcasts its dirty/saving state so
  // the global header can surface Save / Cancel pills (the in-page save bar
  // was removed). Same window-event bridge.
  const [projectAdminHeaderState, setProjectAdminHeaderState] = useState<{
    dirty: boolean;
    isSaving: boolean;
  } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<typeof projectAdminHeaderState>).detail;
      setProjectAdminHeaderState(detail ?? null);
    };
    window.addEventListener('project-admin:state-change', onChange);
    return () => window.removeEventListener('project-admin:state-change', onChange);
  }, []);

  // Project → Members tab. The search / member-count / Add Member controls are
  // hosted in this global header; TeamAssignmentPanel bridges its member count
  // and add-panel open state via `project-members:state-change`.
  const [projectMembersHeaderState, setProjectMembersHeaderState] = useState<{
    count: number;
    hasMembers: boolean;
    addOpen: boolean;
    detailOpen: boolean;
    canManage: boolean;
  } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<typeof projectMembersHeaderState>).detail;
      setProjectMembersHeaderState(detail ?? null);
    };
    window.addEventListener('project-members:state-change', onChange);
    window.dispatchEvent(new Event('project-members:request-state'));
    return () => window.removeEventListener('project-members:state-change', onChange);
  }, []);

  // Project → Instructions tab. Same shape as the dayworks bridge: a
  // list-state + a form-state, where the form-state takes over the
  // back chevron + Save pill when the user is editing an instruction.
  const [instructionsHeaderState, setInstructionsHeaderState] = useState<{
    isFormOpen: boolean;
    count: number;
  } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<typeof instructionsHeaderState>).detail;
      setInstructionsHeaderState(detail ?? null);
    };
    window.addEventListener('project-instructions:state-change', onChange);
    return () => window.removeEventListener('project-instructions:state-change', onChange);
  }, []);
  const [instructionFormState, setInstructionFormState] = useState<{
    isEditing: boolean;
    canSave: boolean;
    isSaving: boolean;
  } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<typeof instructionFormState>).detail;
      setInstructionFormState(detail ?? null);
    };
    window.addEventListener('project-instruction:state-change', onChange);
    return () => window.removeEventListener('project-instruction:state-change', onChange);
  }, []);

  // Project → Events tab. Mirrors the instructions bridge: list-state tracks
  // whether the form is open so the + pill is suppressed while editing.
  const [eventsHeaderState, setEventsHeaderState] = useState<{ isFormOpen: boolean; count: number; canIssueInstructions: boolean; canCreate?: boolean } | null>(null);
  useEffect(() => {
    const onState = (e: Event) => setEventsHeaderState((e as CustomEvent).detail);
    window.addEventListener('project-events:state-change', onState);
    return () => window.removeEventListener('project-events:state-change', onState);
  }, []);

  const [eventFormState, setEventFormState] = useState<EventEditorHeaderState | null>(null);
  useEffect(() => {
    const change = (event: Event) => setEventFormState((event as CustomEvent<EventEditorHeaderState | null>).detail);
    window.addEventListener('project-event-form:state-change', change);
    window.dispatchEvent(new Event('project-event-form:request-state'));
    return () => window.removeEventListener('project-event-form:state-change', change);
  }, []);

  const [activityHeaderState, setActivityHeaderState] = useState<{
    typeFilter: string;
    count: number;
    followUpsCount: number;
  } | null>(null);
  useEffect(() => {
    const onState = (event: Event) => {
      setActivityHeaderState((event as CustomEvent<typeof activityHeaderState>).detail ?? null);
    };
    window.addEventListener('project-activity:state-change', onState);
    return () => window.removeEventListener('project-activity:state-change', onState);
  }, []);

  // Project → Notes tab. Tells the header when a note is open on mobile, so
  // the chevron returns to the list and the note's own actions (pin / edit /
  // delete) take the trailing edge instead of the create pill.
  const [notesHeaderState, setNotesHeaderState] = useState<
    { detailOpen: boolean; mode: string; hasNote: boolean; isPinned: boolean; unreadCount?: number } | null
  >(null);
  useEffect(() => {
    const onState = (e: Event) => setNotesHeaderState((e as CustomEvent).detail ?? null);
    window.addEventListener('project-notes:state-change', onState);
    return () => window.removeEventListener('project-notes:state-change', onState);
  }, []);

  // Project → H&S tab. ProjectHSTab folds its hub/RAMS-list/record/editor
  // state into one payload; the + New RAMS pill only draws on the list view.
  const [hsHeaderState, setHsHeaderState] = useState<HSHeaderState | null>(null);
  useEffect(() => {
    const onState = (e: Event) => setHsHeaderState((e as CustomEvent).detail);
    window.addEventListener('hs:state-change', onState);
    return () => window.removeEventListener('hs:state-change', onState);
  }, []);

  const [hsEditorHeaderState, setHsEditorHeaderState] = useState<HSEditorHeaderState | null>(null);
  useEffect(() => {
    const onState = (event: Event) => {
      const detail = (event as CustomEvent<{
        isSaving?: boolean;
        saveLabel?: string;
        activeSection?: string;
        sectionIds?: readonly string[];
      } | null>).detail;
      setHsEditorHeaderState(
        detail
          ? {
              isSaving: detail.isSaving ?? false,
              saveLabel: detail.saveLabel ?? 'Save changes',
              activeSection: detail.activeSection,
              sectionIds: detail.sectionIds,
            }
          : null,
      );
    };
    window.addEventListener('hs:editor-state-change', onState as EventListener);
    return () => window.removeEventListener('hs:editor-state-change', onState as EventListener);
  }, []);

  const [hsDocumentHeaderState, setHsDocumentHeaderState] = useState<HSDocumentHeaderState | null>(null);
  useEffect(() => {
    const onState = (event: Event) => {
      setHsDocumentHeaderState((event as CustomEvent<HSDocumentHeaderState | null>).detail ?? null);
    };
    window.addEventListener('hs:document-state-change', onState);
    return () => window.removeEventListener('hs:document-state-change', onState);
  }, []);

  // Project → Dayworks tab. Two states:
  //   * `dayworksHeaderState` — the list view is mounted (just shows count
  //     and whether the form is open).
  //   * `dayworkFormState` — when the form takes over, swap the + pill for
  //     Save / Submit pills and a back chevron.
  const [dayworksHeaderState, setDayworksHeaderState] = useState<{
    isFormOpen: boolean;
    count: number;
  } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<typeof dayworksHeaderState>).detail;
      setDayworksHeaderState(detail ?? null);
    };
    window.addEventListener('project-dayworks:state-change', onChange);
    return () => window.removeEventListener('project-dayworks:state-change', onChange);
  }, []);
  const [dayworkFormState, setDayworkFormState] = useState<{
    isEditing: boolean;
    canSave: boolean;
    isSaving: boolean;
    isSubmitting: boolean;
    canSubmit: boolean;
    canDownload: boolean;
    total: number;
  } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<typeof dayworkFormState>).detail;
      setDayworkFormState(detail ?? null);
    };
    window.addEventListener('project-daywork:state-change', onChange);
    return () => window.removeEventListener('project-daywork:state-change', onChange);
  }, []);

  // Workspace Settings (organisation / client-detail) form state — same
  // bridge. WorkspaceOverview broadcasts hasChanges + isSaving so we can
  // surface Save / Cancel as glass pills here instead of the in-page bar.
  const [workspaceFormState, setWorkspaceFormState] = useState<{
    hasChanges: boolean;
    isSaving: boolean;
  } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<typeof workspaceFormState>).detail;
      setWorkspaceFormState(detail ?? null);
    };
    window.addEventListener('workspace:state-change', onChange);
    return () => window.removeEventListener('workspace:state-change', onChange);
  }, []);

  // Profile > Details form: broadcasts unsaved-changes so the header can show
  // Save/Cancel while the details draft is dirty. Web only — native shells use
  // the toolbar bridge in useProfileDraft.
  const [profileDetailsFormState, setProfileDetailsFormState] = useState<{
    hasChanges: boolean;
  } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<typeof profileDetailsFormState>).detail;
      setProfileDetailsFormState(detail ?? null);
    };
    window.addEventListener('profile-details:state-change', onChange);
    return () => window.removeEventListener('profile-details:state-change', onChange);
  }, []);

  const [profileSignatureState, setProfileSignatureState] = useState<{ isFormOpen: boolean; canCopy: boolean } | null>(null);
  useEffect(() => {
    const onChange = (event: Event) => setProfileSignatureState((event as CustomEvent<typeof profileSignatureState>).detail ?? null);
    window.addEventListener('profile-signature:state-change', onChange);
    return () => window.removeEventListener('profile-signature:state-change', onChange);
  }, []);

  // Workspaces List (admin /workspaces) — broadcasts active filter so we
  // can colour the Filter pill with a count badge, and the list pane
  // collapsed state so the toggle pill can reflect open/closed.
  const [workspaceCreating, setWorkspaceCreating] = useState(false);
  useEffect(() => {
    const onState = (event: Event) => { const detail = (event as CustomEvent<{ busy: boolean; step: number }>).detail; setWorkspaceCreating(!!detail?.busy); };
    window.addEventListener('workspaces:create-state', onState);
    window.dispatchEvent(new CustomEvent('workspaces:create-request-state'));
    return () => window.removeEventListener('workspaces:create-state', onState);
  }, []);
  const [workspacesListHeaderState, setWorkspacesListHeaderState] = useState<{
    filterStatus: string;
    activeFilterCount: number;
    listPaneCollapsed?: boolean;
    classificationFilter?: string;
  } | null>(null);

  // Workspaces split-view: the currently open workspace, broadcast by
  // WorkspaceList over `workspaces-list:detail-change`. When set, the
  // page title swaps from "Workspaces" to the workspace's own name so
  // the header anchors to whatever the user is editing.
  const [workspaceDetailHeaderState, setWorkspaceDetailHeaderState] = useState<{
    clientId: string;
    clientName: string;
    activeTab?: string;
  } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<{ clientId: string; clientName: string; activeTab?: string } | null>).detail;
      if (!detail) {
        setWorkspaceDetailHeaderState(null);
        return;
      }
      setWorkspaceDetailHeaderState({ clientId: detail.clientId, clientName: detail.clientName, activeTab: detail.activeTab });
    };
    window.addEventListener('workspaces-list:detail-change', onChange);
    return () => window.removeEventListener('workspaces-list:detail-change', onChange);
  }, []);
  const isWorkspaceResourceLibrary =
    currentView === 'workspaces' && workspaceDetailHeaderState?.activeTab === 'resource-library';
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<typeof workspacesListHeaderState>).detail;
      setWorkspacesListHeaderState(detail ?? null);
    };
    window.addEventListener('workspaces-list:state-change', onChange);
    return () => window.removeEventListener('workspaces-list:state-change', onChange);
  }, []);

  // BOQ Importer state — when the full-screen importer overlay is open,
  // the header takes over its top-of-canvas chrome (AI-map sparkles +
  // Close X) so the importer canvas stays clean. Same window-event
  // bridge pattern as the BOQ table state above.
  const [boqImporterState, setBoqImporterState] = useState<{
    open: boolean;
    currentStep?: string;
    canAiMap: boolean;
    isAiMapping: boolean;
    selectedCount: number;
    totalCount: number;
    measuredCount: number;
    excludedCount: number;
    reviewFilter: 'all' | 'items' | 'headings' | 'excluded';
    bills: Array<{ id: string; bill_title: string; bill_no: string }>;
  } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<typeof boqImporterState>).detail;
      setBoqImporterState(detail ?? null);
    };
    window.addEventListener('boq-importer:state-change', onChange);
    return () => window.removeEventListener('boq-importer:state-change', onChange);
  }, []);

  // Background BOQ import progress — fired by useBOQItemActions while the
  // bulk-create request is in flight. The pill stays visible across
  // page navigations (it's mounted in the global header) so the user
  // can leave the importer and watch the import finish from anywhere
  // in the app. We auto-clear ~3 s after a `done` or `error` event so
  // the pill doesn't linger after the toast has fired.
  const [boqImportProgress, setBoqImportProgress] = useState<{
    sent: number;
    total: number;
    phase: 'pending' | 'done' | 'error';
  } | null>(null);
  useEffect(() => {
    const onProgress = (e: Event) => {
      const detail = (e as CustomEvent<{ sent: number; total: number; phase: 'pending' | 'done' | 'error' }>).detail;
      if (!detail || typeof detail.total !== 'number') return;
      setBoqImportProgress(detail);
      if (detail.phase === 'done' || detail.phase === 'error') {
        setTimeout(() => setBoqImportProgress(null), 3000);
      }
    };
    window.addEventListener('boq:import-progress', onProgress);
    return () => window.removeEventListener('boq:import-progress', onProgress);
  }, []);

  // Projects-list state — broadcasts the active filter count so the
  // Filters pill in the header can show the same badge the toolbar one
  // used to.
  const [projectsHeaderState, setProjectsHeaderState] = useState<{
    activeFilterCount: number;
    projectCount: number;
    presentation?: 'table' | 'kanban';
    canSwitchPresentation?: boolean;
    kanbanGrouping?: 'stage' | 'pipeline';
    allCollapsed: boolean;
    canCreate?: boolean;
    canCreateEnquiry?: boolean;
  } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<typeof projectsHeaderState>).detail;
      setProjectsHeaderState(detail ?? null);
    };
    window.addEventListener('projects:state-change', onChange);
    return () => window.removeEventListener('projects:state-change', onChange);
  }, []);

  // Planner header state — ProjectPlannerTab broadcasts owner list, active
  // zoom preset, search-open flag etc. so the trailing pills (Today, Day/
  // Week/Month/Quarter, +, ⋯) show the same active state the in-page
  // toolbar used to.
  const [plannerHeaderState, setPlannerHeaderState] = useState<PlannerHeaderState | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<PlannerHeaderState | null>).detail;
      setPlannerHeaderState(detail ?? null);
    };
    window.addEventListener('planner:state-change', onChange);
    // On a direct/cold load the Planner child mounts before this ancestor
    // effect, so its first state broadcast can arrive before the header is
    // listening. Ask the mounted Planner to resend once the listener exists.
    window.dispatchEvent(new CustomEvent('planner:request-state'));
    return () => window.removeEventListener('planner:state-change', onChange);
  }, []);
  const [plannerEditorHeaderState, setPlannerEditorHeaderState] = useState<{
    visible: boolean;
    editing: boolean;
    canEdit: boolean;
    canSubmit: boolean;
    saving: boolean;
    presentation?: 'dialog' | 'drawer' | 'native-header';
  } | null>(null);
  useEffect(() => {
    const onChange = (event: Event) => {
      const detail = (event as CustomEvent<typeof plannerEditorHeaderState>).detail;
      setPlannerEditorHeaderState(detail ?? null);
    };
    window.addEventListener('planner:editor-state-change', onChange);
    window.dispatchEvent(new CustomEvent('planner:request-editor-state'));
    return () => window.removeEventListener('planner:editor-state-change', onChange);
  }, []);

  // Resource Library header state — whichever pane is mounted broadcasts so
  // the global header can render its toolbar pills. Discriminated by `kind`:
  // LibraryItemsPane sends 'items' (search, taxonomy, collapse,
  // rate profile, schedule, add), MerchantItemsTable sends 'merchant'
  // (search, category/brand filter, sort, image toggle). See
  // headerActions/resourceLibrary.ts for both shapes.
  const [resourceLibraryHeaderState, setResourceLibraryHeaderState] =
    useState<ResourceLibraryHeaderState | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<ResourceLibraryHeaderState | null>).detail;
      setResourceLibraryHeaderState(detail ?? null);
    };
    window.addEventListener('resource-library:state-change', onChange);
    return () => window.removeEventListener('resource-library:state-change', onChange);
  }, []);

  // Resource Library management state — ResourceLibraryManagement broadcasts
  // the active selection so the header can render its management actions.
  const [resourceLibraryPickerState, setResourceLibraryPickerState] = useState<{
    libraries: RateLibrary[];
    selectedLibraryId: string | null;
    canManage: boolean;
    allWorkspaces: Workspace[];
  } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<typeof resourceLibraryPickerState>).detail;
      setResourceLibraryPickerState(detail ?? null);
    };
    window.addEventListener('resource-library:libraries-change', onChange);
    return () => window.removeEventListener('resource-library:libraries-change', onChange);
  }, []);

  // Resource Library item-edit form — open whenever the user is editing /
  // creating a rate item inside the library. When set, the global header
  // swaps the resource-library toolbar for a form-style chrome:
  // back · title · Suggest · + Labour/Plant/Material/Sub · Save.
  const [rateItemFormState, setRateItemFormState] = useState<{
    isEditing: boolean;
    isSaving: boolean;
    canSave: boolean;
    canAddResource: boolean;
    isOverrideMode: boolean;
    libraryName: string | null;
  } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<typeof rateItemFormState>).detail;
      setRateItemFormState(detail ?? null);
    };
    window.addEventListener('resource-library-item:state-change', onChange);
    return () => window.removeEventListener('resource-library-item:state-change', onChange);
  }, []);

  // Resource Library search-pill morph — same UX as workspaces / contacts.
  const [resourceLibrarySearchOpen, setResourceLibrarySearchOpen] = useState(false);
  const [resourceLibrarySearchValue, setResourceLibrarySearchValue] = useState('');
  const resourceLibrarySearchInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const handleToggle = () => {
      setResourceLibrarySearchOpen((prev) => {
        const next = !prev;
        if (next) {
          requestAnimationFrame(() => resourceLibrarySearchInputRef.current?.focus());
        } else {
          setResourceLibrarySearchValue('');
          window.dispatchEvent(
            new CustomEvent('resource-library:query-change', { detail: { query: '' } })
          );
        }
        return next;
      });
    };
    // The pane can clear the query itself (empty-state Reset button, or a
    // library switch). Without this the header input would keep showing a
    // term that no longer filters anything.
    const handleClear = () => setResourceLibrarySearchValue('');
    window.addEventListener('resource-library:toggle-search', handleToggle);
    window.addEventListener('resource-library:clear-search', handleClear);
    return () => {
      window.removeEventListener('resource-library:toggle-search', handleToggle);
      window.removeEventListener('resource-library:clear-search', handleClear);
    };
  }, []);
  const handleResourceLibrarySearchChange = (e: HeaderSearchChange) => {
    const value = getHeaderSearchValue(e);
    setResourceLibrarySearchValue(value);
    window.dispatchEvent(
      new CustomEvent('resource-library:query-change', { detail: { query: value } })
    );
  };
  const dismissResourceLibrarySearch = () => {
    setResourceLibrarySearchOpen(false);
    setResourceLibrarySearchValue('');
    window.dispatchEvent(
      new CustomEvent('resource-library:query-change', { detail: { query: '' } })
    );
  };

  // Contacts header state — CompaniesView broadcasts the active filter,
  // computed label, contact count, and the dynamic filter options
  // (companies + main contractors load at runtime). Header builds the
  // filter pill's subActions menu from this state.
  const [contactsHeaderState, setContactsHeaderState] = useState<{
    activeFilter: string;
    filterLabel: string;
    contactCount: number;
    filterOptions: Array<{ id: string; label: string; group?: 'main' | 'companies' | 'contractors' }>;
    createOptions?: Array<{ id: string; label: string; event: string }>;
    scopeName?: string | null;
    canAdd?: boolean;
    canEdit?: boolean;
    hasSelectedContact?: boolean;
    isEditing?: boolean;
    canRemove?: boolean;
  } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<typeof contactsHeaderState>).detail;
      setContactsHeaderState(detail ?? null);
    };
    window.addEventListener('contacts:state-change', onChange);
    return () => window.removeEventListener('contacts:state-change', onChange);
  }, []);

  // Contacts search-pill morph — same UX as workspaces / projects.
  const [contactsSearchOpen, setContactsSearchOpen] = useState(false);
  const [contactsSearchValue, setContactsSearchValue] = useState('');
  const contactsSearchInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const handleToggle = () => {
      setContactsSearchOpen((prev) => {
        const next = !prev;
        if (next) {
          requestAnimationFrame(() => contactsSearchInputRef.current?.focus());
        } else {
          setContactsSearchValue('');
          window.dispatchEvent(
            new CustomEvent('contacts:query-change', { detail: { query: '' } })
          );
        }
        return next;
      });
    };
    window.addEventListener('contacts:toggle-search', handleToggle);
    return () => window.removeEventListener('contacts:toggle-search', handleToggle);
  }, []);
  const handleContactsSearchChange = (e: HeaderSearchChange) => {
    const value = getHeaderSearchValue(e);
    setContactsSearchValue(value);
    window.dispatchEvent(
      new CustomEvent('contacts:query-change', { detail: { query: value } })
    );
  };
  const dismissContactsSearch = () => {
    setContactsSearchOpen(false);
    setContactsSearchValue('');
    window.dispatchEvent(
      new CustomEvent('contacts:query-change', { detail: { query: '' } })
    );
  };

  // BOQ importer search-pill morph — header owns the input while the
  // importer overlay is open; bridge forwards the query via window events.
  const [boqImporterSearchOpen, setBoqImporterSearchOpen] = useState(false);
  const [boqImporterSearchValue, setBoqImporterSearchValue] = useState('');
  const boqImporterSearchInputRef = useRef<HTMLInputElement>(null);
  const handleBoqImporterSearchChange = useCallback((e: HeaderSearchChange) => {
    const value = getHeaderSearchValue(e);
    setBoqImporterSearchValue(value);
    window.dispatchEvent(new CustomEvent('boq-importer:search-change', { detail: { query: value } }));
  }, []);
  const dismissBoqImporterSearch = useCallback(() => {
    setBoqImporterSearchOpen(false);
    setBoqImporterSearchValue('');
    window.dispatchEvent(new CustomEvent('boq-importer:search-change', { detail: { query: '' } }));
  }, []);

  // Workspaces-list search-pill morph — same UX as projects/messages.
  // Header owns the input; WorkspaceList listens to
  // `workspaces:query-change` and filters its visible rows.
  const [workspacesSearchOpen, setWorkspacesSearchOpen] = useState(false);
  const [workspacesSearchValue, setWorkspacesSearchValue] = useState('');
  const [workspaceActivitySearchValue, setWorkspaceActivitySearchValue] = useState('');
  const workspacesSearchInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const handleToggle = () => {
      setWorkspacesSearchOpen((prev) => {
        const next = !prev;
        if (next) {
          requestAnimationFrame(() => workspacesSearchInputRef.current?.focus());
        } else {
          // Clears both consumers unconditionally rather than branching on the
          // active sub-tab: this listener is registered once with no deps, so
          // reading the tab here would be a stale closure, and resetting the
          // one that isn't showing is a no-op for its listener either way.
          setWorkspacesSearchValue('');
          setUsersSearchValue('');
          setWorkspaceActivitySearchValue('');
          window.dispatchEvent(
            new CustomEvent('workspaces:query-change', { detail: { query: '' } })
          );
          window.dispatchEvent(
            new CustomEvent(WORKSPACE_USERS_SEARCH_EVENT, { detail: { value: '' } })
          );
          window.dispatchEvent(
            new CustomEvent(WORKSPACE_ACTIVITY_SEARCH_EVENT, { detail: { value: '' } })
          );
        }
        return next;
      });
    };
    window.addEventListener('workspaces:toggle-search', handleToggle);
    return () => window.removeEventListener('workspaces:toggle-search', handleToggle);
  }, []);
  const handleWorkspacesSearchChange = (e: HeaderSearchChange) => {
    const value = getHeaderSearchValue(e);
    setWorkspacesSearchValue(value);
    window.dispatchEvent(
      new CustomEvent('workspaces:query-change', { detail: { query: value } })
    );
  };
  const dismissWorkspacesSearch = () => {
    setWorkspacesSearchOpen(false);
    setWorkspacesSearchValue('');
    window.dispatchEvent(
      new CustomEvent('workspaces:query-change', { detail: { query: '' } })
    );
  };

  const isWorkspaceActivityTab =
    currentView === 'workspaces' && workspaceDetailHeaderState?.activeTab === 'activity';
  const [workspaceActivityHeaderState, setWorkspaceActivityHeaderState] = useState<{
    selectedCategory: string;
    count: number;
    loading: boolean;
  } | null>(null);
  useEffect(() => {
    const onState = (event: Event) => {
      setWorkspaceActivityHeaderState((event as CustomEvent<{
        selectedCategory: string;
        count: number;
        loading: boolean;
      } | null>).detail ?? null);
    };
    window.addEventListener(WORKSPACE_ACTIVITY_STATE_EVENT, onState);
    return () => window.removeEventListener(WORKSPACE_ACTIVITY_STATE_EVENT, onState);
  }, []);
  const handleWorkspaceActivitySearchChange = (event: HeaderSearchChange) => {
    const value = getHeaderSearchValue(event);
    setWorkspaceActivitySearchValue(value);
    window.dispatchEvent(new CustomEvent(WORKSPACE_ACTIVITY_SEARCH_EVENT, { detail: { value } }));
  };
  const clearWorkspaceActivitySearch = () => {
    setWorkspaceActivitySearchValue('');
    window.dispatchEvent(new CustomEvent(WORKSPACE_ACTIVITY_SEARCH_EVENT, { detail: { value: '' } }));
  };
  const dismissWorkspaceActivitySearch = () => {
    setWorkspacesSearchOpen(false);
    clearWorkspaceActivitySearch();
  };
  useEffect(() => {
    if (!isWorkspaceActivityTab) setWorkspaceActivitySearchValue('');
  }, [isWorkspaceActivityTab]);

  // Workspace-detail Invoices sub-tab: search + status filter live in the
  // header (not the page body) while the tab is open. InvoiceList listens
  // for `workspace-invoices:search` / `workspace-invoices:filter` in
  // `headerControlled` mode. Default filter is "all" — matches the
  // workspace view's own initialStatusFilter.
  const [invoiceSearchValue, setInvoiceSearchValue] = useState('');
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('all');
  const invoiceSearchInputRef = useRef<HTMLInputElement>(null);
  const isWorkspaceInvoicesTab =
    currentView === 'workspaces' && workspaceDetailHeaderState?.activeTab === 'stripe-invoices';
  const handleInvoiceSearchChange = (e: HeaderSearchChange) => {
    const value = getHeaderSearchValue(e);
    setInvoiceSearchValue(value);
    window.dispatchEvent(new CustomEvent('workspace-invoices:search', { detail: { value } }));
  };
  const dismissInvoiceSearch = () => {
    setInvoiceSearchValue('');
    window.dispatchEvent(new CustomEvent('workspace-invoices:search', { detail: { value: '' } }));
  };
  // Reset when the Invoices tab isn't the active one — InvoiceList remounts
  // fresh (searchTerm '', statusFilter 'all') on every tab entry, so the
  // header's own copy has to match or it'd show stale text with nothing
  // behind it.
  useEffect(() => {
    if (isWorkspaceInvoicesTab) return;
    setInvoiceSearchValue('');
    setInvoiceStatusFilter('all');
  }, [isWorkspaceInvoicesTab]);

  const reportHeaderState = useReportHeaderState();
  const hubSectionTitle = useHubSectionTitle(currentView);
  const pageControlsState = usePageHeaderControls();
  const pageControls = pageControlsState?.view === currentView ? pageControlsState : null;
  const pageControlsSearchRef = useRef<HTMLInputElement>(null);
  const [pageControlsSearchOpen, setPageControlsSearchOpen] = useState(false);
  useEffect(() => setPageControlsSearchOpen(false), [pageControls?.id]);
  // Sales hub Invoices section (admin /sales/invoices): FinanceBilling hosts
  // a second InvoiceList in `headerControlled` mode there and broadcasts over
  // `sales-invoices:state-change` while it's the active section. Same
  // `workspace-invoices:search` / `workspace-invoices:filter` events as the
  // tab above — InvoiceList doesn't care which page dispatches them — but its
  // own state, since the two default to different statuses (all vs unpaid)
  // and are never both on screen at once.
  const [salesInvoicesHeaderState, setSalesInvoicesHeaderState] = useState<{ active: boolean } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      setSalesInvoicesHeaderState((e as CustomEvent<{ active: boolean } | null>).detail ?? null);
    };
    window.addEventListener('sales-invoices:state-change', onChange);
    return () => window.removeEventListener('sales-invoices:state-change', onChange);
  }, []);
  const isSalesInvoicesTab = currentView === 'sales' && !!salesInvoicesHeaderState?.active;
  const [salesInvoiceSearchValue, setSalesInvoiceSearchValue] = useState('');
  const [salesInvoiceStatusFilter, setSalesInvoiceStatusFilter] = useState('unpaid');
  const salesInvoiceSearchInputRef = useRef<HTMLInputElement>(null);
  const handleSalesInvoiceSearchChange = (e: HeaderSearchChange) => {
    const value = getHeaderSearchValue(e);
    setSalesInvoiceSearchValue(value);
    window.dispatchEvent(new CustomEvent('workspace-invoices:search', { detail: { value } }));
  };
  const dismissSalesInvoiceSearch = () => {
    setSalesInvoiceSearchValue('');
    window.dispatchEvent(new CustomEvent('workspace-invoices:search', { detail: { value: '' } }));
  };
  useEffect(() => {
    if (isSalesInvoicesTab) return;
    setSalesInvoiceSearchValue('');
    setSalesInvoiceStatusFilter('unpaid');
  }, [isSalesInvoicesTab]);

  // Workspace-detail Users sub-tab: same relocation the Invoices tab does
  // above. UserManagement runs in `headerControlled` mode and listens for
  // `workspace-users:search` / `workspace-users:filter`. The search pill is
  // the shared `workspaces-search` one, retargeted while this tab is open, so
  // the directory search and the users search never sit on screen together.
  const [usersSearchValue, setUsersSearchValue] = useState('');
  const [usersRoleFilter, setUsersRoleFilter] = useState(USER_ROLE_FILTER_ALL);
  const usersSearchInputRef = useRef<HTMLInputElement>(null);
  // UserManagement reports whether the LIST is on screen. Without it the pills
  // would sit over the add / edit user forms, which they don't act on — the
  // in-page controls used to vanish with the table for free.
  const [usersHeaderState, setUsersHeaderState] = useState<{
    listVisible: boolean;
    inviteVisible?: boolean;
    inviteActionsInHeader?: boolean;
    inviteCanSubmit?: boolean;
    inviteSubmitting?: boolean;
  } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      setUsersHeaderState((e as CustomEvent<{
        listVisible: boolean;
        inviteVisible?: boolean;
        inviteActionsInHeader?: boolean;
        inviteCanSubmit?: boolean;
        inviteSubmitting?: boolean;
      } | null>).detail ?? null);
    };
    window.addEventListener(WORKSPACE_USERS_STATE_EVENT, onChange);
    return () => window.removeEventListener(WORKSPACE_USERS_STATE_EVENT, onChange);
  }, []);
  // Bots section -> header. Same shape as the users tab: pills key off what is
  // on screen (list vs detail), and Cancel/Save appear only once dirty.
  const [botsSearchOpen, setBotsSearchOpen] = useState(false);
  const [botsSearchValue, setBotsSearchValue] = useState('');
  const botsSearchInputRef = useRef<HTMLInputElement>(null);
  const [botsHeaderState, setBotsHeaderState] = useState<WorkspaceBotsHeaderState | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<WorkspaceBotsHeaderState | null>).detail ?? null;
      setBotsHeaderState(detail);
      // The section owns the query, including resets and return from a profile.
      if (typeof detail?.searchQuery === 'string') setBotsSearchValue(detail.searchQuery);
    };
    window.addEventListener(WORKSPACE_BOTS_STATE_EVENT, onChange);
    return () => window.removeEventListener(WORKSPACE_BOTS_STATE_EVENT, onChange);
  }, []);
  const isBotsTabOpen =
    (currentView === 'profile' || currentView === 'account')
    && (profileActiveTab === 'workspace/bots' || profileActiveTab?.startsWith('workspace/bots/'));
  const isBotsListVisible = isBotsTabOpen && !!botsHeaderState?.listVisible;
  const handleBotsSearchChange = (event: HeaderSearchChange) => {
    const query = getHeaderSearchValue(event);
    setBotsSearchValue(query);
    window.dispatchEvent(new CustomEvent(WORKSPACE_BOTS_SEARCH_EVENT, { detail: { query } }));
  };
  const clearBotsSearch = () => {
    setBotsSearchValue('');
    window.dispatchEvent(new CustomEvent(WORKSPACE_BOTS_SEARCH_EVENT, { detail: { query: '' } }));
  };
  const dismissBotsSearch = () => {
    setBotsSearchOpen(false);
    clearBotsSearch();
  };
  useEffect(() => {
    if (!isBotsListVisible) setBotsSearchOpen(false);
    // Preserve a directory query while viewing an agent; leaving the tab
    // unmounts the section, so discard only the header's local input mirror.
    if (!isBotsTabOpen) setBotsSearchValue('');
  }, [isBotsTabOpen, isBotsListVisible]);

  // Two different questions, and conflating them desynced the controls from
  // the table. `isUsersTabOpen` = the tab is selected at all; `isWorkspaceUsersTab`
  // = its LIST is on screen, which is what the pills key off.
  const isUsersTabOpen =
    (currentView === 'workspaces' && workspaceDetailHeaderState?.activeTab === 'users')
    || ((currentView === 'profile' || currentView === 'account')
      && (profileActiveTab === 'workspace/users' || profileActiveTab?.startsWith('workspace/users/')));
  const isWorkspaceUsersTab = isUsersTabOpen && !!usersHeaderState?.listVisible;
  const isWorkspaceInviteForm = isUsersTabOpen && !!usersHeaderState?.inviteVisible;
  const isWorkspaceInviteHeaderForm = isWorkspaceInviteForm && !!usersHeaderState?.inviteActionsInHeader;

  // Edit-user form's Save/Cancel, same bridge shape as the workspace form.
  const [usersFormState, setUsersFormState] = useState<{ hasChanges: boolean; userName?: string; inspectorActions?: boolean } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      setUsersFormState((e as CustomEvent<{ hasChanges: boolean; userName?: string; inspectorActions?: boolean } | null>).detail ?? null);
    };
    window.addEventListener(WORKSPACE_USER_FORM_STATE_EVENT, onChange);
    return () => window.removeEventListener(WORKSPACE_USER_FORM_STATE_EVENT, onChange);
  }, []);
  const isWorkspaceUserForm = isUsersTabOpen && !!usersFormState;
  const handleUsersSearchChange = (e: HeaderSearchChange) => {
    const value = getHeaderSearchValue(e);
    setUsersSearchValue(value);
    window.dispatchEvent(new CustomEvent(WORKSPACE_USERS_SEARCH_EVENT, { detail: { value } }));
  };
  const clearUsersSearch = () => {
    setUsersSearchValue('');
    window.dispatchEvent(new CustomEvent(WORKSPACE_USERS_SEARCH_EVENT, { detail: { value: '' } }));
  };
  const dismissUsersSearchPill = () => {
    setWorkspacesSearchOpen(false);
    clearUsersSearch();
  };
  // Keyed on the TAB, not on list visibility. UserManagement is not like
  // InvoiceList: it stays mounted and keeps its searchTerm / filterRole while
  // the edit form is showing. Resetting on list visibility therefore cleared
  // the header while the page kept filtering, and returning from the form gave
  // an empty search box and "All Roles" over an invisibly filtered table.
  // Leaving the tab really does unmount it (Radix TabsContent), so its state
  // and the header's reset together — which is the case this must cover.
  useEffect(() => {
    if (isUsersTabOpen) return;
    setUsersSearchValue('');
    setUsersRoleFilter(USER_ROLE_FILTER_ALL);
  }, [isUsersTabOpen]);

  // Employers screen: search + status filter + Add live in the header, and the
  // screen reports which of its three states (directory / drilled-in profile /
  // add form) owns the content area. EmployerManagement is the broadcaster, not
  // EmployerList — it is the only component that knows about all three.
  const [employersHeaderState, setEmployersHeaderState] = useState<{
    listVisible: boolean;
    statusFilter: EmployerStatusFilter;
    selectedName: string | null;
  } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      setEmployersHeaderState((e as CustomEvent<typeof employersHeaderState>).detail ?? null);
    };
    window.addEventListener(EMPLOYERS_STATE_EVENT, onChange);
    return () => window.removeEventListener(EMPLOYERS_STATE_EVENT, onChange);
  }, []);
  const isEmployersRoute = currentView === 'employers';
  const isEmployersList = isEmployersRoute && !!employersHeaderState?.listVisible;
  // Non-null means the profile replaced the directory, which is the one state
  // that needs a bare back chevron and the employer's name as the title.
  const employerSelectedName = isEmployersRoute
    ? (employersHeaderState?.selectedName ?? null)
    : null;

  // The pill keeps its own copy so the native <select> doesn't flash back to the
  // previous option while the intent round-trips through the feature. The screen
  // is still the source of truth — its empty state can reset the filter to "all"
  // without touching the pill — so the channel wins whenever the two differ.
  // Deliberately NOT reset when the list goes away: EmployerManagement owns the
  // filter and outlives both the profile and the form, so the filter is still
  // applied when the user comes back.
  const [employersStatusFilter, setEmployersStatusFilter] =
    useState<EmployerStatusFilter>(EMPLOYER_STATUS_FILTER_ALL);
  useEffect(() => {
    const next = employersHeaderState?.statusFilter ?? EMPLOYER_STATUS_FILTER_ALL;
    setEmployersStatusFilter(prev => (prev === next ? prev : next));
  }, [employersHeaderState?.statusFilter]);

  const [employersSearchOpen, setEmployersSearchOpen] = useState(false);
  const [employersSearchValue, setEmployersSearchValue] = useState('');
  const employersSearchInputRef = useRef<HTMLInputElement>(null);
  // A callback rather than the `<view>:toggle-search` window event the older
  // morphs use: nothing outside this component ever needs to open the field, so
  // the indirection would only add a global listener. Stable identity, and the
  // functional update means the pill's closure can't read a stale flag.
  const toggleEmployersSearch = useCallback(() => {
    setEmployersSearchOpen((prev) => {
      const next = !prev;
      if (next) {
        requestAnimationFrame(() => employersSearchInputRef.current?.focus());
      } else {
        setEmployersSearchValue('');
        window.dispatchEvent(new CustomEvent(EMPLOYERS_SEARCH_EVENT, { detail: { value: '' } }));
      }
      return next;
    });
  }, []);
  const handleEmployersSearchChange = (e: HeaderSearchChange) => {
    const value = getHeaderSearchValue(e);
    setEmployersSearchValue(value);
    window.dispatchEvent(new CustomEvent(EMPLOYERS_SEARCH_EVENT, { detail: { value } }));
  };
  const dismissEmployersSearch = () => {
    setEmployersSearchOpen(false);
    setEmployersSearchValue('');
    window.dispatchEvent(new CustomEvent(EMPLOYERS_SEARCH_EVENT, { detail: { value: '' } }));
  };
  // EmployerList unmounts whenever a profile or the form takes over, so it comes
  // back with an empty query. Without this the header would return holding text
  // that filters nothing — and, worse, an open morph bar with no input behind
  // it, which hides the title and every trailing pill with no way out.
  useEffect(() => {
    if (isEmployersList) return;
    setEmployersSearchOpen(false);
    setEmployersSearchValue('');
  }, [isEmployersList]);

  // Plant Register screen: same bridge shape as Employers above, minus the
  // separate add/edit form — PlantRegisterView has only a table and a
  // drilled-in (possibly still-unsaved) machine.
  const [plantHeaderState, setPlantHeaderState] = useState<PlantListState | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      setPlantHeaderState((e as CustomEvent<PlantListState | null>).detail ?? null);
    };
    window.addEventListener(PLANT_STATE_EVENT, onChange);
    return () => window.removeEventListener(PLANT_STATE_EVENT, onChange);
  }, []);
  // Also true for the register embedded in a project's H&S tab (ProjectHSTab
  // dispatches hs:state-change with section: 'register' only while that
  // section is mounted, never lingering after a tab switch) — not gated on
  // projectDetailTab, which is declared later in this component and would be
  // a temporal-dead-zone reference here.
  const isPlantRoute = currentView === 'plant' || hsHeaderState?.section === 'register';
  const isPlantList = isPlantRoute && !!plantHeaderState?.listVisible;
  const plantExamDueCount = plantHeaderState?.examDueCount ?? 0;
  const plantSelectedLabel = isPlantRoute ? (plantHeaderState?.selectedLabel ?? null) : null;

  const [plantDetailState, setPlantDetailState] = useState<PlantDetailState | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      setPlantDetailState((e as CustomEvent<PlantDetailState | null>).detail ?? null);
    };
    window.addEventListener(PLANT_DETAIL_STATE_EVENT, onChange);
    return () => window.removeEventListener(PLANT_DETAIL_STATE_EVENT, onChange);
  }, []);

  const [plantStatusFilter, setPlantStatusFilter] =
    useState<PlantStatusFilter>(PLANT_STATUS_FILTER_ALL);
  useEffect(() => {
    const next = plantHeaderState?.statusFilter ?? PLANT_STATUS_FILTER_ALL;
    setPlantStatusFilter(prev => (prev === next ? prev : next));
  }, [plantHeaderState?.statusFilter]);

  const [plantSearchOpen, setPlantSearchOpen] = useState(false);
  const [plantSearchValue, setPlantSearchValue] = useState('');
  const plantSearchInputRef = useRef<HTMLInputElement>(null);
  const togglePlantSearch = useCallback(() => {
    setPlantSearchOpen((prev) => {
      const next = !prev;
      if (next) {
        requestAnimationFrame(() => plantSearchInputRef.current?.focus());
      } else {
        setPlantSearchValue('');
        window.dispatchEvent(new CustomEvent(PLANT_SEARCH_EVENT, { detail: { value: '' } }));
      }
      return next;
    });
  }, []);
  const handlePlantSearchChange = (e: HeaderSearchChange) => {
    const value = getHeaderSearchValue(e);
    setPlantSearchValue(value);
    window.dispatchEvent(new CustomEvent(PLANT_SEARCH_EVENT, { detail: { value } }));
  };
  const dismissPlantSearch = () => {
    setPlantSearchOpen(false);
    setPlantSearchValue('');
    window.dispatchEvent(new CustomEvent(PLANT_SEARCH_EVENT, { detail: { value: '' } }));
  };
  // Same reason as Employers above: the table unmounts under a drilled-in
  // machine, so a stale query would otherwise hide the title and every
  // trailing pill behind an open morph bar with nothing behind it.
  useEffect(() => {
    if (isPlantList) return;
    setPlantSearchOpen(false);
    setPlantSearchValue('');
  }, [isPlantList]);

  // Payroll / Tax hubs: the active section label replaces the static hub
  // title and, when canCreate is true, a + pill appears in the trailing edge.
  const [payrollTaxHeaderState, setPayrollTaxHeaderState] = useState<{
    hub: 'payroll' | 'tax';
    sectionId: string;
    sectionLabel: string;
    canCreate: boolean;
    createLabel: string;
    profileOpen?: boolean;
    sectionSubtitle?: string;
  } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<typeof payrollTaxHeaderState>).detail;
      setPayrollTaxHeaderState(detail ?? null);
    };
    window.addEventListener('payroll-tax:state-change', onChange);
    return () => window.removeEventListener('payroll-tax:state-change', onChange);
  }, []);

  const [payrollTaxFilterState, setPayrollTaxFilterState] = useState<{
    statusFilter: string;
    statusOptions: { value: string; label: string }[];
  } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<typeof payrollTaxFilterState>).detail;
      setPayrollTaxFilterState(detail ?? null);
    };
    window.addEventListener('payroll-tax:filter-change', onChange);
    return () => window.removeEventListener('payroll-tax:filter-change', onChange);
  }, []);

  // Workflows settings page: when active, the header surfaces search +
  // a New button. WorkflowSettings dispatches `workflows:state-change`
  // on mount/unmount so the header knows when to render these actions.
  const [workflowsHeaderState, setWorkflowsHeaderState] = useState<{
    active: boolean;
    editorOpen?: boolean;
    isNew?: boolean;
    isLocked?: boolean;
    dirty?: boolean;
    canSave?: boolean;
    isSaving?: boolean;
  } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<{ active: boolean; editorOpen?: boolean; isNew?: boolean; isLocked?: boolean; dirty?: boolean; canSave?: boolean; isSaving?: boolean } | null>).detail;
      setWorkflowsHeaderState(detail ?? null);
    };
    window.addEventListener('workflows:state-change', onChange);
    return () => window.removeEventListener('workflows:state-change', onChange);
  }, []);
  const [workflowsSearchOpen, setWorkflowsSearchOpen] = useState(false);
  const [workflowsSearchValue, setWorkflowsSearchValue] = useState('');
  const workflowsSearchInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const handleToggle = () => {
      setWorkflowsSearchOpen((prev) => {
        const next = !prev;
        if (next) {
          requestAnimationFrame(() => workflowsSearchInputRef.current?.focus());
        } else {
          setWorkflowsSearchValue('');
          window.dispatchEvent(
            new CustomEvent('workflows:query-change', { detail: { query: '' } })
          );
        }
        return next;
      });
    };
    window.addEventListener('workflows:toggle-search', handleToggle);
    return () => window.removeEventListener('workflows:toggle-search', handleToggle);
  }, []);
  const handleWorkflowsSearchChange = (e: HeaderSearchChange) => {
    const value = getHeaderSearchValue(e);
    setWorkflowsSearchValue(value);
    window.dispatchEvent(
      new CustomEvent('workflows:query-change', { detail: { query: value } })
    );
  };
  const dismissWorkflowsSearch = () => {
    setWorkflowsSearchOpen(false);
    setWorkflowsSearchValue('');
    window.dispatchEvent(
      new CustomEvent('workflows:query-change', { detail: { query: '' } })
    );
  };

  // Trigger Catalog settings page: the sibling of Workflows under the same
  // sidebar group, so search + create + filters live here rather than in a
  // toolbar of its own. TriggerCatalogSettings publishes the current filter
  // choices so the menus below can show what is selected.
  const [triggerCatalogHeaderState, setTriggerCatalogHeaderState] = useState<{
    active: boolean;
    statusFilter?: string;
    categoryFilter?: string;
    categoryOptions?: { value: string; label: string }[];
  } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<typeof triggerCatalogHeaderState>).detail;
      setTriggerCatalogHeaderState(detail ?? null);
    };
    window.addEventListener('trigger-catalog:state-change', onChange);
    return () => window.removeEventListener('trigger-catalog:state-change', onChange);
  }, []);
  const [triggerCatalogSearchOpen, setTriggerCatalogSearchOpen] = useState(false);
  const [triggerCatalogSearchValue, setTriggerCatalogSearchValue] = useState('');
  const triggerCatalogSearchInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const handleToggle = () => {
      setTriggerCatalogSearchOpen((prev) => {
        const next = !prev;
        if (next) {
          requestAnimationFrame(() => triggerCatalogSearchInputRef.current?.focus());
        } else {
          setTriggerCatalogSearchValue('');
          window.dispatchEvent(
            new CustomEvent('trigger-catalog:query-change', { detail: { query: '' } })
          );
        }
        return next;
      });
    };
    window.addEventListener('trigger-catalog:toggle-search', handleToggle);
    return () => window.removeEventListener('trigger-catalog:toggle-search', handleToggle);
  }, []);
  const handleTriggerCatalogSearchChange = (e: HeaderSearchChange) => {
    const value = getHeaderSearchValue(e);
    setTriggerCatalogSearchValue(value);
    window.dispatchEvent(
      new CustomEvent('trigger-catalog:query-change', { detail: { query: value } })
    );
  };
  const dismissTriggerCatalogSearch = () => {
    setTriggerCatalogSearchOpen(false);
    setTriggerCatalogSearchValue('');
    window.dispatchEvent(
      new CustomEvent('trigger-catalog:query-change', { detail: { query: '' } })
    );
  };

  // Project-list search-pill morph: clicking the Search pill expands it
  // in place into an inline search input. Header owns the input UX
  // (focus, value, dismiss); ProjectListView listens to
  // `projects:query-change` and applies the filter to its list.
  const [projectsSearchOpen, setProjectsSearchOpen] = useState(false);
  const [projectsSearchValue, setProjectsSearchValue] = useState('');
  const projectsSearchInputRef = useRef<HTMLInputElement>(null);
  // Mirror the live query into a ref so the replay handler below can read
  // the latest value with a stable listener (no re-subscribe per keystroke).
  const projectsSearchValueRef = useRef('');
  projectsSearchValueRef.current = projectsSearchValue;
  useEffect(() => {
    const handleToggle = () => {
      setProjectsSearchOpen((prev) => {
        const next = !prev;
        if (next) {
          requestAnimationFrame(() => projectsSearchInputRef.current?.focus());
        } else {
          setProjectsSearchValue('');
          window.dispatchEvent(
            new CustomEvent('projects:query-change', { detail: { query: '' } })
          );
        }
        return next;
      });
    };
    window.addEventListener('projects:toggle-search', handleToggle);
    return () => window.removeEventListener('projects:toggle-search', handleToggle);
  }, []);
  const handleProjectsSearchChange = (e: HeaderSearchChange) => {
    const value = getHeaderSearchValue(e);
    setProjectsSearchValue(value);
    window.dispatchEvent(
      new CustomEvent('projects:query-change', { detail: { query: value } })
    );
  };
  const dismissProjectsSearch = () => {
    setProjectsSearchOpen(false);
    setProjectsSearchValue('');
    window.dispatchEvent(
      new CustomEvent('projects:query-change', { detail: { query: '' } })
    );
  };
  // ProjectListView resets its mirrored `searchQuery` to '' every time it
  // (re)mounts, and it remounts on background churn (session refresh,
  // realtime updates, view round-trips) while this persistent header keeps
  // the text. Without a replay the list silently un-filters while the box
  // still shows the query. The freshly mounted list asks via
  // `projects:request-search`; we answer by re-broadcasting the current value.
  useEffect(() => {
    const replay = () => {
      if (!projectsSearchValueRef.current) return;
      window.dispatchEvent(
        new CustomEvent('projects:query-change', {
          detail: { query: projectsSearchValueRef.current },
        })
      );
    };
    window.addEventListener('projects:request-search', replay);
    return () => window.removeEventListener('projects:request-search', replay);
  }, []);

  // Project → Notes tab search — a persistent header field on desktop (the
  // page's own box is the phone's search), filtering via
  // `project-notes:set-query`.
  const [projectNotesSearchOpen, setProjectNotesSearchOpen] = useState(false);
  const [projectNotesSearchValue, setProjectNotesSearchValue] = useState('');
  const projectNotesSearchInputRef = useRef<HTMLInputElement>(null);
  const handleProjectNotesSearchChange = (e: HeaderSearchChange) => {
    const value = getHeaderSearchValue(e);
    setProjectNotesSearchValue(value);
    window.dispatchEvent(new CustomEvent('project-notes:set-query', { detail: { query: value } }));
  };
  const dismissProjectNotesSearch = () => {
    setProjectNotesSearchOpen(false);
    setProjectNotesSearchValue('');
    window.dispatchEvent(new CustomEvent('project-notes:set-query', { detail: { query: '' } }));
  };

  // Project → Activity tab search — the same arrangement as Notes: a
  // persistent header field on desktop (the phone keeps the page's own),
  // filtering the timeline via `project-activity:set-query`.
  const [projectActivitySearchOpen, setProjectActivitySearchOpen] = useState(false);
  const [projectActivitySearchValue, setProjectActivitySearchValue] = useState('');
  const projectActivitySearchInputRef = useRef<HTMLInputElement>(null);
  const handleProjectActivitySearchChange = (e: HeaderSearchChange) => {
    const value = getHeaderSearchValue(e);
    setProjectActivitySearchValue(value);
    window.dispatchEvent(new CustomEvent('project-activity:set-query', { detail: { query: value } }));
  };
  const dismissProjectActivitySearch = () => {
    setProjectActivitySearchOpen(false);
    setProjectActivitySearchValue('');
    window.dispatchEvent(new CustomEvent('project-activity:set-query', { detail: { query: '' } }));
  };

  // Project → Members tab search — a persistent header field that filters the
  // members table via `project-members:set-filter`.
  const [projectMembersSearchOpen, setProjectMembersSearchOpen] = useState(false);
  const [projectMembersSearchValue, setProjectMembersSearchValue] = useState('');
  const projectMembersSearchInputRef = useRef<HTMLInputElement>(null);
  const handleProjectMembersSearchChange = (e: HeaderSearchChange) => {
    const value = getHeaderSearchValue(e);
    setProjectMembersSearchValue(value);
    window.dispatchEvent(new CustomEvent('project-members:set-filter', { detail: { query: value } }));
  };
  // Roster and add-people share the header search; switching views starts it empty.
  const projectMembersAddOpen = projectMembersHeaderState?.addOpen ?? false;
  useEffect(() => {
    setProjectMembersSearchValue('');
  }, [projectMembersAddOpen]);
  const dismissProjectMembersSearch = () => {
    setProjectMembersSearchOpen(false);
    setProjectMembersSearchValue('');
    window.dispatchEvent(new CustomEvent('project-members:set-filter', { detail: { query: '' } }));
  };

  // Enquiries (consultancy pipeline) search — a persistent header field that
  // filters the board's cards via `pipeline:query-change`. The board re-asks
  // for the current value on mount via `pipeline:request-search`.
  const [pipelineSearchValue, setPipelineSearchValue] = useState('');
  const pipelineSearchInputRef = useRef<HTMLInputElement>(null);
  const pipelineSearchValueRef = useRef('');
  pipelineSearchValueRef.current = pipelineSearchValue;
  const handlePipelineSearchChange = (e: HeaderSearchChange) => {
    const value = getHeaderSearchValue(e);
    setPipelineSearchValue(value);
    window.dispatchEvent(new CustomEvent('pipeline:query-change', { detail: { query: value } }));
  };
  const dismissPipelineSearch = () => {
    setPipelineSearchValue('');
    window.dispatchEvent(new CustomEvent('pipeline:query-change', { detail: { query: '' } }));
  };
  useEffect(() => {
    const replay = () => {
      window.dispatchEvent(
        new CustomEvent('pipeline:query-change', { detail: { query: pipelineSearchValueRef.current } }),
      );
    };
    window.addEventListener('pipeline:request-search', replay);
    return () => window.removeEventListener('pipeline:request-search', replay);
  }, []);

  // BOQ search-pill morph — same UX as projects / messages. The pill
  // expands in place into a search input; typing dispatches
  // `boq:search-change` which BOQBuilder listens to. Closing clears
  // the value and broadcasts an empty query so the table un-filters.
  const [boqSearchOpen, setBoqSearchOpen] = useState(false);
  const [boqSearchValue, setBoqSearchValue] = useState('');
  const boqSearchInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    // The header pill itself toggles the morph via its onClick handler
    // below, but BOQBuilder + keyboard shortcuts can also request open
    // via this event — mirrors the projects-search bridge.
    const handleOpen = () => {
      setBoqSearchOpen(true);
      requestAnimationFrame(() => boqSearchInputRef.current?.focus());
    };
    window.addEventListener('boq:open-search', handleOpen);
    return () => window.removeEventListener('boq:open-search', handleOpen);
  }, []);
  const handleBoqSearchChange = (e: HeaderSearchChange) => {
    const value = getHeaderSearchValue(e);
    setBoqSearchValue(value);
    window.dispatchEvent(
      new CustomEvent('boq:search-change', { detail: { query: value } })
    );
  };
  const dismissBoqSearch = () => {
    setBoqSearchOpen(false);
    setBoqSearchValue('');
    window.dispatchEvent(
      new CustomEvent('boq:search-change', { detail: { query: '' } })
    );
  };

  // Documents search-pill morph — same UX as BOQ. Tap the Search icon and
  // it expands in place into an input; typing dispatches
  // `documents:search-change` which DocumentToolbar listens to and feeds
  // straight into onSearchChange. Dismissing clears the query.
  const [documentsSearchOpen, setDocumentsSearchOpen] = useState(false);
  const [documentsSearchValue, setDocumentsSearchValue] = useState('');
  const documentsSearchInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const handleOpen = () => {
      setDocumentsSearchOpen(true);
      requestAnimationFrame(() => documentsSearchInputRef.current?.focus());
    };
    // The persistent search bar in DocumentToolbar dispatches close-search
    // on Cancel / blur-with-empty-query so the header chrome restores.
    const handleClose = () => {
      setDocumentsSearchOpen(false);
    };
    // Mirror every `documents:search-change` into `documentsSearchValue`
    // so dictation from the body-bar mic (toolbar-side) updates the morph
    // input in real time. The header also dispatches this event when the
    // user types into the morph — hearing its own dispatch back is safe
    // because React bails out of a re-render when setState receives the
    // same value (Object.is equality).
    const handleExternalSearchChange = (e: Event) => {
      const next = (e as CustomEvent<{ query?: string }>).detail?.query ?? '';
      setDocumentsSearchValue((prev) => (prev === next ? prev : next));
    };
    window.addEventListener('documents:open-search', handleOpen);
    window.addEventListener('documents:close-search', handleClose);
    window.addEventListener('documents:search-change', handleExternalSearchChange);
    return () => {
      window.removeEventListener('documents:open-search', handleOpen);
      window.removeEventListener('documents:close-search', handleClose);
      window.removeEventListener('documents:search-change', handleExternalSearchChange);
    };
  }, []);
  // Reflect external query updates (e.g. native search bridge writes
  // through `onSearchChange` directly, bypassing the search-change event)
  // so the pill input stays in sync.
  // Note: we no longer auto-open the morph pill just because a query is
  // present. Doing so kills the header chrome (back / title / bell) on
  // navigation-in with a persisted query, while the body placeholder bar
  // stays visible — leaving the user with no title and a redundant
  // search affordance. The morph now only opens via explicit user
  // intent (tap on the body bar / search pill / `documents:open-search`).
  useEffect(() => {
    if (documentsHeaderState && documentsHeaderState.searchQuery !== documentsSearchValue) {
      setDocumentsSearchValue(documentsHeaderState.searchQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentsHeaderState?.searchQuery]);
  const handleDocumentsSearchChange = (e: HeaderSearchChange) => {
    const value = getHeaderSearchValue(e);
    setDocumentsSearchValue(value);
    window.dispatchEvent(
      new CustomEvent('documents:search-change', { detail: { query: value } })
    );
  };
  const dismissDocumentsSearch = () => {
    setDocumentsSearchOpen(false);
    setDocumentsSearchValue('');
    window.dispatchEvent(
      new CustomEvent('documents:search-change', { detail: { query: '' } })
    );
    // Tell the body's placeholder bar the morph has closed so it can
    // restore its idle "tap to search" appearance.
    window.dispatchEvent(new CustomEvent('documents:search-closed'));
  };

  const documentsDictation = useDictation({
    onResult: (text) => {
      setDocumentsSearchValue(text);
      window.dispatchEvent(new CustomEvent('documents:search-change', { detail: { query: text } }));
    },
  });

  // Messages search-pill morph — same UX as projects: clicking the
  // Search pill expands it in place into an inline search input. Header
  // owns the input UX (focus, value, dismiss); ChatListPanel listens to
  // `messages:query-change` and applies the filter to its visible chats.
  // The pill open-state stays synced with `messages:toggle-search` /
  // `messages:dismiss-search` so the existing chat-list bridge keeps
  // working (the events still drive `isSearchMode` inside ChatListPanel,
  // which is what controls list filtering).
  const [messagesSearchOpen, setMessagesSearchOpen] = useState(false);
  const [messagesSearchValue, setMessagesSearchValue] = useState('');

  // Email thread state — when EmailView opens a thread it broadcasts
  // `email:thread-state` with the subject + star flag. The header switches
  // its title and trailing actions to thread-mode while a thread is open.
  const [emailThread, setEmailThread] = useState<{ subject: string; isStarred: boolean } | null>(null);
  useEffect(() => {
    const onThreadState = (e: Event) => {
      const detail = (e as CustomEvent<{ subject: string; isStarred: boolean } | null>).detail;
      setEmailThread(detail ?? null);
    };
    window.addEventListener('email:thread-state', onThreadState);
    return () => window.removeEventListener('email:thread-state', onThreadState);
  }, []);
  const emailThreadOpen = currentView === 'email' && !!emailThread;
  const emailThreadStarred = emailThread?.isStarred ?? false;
  const [emailSearchValue, setEmailSearchValue] = useState('');
  const emailSearchInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const syncQuery = (event: Event) => {
      const detail = (event as CustomEvent<{ query?: string }>).detail;
      setEmailSearchValue(detail?.query ?? '');
    };
    window.addEventListener('email:query-sync', syncQuery);
    return () => window.removeEventListener('email:query-sync', syncQuery);
  }, []);
  const handleEmailSearchChange = (event: HeaderSearchChange) => {
    const query = getHeaderSearchValue(event);
    setEmailSearchValue(query);
    window.dispatchEvent(new CustomEvent('email:query-change', { detail: { query } }));
  };
  const clearEmailSearch = () => {
    setEmailSearchValue('');
    window.dispatchEvent(new CustomEvent('email:query-change', { detail: { query: '' } }));
  };

  // Folder-mode filter chips. Each entry maps to a Gmail `q=` operator the
  // EmailView passes through to the API. Header owns the open/closed state
  // of the popover; the chosen filters are stored client-side so refreshing
  // the page (or re-opening the popover) keeps the user's intent.
  const EMAIL_FILTERS: { id: string; label: string; q: string }[] = [
    { id: 'unread',   label: 'Unread',          q: 'is:unread' },
    { id: 'starred',  label: 'Starred',         q: 'is:starred' },
    { id: 'attached', label: 'Has attachment',  q: 'has:attachment' },
  ];
  const [emailFilterMenuOpen, setEmailFilterMenuOpen] = useState(false);
  const [emailActiveFilters, setEmailActiveFilters] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem('quantis.email.filters.v1');
      return new Set(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set();
    }
  });
  const persistFilters = (next: Set<string>) => {
    try { localStorage.setItem('quantis.email.filters.v1', JSON.stringify([...next])); } catch { /* quota */ }
  };
  const broadcastFilters = (next: Set<string>) => {
    const q = EMAIL_FILTERS.filter((f) => next.has(f.id)).map((f) => f.q).join(' ');
    window.dispatchEvent(new CustomEvent('email:set-filter', { detail: { q } }));
  };
  const toggleEmailFilter = (id: string) => {
    setEmailActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      persistFilters(next);
      broadcastFilters(next);
      return next;
    });
  };
  // Re-broadcast on mount so the EmailView picks up persisted filters from
  // last session without needing the user to toggle them again.
  useEffect(() => {
    if (currentView === 'email') broadcastFilters(emailActiveFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView]);
  const messagesSearchInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const handleToggle = () => {
      setMessagesSearchOpen((prev) => {
        const next = !prev;
        if (next) {
          requestAnimationFrame(() => messagesSearchInputRef.current?.focus());
        } else {
          setMessagesSearchValue('');
          window.dispatchEvent(
            new CustomEvent('messages:query-change', { detail: { query: '' } })
          );
        }
        return next;
      });
    };
    const handleDismiss = () => {
      setMessagesSearchOpen(false);
      setMessagesSearchValue('');
      window.dispatchEvent(
        new CustomEvent('messages:query-change', { detail: { query: '' } })
      );
    };
    window.addEventListener('messages:toggle-search', handleToggle);
    window.addEventListener('messages:dismiss-search', handleDismiss);
    return () => {
      window.removeEventListener('messages:toggle-search', handleToggle);
      window.removeEventListener('messages:dismiss-search', handleDismiss);
    };
  }, []);
  const handleMessagesSearchChange = (e: HeaderSearchChange) => {
    const value = getHeaderSearchValue(e);
    setMessagesSearchValue(value);
    window.dispatchEvent(
      new CustomEvent('messages:query-change', { detail: { query: value } })
    );
  };
  const dismissMessagesSearch = () => {
    setMessagesSearchOpen(false);
    setMessagesSearchValue('');
    setMessagesHeaderMode('idle');
    window.dispatchEvent(
      new CustomEvent('messages:query-change', { detail: { query: '' } })
    );
    window.dispatchEvent(new CustomEvent('messages:dismiss-search'));
  };

  // Compose morph — SquarePen pill expands into a "Search people and
  // projects" input. Mirrors the messages-search morph but owns a
  // separate open/value/ref triple so both can coexist without clashing.
  const [messagesComposeOpen, setMessagesComposeOpen] = useState(false);
  const [messagesComposeValue, setMessagesComposeValue] = useState('');
  const messagesComposeInputRef = useRef<HTMLInputElement>(null);
  // MessagesView's chat-change broadcast also reaches desktop; it suppresses
  // inbox-only actions while a conversation is selected. The conversation
  // identity itself comes from messagesConversationState below.
  const [desktopChatSelected, setDesktopChatSelected] = useState(false);
  useEffect(() => {
    const handleChatChange = (e: Event) => {
      setDesktopChatSelected(!!(e as CustomEvent<{ chatSlug: string | null }>).detail?.chatSlug);
    };
    window.addEventListener('messages:chat-change', handleChatChange);
    return () => window.removeEventListener('messages:chat-change', handleChatChange);
  }, []);
  // Same reasoning as desktopChatSelected above, for the NewMessagePanel:
  // it's a self-contained inspector with no local header of its own, so this
  // global header owns its title + back arrow, the same way it does for an
  // open conversation.
  const [desktopComposeOpen, setDesktopComposeOpen] = useState(false);
  useEffect(() => {
    const handleComposeState = (e: Event) => {
      setDesktopComposeOpen(!!(e as CustomEvent<{ open: boolean }>).detail?.open);
    };
    window.addEventListener('messages:compose-state', handleComposeState);
    return () => window.removeEventListener('messages:compose-state', handleComposeState);
  }, []);
  useEffect(() => {
    // Desktop no longer morphs the header into a search pill for compose —
    // MessagesView opens its own NewMessagePanel (fixed-width inspector,
    // same slot as ThreadPanel) off this same event. Mobile compose renders
    // its own To: field inside the compose screen content, so the header
    // morph was never wanted there either; nothing left for this listener.
    const handleDismissCompose = () => {
      setMessagesComposeOpen(false);
      setMessagesComposeValue('');
    };
    window.addEventListener('messages:dismiss-compose', handleDismissCompose);
    return () => {
      window.removeEventListener('messages:dismiss-compose', handleDismissCompose);
    };
  }, [isDesktopViewport]);
  const handleMessagesComposeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMessagesComposeValue(value);
    window.dispatchEvent(new CustomEvent('messages:query-change', { detail: { query: value } }));
  };
  const dismissMessagesCompose = () => {
    setMessagesComposeOpen(false);
    setMessagesComposeValue('');
    window.dispatchEvent(new CustomEvent('messages:query-change', { detail: { query: '' } }));
    window.dispatchEvent(new CustomEvent('messages:dismiss-compose'));
  };

  // Project-detail tab + sub-view state — lets the back button branch
  // through three levels:
  //   • application open  → close the application (back to list)
  //   • not on Overview   → bounce to Overview
  //   • on Overview       → leave the project (back to projects list)
  const [projectDetailTab, setProjectDetailTab] = useState<string>(projectActiveTab ?? 'overview');
  const [projectDetailApplicationOpen, setProjectDetailApplicationOpen] = useState<boolean>(false);
  useEffect(() => {
    if (projectActiveTab) setProjectDetailTab(projectActiveTab);
  }, [projectActiveTab]);
  const [procurementHeaderState, setProcurementHeaderState] = useState<ProjectProcurementHeaderState | null>(null);
  const procurementSearchInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const onChange = (event: Event) => {
      setProcurementHeaderState(
        (event as CustomEvent<ProjectProcurementHeaderState | null>).detail ?? null,
      );
    };
    window.addEventListener(PROCUREMENT_HEADER_STATE_EVENT, onChange);
    window.dispatchEvent(new CustomEvent(PROCUREMENT_HEADER_REQUEST_EVENT));
    return () => window.removeEventListener(PROCUREMENT_HEADER_STATE_EVENT, onChange);
  }, []);
  const handleProcurementSearchChange = (event: HeaderSearchChange) => {
    window.dispatchEvent(new CustomEvent(PROCUREMENT_SEARCH_EVENT, {
      detail: { query: getHeaderSearchValue(event) },
    }));
  };
  const clearProcurementSearch = () => {
    window.dispatchEvent(new CustomEvent(PROCUREMENT_SEARCH_EVENT, { detail: { query: '' } }));
  };
  const [templatesHeaderState, setTemplatesHeaderState] = useState<TemplatesHeaderState | null>(null);
  const templatesSearchInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const onChange = (event: Event) => {
      setTemplatesHeaderState(
        (event as CustomEvent<TemplatesHeaderState | null>).detail ?? null,
      );
    };
    window.addEventListener(TEMPLATES_HEADER_STATE_EVENT, onChange);
    window.dispatchEvent(new CustomEvent(TEMPLATES_HEADER_REQUEST_EVENT));
    return () => window.removeEventListener(TEMPLATES_HEADER_STATE_EVENT, onChange);
  }, []);
  useEffect(() => {
    if (currentView === 'system-settings' && (systemSettingsActiveTab ?? '').startsWith('templates')) {
      window.dispatchEvent(new CustomEvent(TEMPLATES_HEADER_REQUEST_EVENT));
      return;
    }
    setTemplatesHeaderState(null);
  }, [currentView, systemSettingsActiveTab]);
  const handleTemplatesSearchChange = (event: HeaderSearchChange) => {
    window.dispatchEvent(new CustomEvent(TEMPLATES_SEARCH_EVENT, {
      detail: { query: getHeaderSearchValue(event) },
    }));
  };
  const clearTemplatesSearch = () => {
    window.dispatchEvent(new CustomEvent(TEMPLATES_SEARCH_EVENT, { detail: { query: '' } }));
  };
  const interimIdentityLabel = useMemo(() => {
    if (!projectDetailApplicationOpen || !interimHeaderState) return null;
    const application = interimHeaderState.applicationNo
      ? `Application #${interimHeaderState.applicationNo}`
      : 'New application';
    const status = interimHeaderState.statusLabel.toUpperCase();
    const period = interimHeaderState.periodEndingLabel
      ? `Period ending ${interimHeaderState.periodEndingLabel}`
      : null;
    return [application, status, period].filter(Boolean).join(' · ');
  }, [interimHeaderState, projectDetailApplicationOpen]);
  const [sitePhotosHeaderState, setSitePhotosHeaderState] = useState<{
    isPreparing: boolean;
    isUploading: boolean;
    view: 'gallery' | 'map';
    fullView: boolean;
    projectName: string;
  }>({ isPreparing: false, isUploading: false, view: 'gallery', fullView: false, projectName: '' });
  const [projectEmailHeaderState, setProjectEmailHeaderState] = useState<{
    hasThreads: boolean;
    loading: boolean;
    isClient: boolean;
    query: string;
    detailOpen: boolean;
    subject: string;
    subtitle: string;
  }>({ hasThreads: false, loading: false, isClient: false, query: '', detailOpen: false, subject: '', subtitle: '' });
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<{ activeTab?: string; applicationOpen?: boolean }>).detail;
      if (detail?.activeTab) setProjectDetailTab(detail.activeTab);
      if (typeof detail?.applicationOpen === 'boolean') setProjectDetailApplicationOpen(detail.applicationOpen);
    };
    window.addEventListener('project-detail:state-change', onChange);
    return () => window.removeEventListener('project-detail:state-change', onChange);
  }, []);

  useEffect(() => {
    const onChange = (event: Event) => {
      const detail = (event as CustomEvent<Partial<typeof sitePhotosHeaderState>>).detail;
      if (!detail) return;
      setSitePhotosHeaderState((current) => ({ ...current, ...detail }));
    };
    window.addEventListener('site-photos:state-change', onChange);
    window.dispatchEvent(new CustomEvent('site-photos:request-state'));
    return () => window.removeEventListener('site-photos:state-change', onChange);
  }, []);

  // Project → Site Register tab, and the RFI Register (a project tab, and a
  // System Settings section for admins). Each page publishes its state on
  // mount and null on unmount; the request events cover a header that mounts
  // after the page has.
  const [projectOverviewHeaderState, setProjectOverviewHeaderState] = useState<ProjectOverviewHeaderState | null>(null);
  useEffect(() => {
    const onState = (event: Event) => {
      setProjectOverviewHeaderState((event as CustomEvent<ProjectOverviewHeaderState | null>).detail ?? null);
    };
    window.addEventListener(PROJECT_OVERVIEW_STATE_EVENT, onState);
    window.dispatchEvent(new CustomEvent(PROJECT_OVERVIEW_REQUEST_STATE_EVENT));
    return () => window.removeEventListener(PROJECT_OVERVIEW_STATE_EVENT, onState);
  }, []);
  const [siteRegisterHeaderState, setSiteRegisterHeaderState] = useState<SiteRegisterHeaderState | null>(null);
  useEffect(() => {
    const onState = (event: Event) => {
      setSiteRegisterHeaderState((event as CustomEvent<SiteRegisterHeaderState | null>).detail ?? null);
    };
    window.addEventListener(SITE_REGISTER_STATE_EVENT, onState);
    window.dispatchEvent(new CustomEvent(SITE_REGISTER_REQUEST_STATE_EVENT));
    return () => window.removeEventListener(SITE_REGISTER_STATE_EVENT, onState);
  }, []);
  const [rfiRegisterHeaderState, setRfiRegisterHeaderState] = useState<RfiRegisterHeaderState | null>(null);
  const [rfiFormHeaderState, setRfiFormHeaderState] = useState<RfiFormHeaderState | null>(null);
  const rfiSearchInputRef = useRef<HTMLInputElement>(null);
  // The field keeps its own value, as the other header searches do: bound to
  // the page's echoed state it would snap back between keystrokes. It follows
  // every set-query event, so the page clearing its search clears it too, and
  // a register that has just mounted starts it from that register's query.
  const [rfiSearchValue, setRfiSearchValue] = useState('');
  const [reportsSearchValue, setReportsSearchValue] = useState('');
  const reportsSearchInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const onQuery = (event: Event) => setReportsSearchValue((event as CustomEvent<{ query?: string }>).detail?.query ?? '');
    window.addEventListener('reports:query-change', onQuery);
    return () => window.removeEventListener('reports:query-change', onQuery);
  }, []);
  const rfiRegisterPublishedRef = useRef<RfiRegisterHeaderState | null>(null);
  useEffect(() => {
    const onQuery = (event: Event) => {
      setRfiSearchValue((event as CustomEvent<{ query?: string }>).detail?.query ?? '');
    };
    window.addEventListener(RFI_SET_QUERY_EVENT, onQuery);
    return () => window.removeEventListener(RFI_SET_QUERY_EVENT, onQuery);
  }, []);
  useEffect(() => {
    const onRegister = (event: Event) => {
      const next = (event as CustomEvent<RfiRegisterHeaderState | null>).detail ?? null;
      if (next && !rfiRegisterPublishedRef.current) setRfiSearchValue(next.query);
      rfiRegisterPublishedRef.current = next;
      setRfiRegisterHeaderState(next);
    };
    const onForm = (event: Event) => {
      setRfiFormHeaderState((event as CustomEvent<RfiFormHeaderState | null>).detail ?? null);
    };
    window.addEventListener(RFI_REGISTER_STATE_EVENT, onRegister);
    window.addEventListener(RFI_FORM_STATE_EVENT, onForm);
    window.dispatchEvent(new CustomEvent(RFI_REGISTER_REQUEST_STATE_EVENT));
    window.dispatchEvent(new CustomEvent(RFI_FORM_REQUEST_STATE_EVENT));
    return () => {
      window.removeEventListener(RFI_REGISTER_STATE_EVENT, onRegister);
      window.removeEventListener(RFI_FORM_STATE_EVENT, onForm);
    };
  }, []);
  // Settings mounts the register in its RFI Register section only, so on that
  // view a published state is enough to know it is the one on screen.
  const onRfiSurface = (currentView === 'project-detail' && projectDetailTab === 'rfis')
    || currentView === 'system-settings';
  const activeRfiForm = onRfiSurface ? rfiFormHeaderState : null;
  const activeRfiRegister = onRfiSurface && !rfiFormHeaderState ? rfiRegisterHeaderState : null;

  const [projectEmailSearchOpen, setProjectEmailSearchOpen] = useState(false);
  const [projectEmailSearchValue, setProjectEmailSearchValue] = useState('');
  const projectEmailSearchInputRef = useRef<HTMLInputElement>(null);
  const projectEmailSearchValueRef = useRef('');
  projectEmailSearchValueRef.current = projectEmailSearchValue;
  useEffect(() => {
    if (projectEmailHeaderState.query !== projectEmailSearchValue) {
      setProjectEmailSearchValue(projectEmailHeaderState.query);
    }
  }, [projectEmailHeaderState.query, projectEmailSearchValue]);
  useEffect(() => {
    const handleToggle = () => {
      setProjectEmailSearchOpen((previous) => {
        const next = !previous;
        if (next) requestAnimationFrame(() => projectEmailSearchInputRef.current?.focus());
        else {
          setProjectEmailSearchValue('');
          window.dispatchEvent(new CustomEvent('project-email:query-change', { detail: { query: '' } }));
        }
        return next;
      });
    };
    const replay = () => {
      if (projectEmailSearchValueRef.current) {
        window.dispatchEvent(new CustomEvent('project-email:query-change', {
          detail: { query: projectEmailSearchValueRef.current },
        }));
      }
    };
    window.addEventListener('project-email:toggle-search', handleToggle);
    window.addEventListener('project-email:request-search', replay);
    return () => {
      window.removeEventListener('project-email:toggle-search', handleToggle);
      window.removeEventListener('project-email:request-search', replay);
    };
  }, []);
  const handleProjectEmailSearchChange = (event: HeaderSearchChange) => {
    const value = getHeaderSearchValue(event);
    setProjectEmailSearchValue(value);
    window.dispatchEvent(new CustomEvent('project-email:query-change', { detail: { query: value } }));
  };
  const dismissProjectEmailSearch = () => {
    setProjectEmailSearchOpen(false);
    setProjectEmailSearchValue('');
    window.dispatchEvent(new CustomEvent('project-email:query-change', { detail: { query: '' } }));
  };

  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<Partial<typeof projectEmailHeaderState>>).detail;
      if (!detail) return;
      setProjectEmailHeaderState((current) => ({
        ...current,
        ...detail,
        hasThreads: !!detail.hasThreads,
        loading: !!detail.loading,
        isClient: !!detail.isClient,
        detailOpen: !!detail.detailOpen,
      }));
    };
    window.addEventListener('project-email:state-change', onChange);
    // The tab can mount before this global header effect subscribes.
    window.dispatchEvent(new CustomEvent('project-email:request-state'));
    return () => window.removeEventListener('project-email:state-change', onChange);
  }, []);

  const [messagesHeaderState, setMessagesHeaderState] = useState<{
    isSearchMode: boolean;
    mode: 'idle' | 'search' | 'compose';
    isComposeMode: boolean;
  }>({ isSearchMode: false, mode: 'idle', isComposeMode: false });
  const setMessagesHeaderMode = (mode: 'idle' | 'search' | 'compose') => {
    setMessagesHeaderState({
      isSearchMode: mode !== 'idle',
      mode,
      isComposeMode: mode === 'compose',
    });
  };
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<{ isSearchMode?: boolean; mode?: 'idle' | 'search' | 'compose'; isComposeMode?: boolean }>).detail;
      const mode = detail?.mode ?? (detail?.isComposeMode ? 'compose' : detail?.isSearchMode ? 'search' : 'idle');
      setMessagesHeaderState({
        isSearchMode: !!detail?.isSearchMode,
        mode,
        isComposeMode: mode === 'compose',
      });
    };
    window.addEventListener('messages:state-change', onChange);
    return () => window.removeEventListener('messages:state-change', onChange);
  }, []);

  // When a chat is open on mobile web (compact layout), ChatHeader broadcasts
  // `messages:conversation-state` with the conversation name + subtitle so the
  // shared header can display them instead of the bespoke ChatHeader bar.
  // After registering the listener we fire `messages:request-conversation-state`
  // so ChatHeader re-broadcasts its current value — this fixes HMR scenarios
  // where both files update and ChatHeader dispatches before we register.
  const [messagesConversationState, setMessagesConversationState] = useState<{
    name: string;
    subtitle: string | null;
  } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<{ name: string; subtitle: string | null } | null>).detail;
      setMessagesConversationState(detail ?? null);
    };
    window.addEventListener('messages:conversation-state', onChange);
    // Prompt ChatHeader to re-broadcast immediately in case it already
    // dispatched before this listener was registered (HMR race).
    window.dispatchEvent(new CustomEvent('messages:request-conversation-state'));
    return () => window.removeEventListener('messages:conversation-state', onChange);
  }, []);

  // The Members view publishes its title, count and mode so the back chevron,
  // "Members · 4 participants" and Add people live in this header rather than
  // a second header row inside the page. Null once the view closes.
  const [messagesMembersState, setMessagesMembersState] = useState<MessagesMembersHeaderState | null>(null);
  // Add members filters its people with the header search (desktop global
  // field, phone morph) rather than a field of its own in the page.
  const [membersQuery, setMembersQuery] = useState('');
  const [membersSearchOpen, setMembersSearchOpen] = useState(false);
  const membersSearchInputRef = useRef<HTMLInputElement>(null);
  const setMembersQueryAndSend = useCallback((query: string) => {
    setMembersQuery(query);
    window.dispatchEvent(new CustomEvent('messages:members:query', { detail: { query } }));
  }, []);
  const handleMembersSearchChange = (change: HeaderSearchChange) => setMembersQueryAndSend(getHeaderSearchValue(change));
  const dismissMembersSearch = () => { setMembersQueryAndSend(''); setMembersSearchOpen(false); };
  const membersAdding = Boolean(messagesMembersState?.adding);
  useEffect(() => {
    // Leaving Add members drops the query, so the next visit starts clean.
    if (membersAdding) return;
    setMembersSearchOpen(false);
    setMembersQuery((current) => {
      if (current) window.dispatchEvent(new CustomEvent('messages:members:query', { detail: { query: '' } }));
      return '';
    });
  }, [membersAdding]);
  useEffect(() => {
    const onChange = (e: Event) => {
      setMessagesMembersState((e as CustomEvent<MessagesMembersHeaderState | null>).detail ?? null);
    };
    window.addEventListener('messages:members-state', onChange);
    window.dispatchEvent(new CustomEvent('messages:request-members-state'));
    return () => window.removeEventListener('messages:members-state', onChange);
  }, []);

  // Employer-tab role-management state. The Add Project form's
  // EmployerTab publishes which roles are still available to add and
  // whether an employer is selected; the header renders the
  // user-plus pill when this lights up. Pill click → popover with
  // the inactive roles + a "Create new contact" entry; each item
  // dispatches an `add-project:employer:*` event the form listens
  // for.
  const [addProjectEmployerState, setAddProjectEmployerState] = useState<{
    available: boolean;
    hasContractor: boolean;
    inactiveRoles: Array<{ key: string; label: string }>;
  }>({ available: false, hasContractor: false, inactiveRoles: [] });
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<typeof addProjectEmployerState | undefined>).detail;
      if (!detail) {
        setAddProjectEmployerState({ available: false, hasContractor: false, inactiveRoles: [] });
        return;
      }
      setAddProjectEmployerState(detail);
    };
    window.addEventListener('add-project:employer-state-change', onChange);
    return () => window.removeEventListener('add-project:employer-state-change', onChange);
  }, []);

  const [supportModalOpen, setSupportModalOpen] = useState(false);

  // Refs for the iOS-style large-title and compact inline-title DOM nodes.
  // Updated directly by the scroll listener so React never re-renders on
  // the 60-120 Hz scroll path.
  const largeTitleRef = useRef<HTMLDivElement>(null);
  const compactTitleRef = useRef<HTMLDivElement>(null);

  // On the calendar view we show the current month name as the title
  // ("April") rather than the literal "Calendar" page name — matches
  // the iOS Calendar / Fantastical convention.
  /** Split a project title into its name and its " - 1259" reference for
   *  display. The reference is metadata, not part of the name, and rendering
   *  both at the title's weight makes the number read as if it were.
   *
   *  Anchored on a RUN OF DIGITS to the end, not on the separator: project
   *  names legitimately contain " - " (e.g. "Lower Ground Floor - Wall Types"),
   *  and splitting on the separator alone would tear those in half.
   *
   *  Presentation only: buildHeaderTitle keeps emitting one string, which the
   *  native toolbar and document.title still consume verbatim. */
  const splitTitleRef = (title: string): [string, string | null] => {
    const m = /^(.*) - (\d+)$/.exec(title);
    return m ? [m[1], `- ${m[2]}`] : [title, null];
  };

  const resolvedPageTitle = useMemo(() => {
    // When the file viewer is open, the filename becomes the header title.
    if (documentPreviewHeaderState) {
      return documentPreviewHeaderState.fileName;
    }
    if (
      projectDetailTab === 'planner'
      && plannerEditorHeaderState?.visible
      && plannerEditorHeaderState.presentation === 'native-header'
    ) {
      if (!plannerEditorHeaderState.canEdit) return 'Activity details';
      return plannerEditorHeaderState.editing ? 'Edit activity' : 'New activity';
    }
    if (
      currentView === 'project-detail'
      && projectDetailTab === 'email'
      && projectEmailHeaderState.detailOpen
      && projectEmailHeaderState.subject
    ) {
      return projectEmailHeaderState.subject;
    }
    if (currentView === 'project-detail' && projectDetailTab === 'site_photos') {
      return sitePhotosHeaderState.fullView ? 'Site Photo' : 'Site Photos';
    }
    if (currentView === 'project-detail' && projectDetailTab === 'site-register') {
      return 'Site Register';
    }
    if (currentView === 'project-detail' && projectReportIdFromTab(projectDetailTab) && reportHeaderState) {
      return reportHeaderState.title;
    }
    if (currentView === 'project-detail' && projectDetailTab === 'events' && eventFormState) return eventFormState.title;
    if (activeRfiForm) return activeRfiForm.title;
    if (activeRfiRegister) return currentView === 'project-detail' ? 'Changes' : 'RFI Register';
    if (projectDetailTab === 'takeoff' && takeoffCanvasState?.sheetName) {
      return takeoffCanvasState.sheetName;
    }
    if (currentView === 'calendar') {
      if (nativeBridge.isNative && (nativeBridge.platform === 'macos' || nativeBridge.platform === 'android')) {
        return calendarHeaderState?.headerLabel ?? 'Calendar';
      }
      return 'Calendar';
    }
    if ((currentView === 'resource-library' || isWorkspaceResourceLibrary) && rateItemFormState) {
      // Inside the rate-item edit form — the title becomes the form
      // mode; the library name is still reachable via the back chevron.
      return rateItemFormState.isEditing ? 'Edit Rate Item' : 'New Rate Item';
    }
    if (currentView === 'resource-library') {
      const activeLibrary = resourceLibraryPickerState?.libraries.find(
        (library) => library.id === resourceLibraryPickerState.selectedLibraryId,
      );
      if (activeLibrary) {
        return `${formatLibraryName(activeLibrary.name)} Resource Library`;
      }
    }
    if (
      (currentView === 'workspaces' || currentView === 'profile' || currentView === 'account')
      && usersFormState?.userName
    ) {
      // Editing a user is a level below the workspace, so the title names the
      // person — the form no longer carries its own identity block, and the
      // back chevron beside this returns to the user list. Same shape as the
      // document preview morphing the title to the filename.
      return usersFormState.userName;
    }
    if (currentView === 'workspaces' && workspaceDetailHeaderState?.clientName) {
      // Inside the directory split, anchor the header to whichever
      // workspace is open in the right pane (matches the URL's
      // /workspaces/{slug}/edit segment).
      return workspaceDetailHeaderState.clientName;
    }
    if (currentView === 'contacts' && contactsHeaderState?.scopeName) return contactsHeaderState.scopeName;
    if (currentView === 'employers' && employerSelectedName) {
      // Drilled into an employer: the profile is the whole content area and the
      // back chevron beside this returns to the directory. Same shape as the
      // workspace name replacing "Workspaces" above.
      return employerSelectedName;
    }
    const outreachRecordTitle = outreachApprovalsState?.title || prospectDetailState?.title;
    if (currentView === 'outreach' && outreachRecordTitle) {
      // Drilled into a batch or a prospect: the record names the page, the
      // same shape as the employer profile above.
      return outreachRecordTitle;
    }
    if (isPlantRoute && plantSelectedLabel) {
      // Drilled into a machine (or a still-unsaved "New machine" draft): same
      // shape as the employer profile above. isPlantRoute also covers the
      // register embedded in a project's H&S tab, not just standalone /plant.
      return plantSelectedLabel;
    }
    if ((currentView === 'payroll' || currentView === 'tax') && payrollTaxHeaderState?.sectionLabel) {
      return payrollTaxHeaderState.sectionLabel;
    }
    if (currentView === 'messages' && messagesMembersState) {
      return messagesMembersTitle(messagesMembersState);
    }
    // Compact navigation shows the conversation identity; desktop keeps the
    // feature title here and the chat identity in its content header.
    if (currentView === 'messages' && messagesConversationState && !isDesktopViewport) {
      return messagesConversationState.name;
    }
    // Desktop compose has its own pane header, so the global title stays put.
    // Compose screen (mobile) — the back-chevron view titles itself like
    // iOS Messages' sheet; the To: field sits below in the content.
    if (currentView === 'messages' && messagesHeaderState.isComposeMode && !isDesktopViewport) {
      return 'New Message';
    }
    if (
      currentView === 'system-settings'
      && templatesHeaderState
      && templatesHeaderState.mode !== 'library'
    ) {
      return templatesHeaderState.title;
    }
    // Keep the mobile Home app bar labelled during transient shell/HMR state.
    // The route title is authoritative once available; this only prevents an
    // otherwise blank 64px bar while the dashboard title prop reconnects.
    if (pageControls) return pageControls.title;
    if (hubSectionTitle) return hubSectionTitle;
    return pageTitle ?? (currentView === 'dashboard' ? 'Home' : null);
    // usersFormState: the edit-user title branch reads it, so the title would
    // otherwise stay on the workspace name until an unrelated dep churned.
  }, [currentView, pageTitle, hubSectionTitle, pageControls, calendarHeaderState, resourceLibraryHeaderState, resourceLibraryPickerState, rateItemFormState, workspaceDetailHeaderState, isWorkspaceResourceLibrary, usersFormState, documentPreviewHeaderState, payrollTaxHeaderState, messagesConversationState, messagesMembersState, messagesHeaderState.isComposeMode, isDesktopViewport, employerSelectedName, contactsHeaderState?.scopeName, isPlantRoute, plantSelectedLabel, outreachApprovalsState?.title, prospectDetailState?.title, projectDetailTab, projectEmailHeaderState.detailOpen, projectEmailHeaderState.subject, takeoffCanvasState, templatesHeaderState, plannerEditorHeaderState, eventFormState, sitePhotosHeaderState.fullView, activeRfiForm, activeRfiRegister, reportHeaderState]);

  const templatesHeaderSubtitle = currentView === 'system-settings'
    && templatesHeaderState
    && templatesHeaderState.mode !== 'library'
      ? templatesHeaderState.subtitle
      : null;

  const sitePhotosHeaderSubtitle = currentView === 'project-detail'
    && projectDetailTab === 'site_photos'
      ? sitePhotosHeaderState.projectName
      : null;

  // "12 RFIs, 1 overdue", or an open RFI's subject. Shown under the title in
  // the phone header; the desktop toolbar is a single line.
  const rfiHeaderSubtitle = activeRfiForm
    ? activeRfiForm.subtitle
    : activeRfiRegister?.subtitle ?? null;

  // Native-chrome shells (iOS tab bar, macOS sidebar + toolbar) replace the
  // web main header. Project-list chrome stays web-owned except under the
  // iOS tab bar, since the macOS toolbar has no list controls.
  const shouldUseNativeMainHeader = nativeShellReplacesWebHeader(nativeBridge);
  const nativeOwnsProjectListChrome =
    nativeBridge.isNative && nativeBridge.hasNativeTabBar;
  const shouldRenderMainRow = !shouldUseNativeMainHeader;
  // Desktop only — this trigger lives in the desktop row. Mobile's leading
  // edge is built entirely from `mobileHeaderConfig.leadingActions` (see the
  // project-sections toggle in the 'project-detail' case), so a flag here
  // would never reach it.
  const [sidebarToggleInHeader, setSidebarToggleInHeader] = useState(false);
  useEffect(() => {
    const onSidebarState = (event: Event) => {
      const detail = (event as CustomEvent<{ collapsed: boolean; toggleInHeader?: boolean }>).detail;
      setSidebarToggleInHeader(detail.toggleInHeader ?? detail.collapsed);
    };
    window.addEventListener('app-sidebar:state-change', onSidebarState);
    window.dispatchEvent(new CustomEvent('app-sidebar:request-state'));
    return () => window.removeEventListener('app-sidebar:state-change', onSidebarState);
  }, []);
  const shouldRenderDesktopSidebarToggle =
    shouldRenderMainRow &&
    !nativeBridge.isNative &&
    isDesktopViewport && usesTabletSidebarLayout && sidebarToggleInHeader;
  // A tablet puts the second rail away from here: the sidebar button is one of the few controls
  // the leading edge may carry.
  const sectionRail = useSectionRailHeaderState();
  const collapsibleSectionRail = useSectionRailCollapseHeaderState();
  const shouldRenderSecondRailToggle =
    shouldRenderMainRow && railReplacesHeaderShortcuts && sectionRail.available;
  // Only the phone's Alerts page drops them: the desktop frame (tablets included) reaches
  // Alerts as a page too and still needs search, AI and messages up here.
  const shouldRenderHeaderActions =
    currentView !== 'notifications' || isDesktopViewport;
  // The back/forward pair is pinned 8px left of search, with the same 36px boxes as other controls.
  // so the title must stop short of it as it does of the search itself. A tablet shows Back only.
  const historyArrowsWidth = railReplacesHeaderShortcuts ? 52 : 82;
  const desktopSearchLayout = useDesktopHeaderSearchLayout(
    isDesktopViewport && shouldRenderMainRow, resolvedPageTitle !== null,
    shouldRenderHeaderActions ? 52 : 0, isDesktopViewport && !isNativeShell() ? historyArrowsWidth : 0,
  );

  // Contextual sub-nav items. The header renders these as a flat tab strip,
  // so drop the kind:'heading' group labels that buildProjectSidebarTabItems
  // interleaves for the sidebar — they'd otherwise show as empty, iconless tabs.
  const workspaceNavigation = useWorkspaceNavigation(projectTabProps?.projectId);
  const projectTabItems = useMemo(
    () => buildProjectSidebarTabItems(projectTabProps ?? null, projectPermissions ?? null, workspaceNavigation.authoritative ? workspaceNavigation.navigation?.project ?? null : undefined, projectDetailTab)
      .filter((tab) => tab.kind !== 'heading'),
    [projectTabProps, projectPermissions, workspaceNavigation.authoritative, workspaceNavigation.navigation, projectDetailTab]
  );
  const searchHistoryOwner = JSON.stringify([user?.id, activeWorkspaceId]);
  const navigationSearchScope = JSON.stringify([searchHistoryOwner, currentView, projectTabProps?.projectId, projectDetailTab]);
  const [navigationSearch, setNavigationSearch] = useState({ scope: '', value: '' });
  const navigationSearchInputRef = useRef<HTMLInputElement>(null);
  const recentSearchMainItems = useMemo(() => {
    const groups = getSidebarNavGroups({ role: user?.role ?? '', internalWorkspace: workspaceNavigation.internalWorkspace, resolvedItems: workspaceNavigation.navigation?.main ?? null });
    return [...groups.mainNavItems, ...groups.crmNavItems, ...groups.adminNavItems];
  }, [user?.role, workspaceNavigation.internalWorkspace, workspaceNavigation.navigation]);
  const recentSearchMainIds = useMemo(() => recentSearchMainItems.map(item => item.id), [recentSearchMainItems]);
  const recentSearchProjectIds = useMemo(() => projectTabItems.map(item => item.id), [projectTabItems]);
  const currentSearchPlace = useMemo<RecentHeaderPlace | null>(() => {
    if (!user?.id || !activeWorkspaceId) return null;
    if (currentView === 'project-detail' && projectTabProps?.projectId && onProjectTabChange) {
      const item = projectTabItems.find(tab => tab.id === projectDetailTab);
      return item ? { id: `project:${projectTabProps.projectId}:${item.id}`, label: item.label,
        description: projectTabProps.projectName,
        route: { view: 'project-detail', projectId: projectTabProps.projectId, tab: item.id } } : null;
    }
    const item = recentSearchMainItems.find(item => item.id === currentView);
    return item && onNavigate ? { id: item.id, label: item.label, route: { view: item.id } } : null;
  }, [user?.id, activeWorkspaceId, currentView, projectTabProps?.projectId, projectTabProps?.projectName, projectDetailTab, projectTabItems, recentSearchMainItems, onNavigate, onProjectTabChange]);
  const recentSearchPlaces = useRecentHeaderPlaces({ owner: searchHistoryOwner, current: currentSearchPlace,
    mainIds: recentSearchMainIds, projectId: currentView === 'project-detail' ? projectTabProps?.projectId : undefined, projectTabIds: recentSearchProjectIds });

  const projectScreenLabel = isProjectDetailView && resolvedPageTitle === pageTitle
    ? (projectDetailTab === 'hs' && hsHeaderState && hsHeaderState.view !== 'hub'
        ? ((hsHeaderState?.section ?? 'rams') === 'rams'
          ? getHSSectionLabel(hsHeaderState?.view === 'editor'
            ? hsEditorHeaderState?.activeSection
            : hsHeaderState?.view === 'record' ? hsDocumentHeaderState?.activeSection : undefined)
          : undefined) ?? HS_SECTION_LABELS[hsHeaderState?.section ?? 'rams']
        : undefined)
      ?? PROJECT_PROCUREMENT_SECTIONS.find((section) => section.id === projectDetailTab)?.label
      ?? (isProjectAdminTab(projectDetailTab) ? 'Admin' : undefined)
      ?? projectTabItems.find((tab) => tab.id === projectDetailTab)?.label
    : undefined;

  // Only show the project tab sub-nav in the header when running inside the
  // native app shell. On mobile web the sections live behind the leading
  // sidebar toggle (the drilled-in sidebar lists them), so a second tab row
  // at the top would just clutter the screen.
  const showProjectSubNav =
    isProjectDetailView &&
    projectTabItems.length > 0 &&
    !!onProjectTabChange &&
    nativeBridge.isNative;
  const projectListHeaderProps =
    isProjectListView && !nativeOwnsProjectListChrome ? (projectListProps ?? null) : null;
  const shouldShowProjectListSearch = !nativeOwnsProjectListChrome;
  // Floating canvas header is the default on every viewport — desktop
  // included. Header is transparent (no purple fill) and the title text
  // flips to dark so it reads against the page bg. Mirrors the gate in
  // AppLayout.
  const usesFloatingMobileDashboardHeader = !!currentView;

  const isTakeoffView = currentView === 'project-detail' && projectDetailTab === 'takeoff';
  const takeoffGallerySearchAvailable = isTakeoffGallerySearchAvailable(
    isTakeoffView,
    takeoffHeaderState,
    !!takeoffCanvasState,
  );

  const mobileHeaderConfigBase = useMemo(() => {
    const trailingActions: MobileHeaderAction[] = [];
    const leadingActions: MobileHeaderAction[] = [];

    // Background BOQ-import progress pill — pinned to the leading edge
    // of trailingActions so the user can leave the importer (or even the
    // BOQ entirely) and still see "Importing 247/930" wherever they go.
    // Spinner during pending, check on done, alert on error. Click is a
    // noop — purely informational; the toast on completion does the
    // celebrating, the pill auto-clears 3 s after done/error (handled
    // upstream in the listener).
    if (boqImportProgress) {
      const { sent, total, phase } = boqImportProgress;
      trailingActions.push({
        id: 'boq-import-progress',
        icon: phase === 'pending' ? Loader2 : phase === 'done' ? CheckCircle2 : AlertCircle,
        label:
          phase === 'pending'
            ? (total > 0 ? `Importing ${sent}/${total}` : 'Importing…')
            : phase === 'done'
              ? `Imported ${total}`
              : 'Import failed',
        ariaLabel: 'BOQ import progress',
        onClick: () => {/* informational pill */},
        group: 'progress',
      });
    }

    // BOQ Importer takes over the header when its overlay is open.
    // The importer canvas is full-screen so the rest of the page-context
    // actions don't apply. Bell stays out too.
    if (boqImporterState?.open) {
      const {
        totalCount = 0, measuredCount = 0, excludedCount = 0,
        reviewFilter = 'all', selectedCount = 0, bills = [],
      } = boqImporterState;

      if (boqImporterState.currentStep !== 'map-review') return { trailingActions, leadingActions };

      // Search pill
      leadingActions.push({
        id: 'boq-importer-search',
        icon: Search,
        ariaLabel: 'Search items',
        active: boqImporterSearchOpen || boqImporterSearchValue.length > 0,
        onClick: () => setBoqImporterSearchOpen(prev => {
          const next = !prev;
          if (next) requestAnimationFrame(() => boqImporterSearchInputRef.current?.focus());
          else dismissBoqImporterSearch();
          return next;
        }),
        group: 'view',
      });

      // Single filter pill — opens a popover with All / Items / Headings / Excluded.
      // Active (brand tint) when a non-default filter is selected.
      const dispatchFilter = (filter: 'all' | 'items' | 'headings' | 'excluded') =>
        window.dispatchEvent(new CustomEvent('boq-importer:filter-change', { detail: { filter } }));
      const filterLabel = reviewFilter === 'all' ? 'Filter'
        : reviewFilter === 'items' ? 'Items'
        : reviewFilter === 'headings' ? 'Headings'
        : 'Excluded';
      const filterSubActions: MobileHeaderSubAction[] = [
        { id: 'all',      label: `All (${totalCount})`,                     onClick: () => dispatchFilter('all'),      trailingIcon: reviewFilter === 'all'      ? Check : undefined },
        { id: 'items',    label: `Items (${measuredCount})`,                 onClick: () => dispatchFilter('items'),    trailingIcon: reviewFilter === 'items'    ? Check : undefined },
        { id: 'headings', label: `Headings (${totalCount - measuredCount})`, onClick: () => dispatchFilter('headings'), trailingIcon: reviewFilter === 'headings' ? Check : undefined },
        ...(excludedCount > 0 ? [{ id: 'excluded', label: `Excluded (${excludedCount})`, onClick: () => dispatchFilter('excluded'), trailingIcon: reviewFilter === 'excluded' ? Check : undefined } as MobileHeaderSubAction] : []),
      ];
      // Filter is view configuration → trailing, not leading.
      trailingActions.push({
        id: 'boq-importer-filter',
        icon: SlidersHorizontal,
        label: reviewFilter !== 'all' ? filterLabel : undefined,
        ariaLabel: 'Filter rows',
        active: reviewFilter !== 'all',
        group: 'view',
        subActions: filterSubActions,
      });

      // Assign-to-bill pill — only visible when rows are selected
      if (selectedCount > 0) {
        trailingActions.push({
          id: 'boq-importer-assign-bill',
          label: `${selectedCount} rows`,
          ariaLabel: 'Assign to bill',
          group: 'page',
          ...(bills.length > 0 ? {
            subActions: bills.map(b => ({
              id: b.id,
              label: b.bill_title || `Bill ${b.bill_no}`,
              onClick: () => window.dispatchEvent(new CustomEvent('boq-importer:assign-to-bill', { detail: { billId: b.id } })),
            })),
          } : {
            onClick: () => {},
          }),
        });
      }

      // Select-all / Clear trailing pill
      const allSelected = selectedCount > 0 && selectedCount >= totalCount;
      trailingActions.push({
        id: 'boq-importer-select-all',
        label: allSelected ? 'Clear' : 'Select all',
        ariaLabel: allSelected ? 'Clear selection' : 'Select all rows',
        onClick: () => window.dispatchEvent(new CustomEvent('boq-importer:select-all', { detail: { selectAll: !allSelected } })),
        group: 'page',
      });

      // AI map + Close
      if (boqImporterState.canAiMap) {
        trailingActions.push({
          id: 'boq-importer-ai-map',
          icon: Sparkles,
          ariaLabel: boqImporterState.isAiMapping ? 'Mapping…' : 'AI map columns',
          onClick: () => window.dispatchEvent(new CustomEvent('boq-importer:ai-map')),
          group: 'page',
        });
      }
      return { trailingActions, leadingActions };
    }

    const pushNotificationsAction = () => {
      if (!shouldRenderHeaderActions || (!onNavigate && !onToggleNotifications)) return;
      trailingActions.push({
        id: 'notifications',
        icon: Bell,
        ariaLabel: 'Notifications',
        onClick: () => {
          if (isDesktopViewport && onToggleNotifications) {
            onToggleNotifications();
          } else if (onNavigate) {
            onNavigate('notifications');
          } else {
            onToggleNotifications?.();
          }
        },
        group: 'app',
        badge: notificationsUnreadCount,
      });
    };


    const pushBackAction = (targetView: string, label: string, onClickOverride?: () => void) => {
      if (!onNavigate && !onClickOverride) return;
      leadingActions.push({
        id: `back-${targetView}`,
        icon: ChevronLeft,
        ariaLabel: label,
        onClick: () => {
          if (onClickOverride) onClickOverride();
          else if (onNavigate) onNavigate(targetView);
        },
      });
    };

    const pushWorkspaceUsersListActions = () => {
      if (!isWorkspaceUsersTab) return;
      const activeRole = USER_ROLE_FILTER_OPTIONS.find(o => o.value === usersRoleFilter);
      trailingActions.push({
        id: 'workspace-users-filter',
        icon: SlidersHorizontal,
        label: activeRole?.label ?? 'All Roles',
        ariaLabel: 'Filter users by role',
        group: 'view',
        subActions: USER_ROLE_FILTER_OPTIONS.map(opt => ({
          id: `wuf-${opt.value}`,
          label: opt.label,
          active: usersRoleFilter === opt.value,
          onClick: () => {
            setUsersRoleFilter(opt.value);
            window.dispatchEvent(new CustomEvent(WORKSPACE_USERS_FILTER_EVENT, { detail: { role: opt.value } }));
          },
        })),
      });
      trailingActions.push({
        id: 'workspace-users-add',
        icon: UserPlus,
        label: 'Invite member',
        ariaLabel: 'Invite member',
        onClick: () => window.dispatchEvent(new CustomEvent(WORKSPACE_USERS_CREATE_EVENT)),
        group: 'page',
      });
    };

    const pushWorkspaceActivityActions = () => {
      if (!isWorkspaceActivityTab) return;
      const selectedCategory = workspaceActivityHeaderState?.selectedCategory ?? 'all';
      const activeCategory = WORKSPACE_ACTIVITY_CATEGORY_OPTIONS.find(
        (option) => option.id === selectedCategory,
      );
      trailingActions.push({
        id: 'workspace-activity-filter',
        icon: SlidersHorizontal,
        label: activeCategory?.label ?? 'All activity',
        ariaLabel: 'Filter workspace activity',
        group: 'view',
        subActions: WORKSPACE_ACTIVITY_CATEGORY_OPTIONS.map((option) => ({
          id: `workspace-activity-filter-${option.id}`,
          label: option.label,
          active: selectedCategory === option.id,
          onClick: () => window.dispatchEvent(new CustomEvent(
            WORKSPACE_ACTIVITY_FILTER_EVENT,
            { detail: { category: option.id } },
          )),
        })),
      });
      trailingActions.push({
        id: 'workspace-activity-export',
        icon: Download,
        ariaLabel: 'Export workspace activity',
        disabled: workspaceActivityHeaderState?.loading || workspaceActivityHeaderState?.count === 0,
        onClick: () => window.dispatchEvent(new CustomEvent(WORKSPACE_ACTIVITY_EXPORT_EVENT)),
        group: 'view',
      });
    };

    const pushWorkspaceInviteActions = () => {
      if (!isWorkspaceInviteHeaderForm) return;
      trailingActions.push({
        id: 'workspace-invite-cancel',
        label: 'Cancel',
        ariaLabel: 'Cancel invitation',
        disabled: usersHeaderState?.inviteSubmitting,
        onClick: () => window.dispatchEvent(new CustomEvent(WORKSPACE_USERS_INVITE_CANCEL_EVENT)),
        group: 'page',
      });
      trailingActions.push({
        id: 'workspace-invite-submit',
        icon: usersHeaderState?.inviteSubmitting ? Loader2 : undefined,
        label: usersHeaderState?.inviteSubmitting ? 'Sending' : 'Send invitation',
        ariaLabel: usersHeaderState?.inviteSubmitting ? 'Sending invitation' : 'Send invitation',
        disabled: !usersHeaderState?.inviteCanSubmit || usersHeaderState?.inviteSubmitting,
        onClick: () => window.dispatchEvent(new CustomEvent(WORKSPACE_USERS_INVITE_SUBMIT_EVENT)),
        group: 'page',
      });
    };

    const pushWorkspaceBotsActions = () => {
      if (!isBotsTabOpen || !botsHeaderState) return;
      if (botsHeaderState.listVisible) {
        leadingActions.push({
          id: 'workspace-bots-search',
          icon: Search,
          ariaLabel: 'Search AI agents',
          onClick: () => setBotsSearchOpen(true),
        });
        trailingActions.push({
          id: 'workspace-bots-add',
          icon: Plus,
          label: 'New agent',
          ariaLabel: 'New agent',
          onClick: () => window.dispatchEvent(new CustomEvent(WORKSPACE_BOTS_CREATE_EVENT)),
          group: 'page',
        });
        return;
      }
      if (!botsHeaderState.detailOpen) return;
      if (botsHeaderState.hasChanges && !botsHeaderState.isSaving) {
        trailingActions.push({
          id: 'workspace-bots-cancel',
          label: 'Cancel',
          ariaLabel: 'Discard changes',
          onClick: () => window.dispatchEvent(new CustomEvent(WORKSPACE_BOTS_CANCEL_EVENT)),
          group: 'page',
        });
      }
      if (botsHeaderState.hasChanges || botsHeaderState.isSaving) {
        trailingActions.push({
          id: 'workspace-bots-save',
          icon: botsHeaderState.isSaving ? Loader2 : undefined,
          label: botsHeaderState.isSaving ? 'Saving' : 'Save',
          ariaLabel: botsHeaderState.isSaving ? 'Saving agent' : 'Save changes',
          disabled: botsHeaderState.isSaving,
          onClick: () => window.dispatchEvent(new CustomEvent(WORKSPACE_BOTS_SAVE_EVENT)),
          group: 'page',
        });
      }
    };

    switch (currentView) {
      case 'calendar': {
        if (usesMaterialMobileChrome) leadingActions.push({ id: 'calendar-views-toggle', icon: SidebarLeftIcon, ariaLabel: 'Calendar views', onClick: () => setSidebarOpenMobile(true) });
        // Delegated to buildCalendarHeaderActions — see
        // src/platform/navigation/headerActions/calendar.ts.
        const calendarResult = buildCalendarHeaderActions({
          materialMobile: usesMaterialMobileChrome,
          nativeToolbar: nativeBridge.isNative && (nativeBridge.platform === 'macos' || nativeBridge.platform === 'android'),
          desktopLabelMode: hasRoomForCalendarLabels ? 'all' : hasRoomForCalendarPrimaryLabels ? 'primary' : 'icons',
          calendarHeaderState,
          tasksSelection,
          calendarOverdueCount,
          taskQueueOpen,
          onToggleTaskQueue,
          bookingPanelOpen,
          onToggleBooking,
          onNavigate,
          pushNotificationsAction,
        });
        leadingActions.push(...calendarResult.leadingActions);
        trailingActions.push(...calendarResult.trailingActions);
        break;
      }
      case 'notifications':
      case 'support':
        // Top-level page (Alerts, Support) — no back pill so the large title is shown.
        break;
      case 'more':
        // Top-level tab — no back pill, large title collapses on scroll.
        break;
      case 'preferences':
        // Desktop switches sections from the sidebar. On a phone a pane is a
        // drill-in from the section list, so the chevron is the way back to
        // it; the list itself gets the generic Back-to-More below.
        if (!isDesktopViewport && preferencesActiveTab && onPreferencesTabChange) {
          pushBackAction('preferences', 'Back to preferences', () => onPreferencesTabChange(null));
        }
        break;
      case 'profile':
      case 'account':
        // A section (Personal details, Notifications, ...) is a drill-in from
        // Overview — the mobile Account list is the way in, so the chevron
        // must be the way out, or a phone lands back on the section next
        // visit with no route to Overview. Overview itself exits to Home.
        if (isWorkspaceInviteForm) {
          pushBackAction('profile', 'Back to users', () =>
            window.dispatchEvent(new CustomEvent(WORKSPACE_USERS_INVITE_CANCEL_EVENT)));
        } else if (isWorkspaceUserForm) {
          pushBackAction('profile', 'Back to users', () =>
            window.dispatchEvent(new CustomEvent(WORKSPACE_USER_FORM_CANCEL_EVENT)));
        } else if (isBotsTabOpen && botsHeaderState?.detailOpen) {
          pushBackAction('profile', 'Back to AI Agents', () =>
            window.dispatchEvent(new CustomEvent(WORKSPACE_BOTS_BACK_EVENT)));
        } else if (profileActiveTab && profileActiveTab !== 'overview' && onProfileTabChange) {
          pushBackAction('profile', 'Back to profile', () => onProfileTabChange('overview'));
        } else {
          pushBackAction('dashboard', `Back to dashboard`);
        }
        if ((profileActiveTab === 'signature' || isWorkspaceUserForm) && profileSignatureState) {
          trailingActions.push({
            id: 'profile-signature-copy', label: 'Copy for Mac Mail', ariaLabel: 'Copy for Mac Mail',
            disabled: !profileSignatureState?.canCopy,
            onClick: () => window.dispatchEvent(new CustomEvent('profile-signature:copy')), group: 'create',
          }, {
            id: 'profile-signature-copy-mobile', label: 'Copy for iPhone / iPad', ariaLabel: 'Copy for iPhone or iPad Mail',
            disabled: !profileSignatureState?.canCopy,
            onClick: () => window.dispatchEvent(new CustomEvent('profile-signature:copy', { detail: { format: 'text' } })), group: 'create',
          }, {
            id: 'profile-signature-template', label: 'Contract Group template', ariaLabel: 'Use Contract Group template',
            disabled: profileSignatureState?.isFormOpen,
            onClick: () => window.dispatchEvent(new CustomEvent('profile-signature:template')), group: 'create',
          }, {
            id: 'profile-signature-add', label: 'Add signature', ariaLabel: 'Add signature', emphasis: 'primary',
            disabled: profileSignatureState?.isFormOpen,
            onClick: () => window.dispatchEvent(new CustomEvent('profile-signature:add')), group: 'create',
          });
        }
        // Profile > Details stages edits until Save. When it's dirty, surface
        // Save/Cancel in the header (dispatched back to useProfileDraft).
        if (profileActiveTab === 'details' && profileDetailsFormState?.hasChanges) {
          trailingActions.push({
            // Text pill, not an X — see the workspace-cancel note below.
            id: 'profile-details-cancel',
            label: 'Cancel',
            ariaLabel: 'Discard changes',
            onClick: () => window.dispatchEvent(new CustomEvent('profile-details:cancel')),
            group: 'page',
          });
          trailingActions.push({
            id: 'profile-details-save',
            label: 'Save Changes',
            ariaLabel: 'Save changes',
            onClick: () => window.dispatchEvent(new CustomEvent('profile-details:save')),
            group: 'page',
          });
        }
        // Workspace organisation pages now live inside Profile for the active
        // workspace owner/admin. Keep the same Save/Cancel bridge the former
        // standalone /organisation screen used.
        if (profileActiveTab?.startsWith('workspace/') && workspaceFormState) {
          if (workspaceFormState.hasChanges) {
            trailingActions.push({
              id: 'workspace-cancel',
              label: 'Cancel',
              ariaLabel: 'Discard changes',
              disabled: workspaceFormState.isSaving,
              onClick: () => window.dispatchEvent(new CustomEvent('workspace:cancel')),
              group: 'page',
            });
          }
          trailingActions.push({
            id: 'workspace-save',
            icon: workspaceFormState.isSaving ? Loader2 : undefined,
            label: workspaceFormState.isSaving ? 'Saving' : 'Save',
            ariaLabel: workspaceFormState.isSaving
              ? 'Saving workspace'
              : workspaceFormState.hasChanges ? 'Save workspace' : 'No changes to save',
            disabled: workspaceFormState.isSaving || !workspaceFormState.hasChanges,
            onClick: () => window.dispatchEvent(new CustomEvent('workspace:save')),
            group: 'page',
          });
        }
        pushWorkspaceUsersListActions();
        pushWorkspaceInviteActions();
        pushWorkspaceActivityActions();
        pushWorkspaceBotsActions();
        break;
      case 'organisation':
        pushBackAction('dashboard', 'Back to dashboard');
        if (workspaceFormState) {
          if (workspaceFormState.hasChanges) {
            trailingActions.push({
              // Cancel is a text pill, never an X. An X reads as Close
              // (dismiss the view) rather than discard-my-edits, and Apple's
              // toolbar guidance is explicit that a symbol and a word must not
              // share one button. Pairs with the Save text pill beside it.
              id: 'workspace-cancel',
              label: 'Cancel',
              ariaLabel: 'Discard changes',
              disabled: workspaceFormState.isSaving,
              onClick: () => window.dispatchEvent(new CustomEvent('workspace:cancel')),
              group: 'page',
            });
          }
          trailingActions.push({
            id: 'workspace-save',
            icon: workspaceFormState.isSaving ? Loader2 : undefined,
            label: workspaceFormState.isSaving ? 'Saving' : 'Save',
            ariaLabel: workspaceFormState.isSaving
              ? 'Saving workspace'
              : workspaceFormState.hasChanges ? 'Save workspace' : 'No changes to save',
            disabled: workspaceFormState.isSaving || !workspaceFormState.hasChanges,
            onClick: () => window.dispatchEvent(new CustomEvent('workspace:save')),
            group: 'page',
          });
        }
        pushNotificationsAction();
        break;
      case 'contacts': {
        // Leading = navigation only. Filter moved to trailing: Apple's leading
        // enumeration is closed (back/dismiss, sidebar toggle, title, document
        // menu) and list-narrowing tools are not on it. Trailing = filter plus
        // the contact actions (Edit / Add). NOTE: contacts-search MUST stay in
        // leadingActions — its expanding morph-pill is only rendered by
        // renderLeadingAction, so relocating it needs a morph refactor, not a
        // move. On desktop it is already trailing via DESKTOP_GLOBAL_SEARCH_IDS.
        if (!usesMaterialMobileChrome) {
        leadingActions.push({
          id: 'contacts-search',
          icon: Search,
          ariaLabel: 'Search companies',
          onClick: () => window.dispatchEvent(new CustomEvent('contacts:toggle-search')),
        });
        }
        // Filter pill — opens a popover with the dynamic list of options
        // (All / internal / each client company / each main contractor)
        // broadcast by CompaniesView. Active filter paints a brand badge.
        const filterOptions = contactsHeaderState?.filterOptions ?? [];
        const activeFilter = contactsHeaderState?.activeFilter ?? 'all';
        // Inside a company's people list the chevron is the way out and the title names the company.
        if (contactsHeaderState?.scopeName) {
          pushBackAction('contacts', 'Back to companies', () => window.dispatchEvent(new CustomEvent('contacts:back')));
        }
        const subActions: MobileHeaderSubAction[] = filterOptions.map((opt) => ({
          id: `contacts-filter-${opt.id}`,
          label: opt.label,
          active: activeFilter === opt.id,
          onClick: () => window.dispatchEvent(
            new CustomEvent('contacts:set-filter', { detail: { filter: opt.id } })
          ),
        }));
        // Filter is view configuration → trailing, not leading.
        if (subActions.length > 0) {
          trailingActions.push({
            id: 'contacts-filter',
            icon: SlidersHorizontal,
            ariaLabel: 'Filter companies by type',
            badge: activeFilter !== 'all' ? 1 : 0,
            subActions,
            group: 'view',
          });
        }
        if (contactsHeaderState?.isEditing) {
          // Edit mode — Cancel / Remove / Save take over the trailing edge.
          trailingActions.push({
            id: 'contacts-cancel',
            label: 'Cancel',
            ariaLabel: 'Cancel editing',
            onClick: () => window.dispatchEvent(new CustomEvent('contacts:cancel-edit')),
          });
          if (contactsHeaderState?.canRemove) {
            trailingActions.push({
              id: 'contacts-remove',
              label: 'Remove',
              ariaLabel: 'Remove contact',
              group: 'modify',
              onClick: () => window.dispatchEvent(new CustomEvent('contacts:remove-contact')),
            });
          }
          trailingActions.push({
            id: 'contacts-save',
            label: 'Save',
            ariaLabel: 'Save contact',
            group: 'create',
            onClick: () => window.dispatchEvent(new CustomEvent('contacts:save-edit')),
          });
        } else {
          // Edit action — a word ("Edit"), shown only when a contact is selected
          // and the viewer has edit rights.
          if (contactsHeaderState?.canEdit && contactsHeaderState?.hasSelectedContact) {
            trailingActions.push({
              id: 'contacts-edit',
              label: 'Edit',
              ariaLabel: 'Edit contact',
              group: 'modify',
              onClick: () => window.dispatchEvent(new CustomEvent('contacts:open-edit')),
            });
          }
          // Add action — only for privileged roles (super_admin / internal)
          const createOptions = contactsHeaderState?.createOptions ?? [];
          if (contactsHeaderState?.canAdd && createOptions.length > 1) {
            trailingActions.push({
              id: 'contacts-add',
              icon: Plus,
              ariaLabel: 'Add',
              group: 'create',
              subActions: createOptions.map((opt) => ({
                id: `contacts-create-${opt.id}`,
                label: opt.label,
                onClick: () => window.dispatchEvent(new CustomEvent(opt.event)),
              })),
            });
          } else if (contactsHeaderState?.canAdd) {
            trailingActions.push({
              id: 'contacts-add',
              icon: Plus,
              ariaLabel: createOptions[0]?.label ?? 'Add contact',
              group: 'create',
              onClick: () => window.dispatchEvent(new CustomEvent(createOptions[0]?.event ?? 'contacts:open-add')),
            });
          }
        }
        pushNotificationsAction();
        break;
      }
      case 'resource-library': {
        // Delegated to buildResourceLibraryHeaderActions — see
        // src/platform/navigation/headerActions/resourceLibrary.ts.
        buildResourceLibraryHeaderActions({
          resourceLibraryHeaderState,
          rateItemFormState,
          isDesktopViewport,
          leadingActions,
          trailingActions,
          pushNotificationsAction,
        });
        if (usesMaterialMobileChrome) {
          const searchIndex = leadingActions.findIndex(action => action.id === 'resource-library-search');
          if (searchIndex >= 0) leadingActions.splice(searchIndex, 1);
        }
        break;
      }
      case 'email': {
        if (usesMaterialMobileChrome && !emailThreadOpen) leadingActions.push({ id: 'email-folders-toggle', icon: SidebarLeftIcon, ariaLabel: 'Mail folders', onClick: () => setSidebarOpenMobile(true) });
        // Delegated to buildEmailHeaderActions — see
        // src/platform/navigation/headerActions/email.ts.
        buildEmailHeaderActions({
          emailThreadOpen,
          emailThreadStarred,
          emailFilterMenuOpen,
          emailActiveFiltersSize: emailActiveFilters.size,
          onToggleFilterMenu: () => setEmailFilterMenuOpen((o) => !o),
          leadingActions,
          trailingActions,
          pushNotificationsAction,
          pushBackAction,
        });
        break;
      }
      case 'tasks':
        // Delegated to buildTasksHeaderActions — see
        // src/platform/navigation/headerActions/tasks.ts.
        buildTasksHeaderActions({ leadingActions, trailingActions, pushNotificationsAction, pushBackAction });
        break;
      case 'sales':
      case 'purchases':
        // The ONLY way out of these two hubs. Their sections replace the app
        // nav in the sidebar (the Workspaces/Employers drill-in), so without
        // this chevron the hub is a dead end — the same trap the Email inbox
        // had. Matches how the employer drill-in leaves the way out to the
        // header. Purchases joined Sales in drilling in, so it needs the same
        // exit; the macOS equivalent is macSidebarDrillIn in navigation.ts.
        pushBackAction('dashboard', 'Back to dashboard');
        // Sales hub Invoices section: status filter pill replaces the in-page
        // Select FinanceBilling's InvoiceList used to render (see
        // `headerControlled` prop). subActions double as the fallback
        // rendering if the id-intercept below doesn't apply, same
        // graceful-degrade as the workspace-invoices pill.
        if (isSalesInvoicesTab && !pageControls) {
          const activeOption = INVOICE_STATUS_FILTER_OPTIONS.find(o => o.value === salesInvoiceStatusFilter);
          trailingActions.push({
            id: 'sales-invoices-filter',
            icon: SlidersHorizontal,
            label: activeOption?.label ?? 'Unpaid',
            ariaLabel: 'Filter invoices by status',
            group: 'view',
            subActions: INVOICE_STATUS_FILTER_OPTIONS.map(opt => ({
              id: `saif-${opt.value}`,
              label: opt.label,
              active: salesInvoiceStatusFilter === opt.value,
              onClick: () => {
                setSalesInvoiceStatusFilter(opt.value);
                window.dispatchEvent(new CustomEvent('workspace-invoices:filter', { detail: { status: opt.value } }));
              },
            })),
          });
        }
        break;
      case 'messages':
        if (messagesMembersState) {
          buildMessagesMembersHeaderActions({
            state: messagesMembersState, leadingActions, trailingActions, pushNotificationsAction,
            onToggleSearch: () => {
              setMembersSearchOpen((open) => !open);
              requestAnimationFrame(() => membersSearchInputRef.current?.focus());
            },
          });
          break;
        }
        // An open workspace/compact conversation publishes its state from
        // ChatHeader so Back can live in this global row; compact mobile
        // replaces the page-level compose/notification actions with Back
        // while the thread is open. Desktop keeps its own local ChatHeader
        // back arrow instead (so no second one is added here), but still
        // hides Compose/notifications the same way once desktopChatSelected
        // says a conversation has taken over the pane — see its declaration.
        const desktopComposeBackVisible = isDesktopViewport && desktopComposeOpen;
        // Desktop compose keeps its identity inside the conversation pane.
        const conversationBackVisible = !isDesktopViewport && Boolean(messagesConversationState) && !messagesComposeOpen;
        if (conversationBackVisible) {
          leadingActions.push({
            id: 'back-messages-conversation',
            icon: ChevronLeft,
            ariaLabel: 'Back to messages',
            onClick: () => window.dispatchEvent(new CustomEvent('messages:action:back')),
          });
        }
        if (desktopComposeBackVisible) {
          // buildMessagesHeaderActions is skipped entirely below while
          // composing (conversationOpen covers it too) — that's right for
          // Search/Compose, which don't apply here, but it silently took the
          // bell with them. Notifications stays relevant no matter what
          // you're doing in Messages, so it gets pushed on its own.
          pushNotificationsAction();
        }
        const conversationOpen = messagesConversationState || (isDesktopViewport && (desktopChatSelected || desktopComposeOpen));
        if (isDesktopViewport && conversationOpen && !desktopComposeBackVisible && !messagesComposeOpen) {
          pushNotificationsAction();
        }
        if (!conversationOpen || messagesComposeOpen) {
          // Delegated to buildMessagesHeaderActions — see
          // src/platform/navigation/headerActions/messages.ts.
          buildMessagesHeaderActions({
            messagesHeaderState,
            messagesComposeOpen,
            hideBrowseAction: conversationBackVisible,
            onOpenCompose: () => {
              // Mobile: the To: field lives in the compose screen content
              // under the page title (iOS Messages sheet order — title, then
              // To:, then results), and the header shows the back chevron +
              // "New Message" title.
              if (!isDesktopViewport) {
                setMessagesHeaderMode('compose');
                window.dispatchEvent(new CustomEvent('messages:new'));
                return;
              }
              // Desktop: no header morph — MessagesView opens its own
              // NewMessagePanel (a fixed-width inspector, same slot as
              // ThreadPanel) in response to the same event.
              window.dispatchEvent(new CustomEvent('messages:new'));
            },
            onDismissCompose: dismissMessagesCompose,
            setMessagesHeaderMode,
            leadingActions,
            trailingActions,
            pushNotificationsAction,
          });
        }
        break;
      case 'projects':
        // Delegated to buildProjectsHeaderActions — see
        // src/platform/navigation/headerActions/projects.ts.
        buildProjectsHeaderActions({ projectsHeaderState, leadingActions, trailingActions, pushNotificationsAction });
        break;
      case 'pipeline':
        if (usesMaterialMobileChrome) {
          leadingActions.push({
            id: 'pipeline-sections-toggle',
            icon: SidebarLeftIcon,
            ariaLabel: 'Enquiry stages',
            onClick: () => setSidebarOpenMobile(true),
          });
        }
        // Delegated to buildPipelineHeaderActions — see
        // src/platform/navigation/headerActions/pipeline.ts.
        buildPipelineHeaderActions({ leadingActions, trailingActions, pushNotificationsAction, pipelineHeaderState });
        break;
      case 'workspace-pipeline':
        // Delegated to buildWorkspacePipelineHeaderActions — see
        // src/platform/navigation/headerActions/pipeline.ts.
        buildWorkspacePipelineHeaderActions({ leadingActions, trailingActions, pushNotificationsAction });
        break;
      case 'system-settings':
        if (templatesHeaderState && templatesHeaderState.mode !== 'library') {
          leadingActions.push({
            id: 'templates-editor-back',
            icon: ChevronLeft,
            ariaLabel: 'Back to templates',
            onClick: () => window.dispatchEvent(new CustomEvent(TEMPLATES_BACK_EVENT)),
            group: 'page',
          });

          if (templatesHeaderState.mode === 'message-editor' && templatesHeaderState.channel === 'email') {
            trailingActions.push({
              id: 'templates-preview',
              icon: templatesHeaderState.preview ? Pencil : Eye,
              label: templatesHeaderState.preview ? 'Edit' : 'Preview',
              ariaLabel: templatesHeaderState.preview ? 'Edit template' : 'Preview template',
              onClick: () => window.dispatchEvent(new CustomEvent(TEMPLATES_PREVIEW_EVENT)),
              group: 'view',
            });
          }

          trailingActions.push({
            id: 'templates-reset',
            icon: RefreshCw,
            label: 'Reset',
            ariaLabel: 'Reset template',
            disabled: templatesHeaderState.saving
              || (templatesHeaderState.mode === 'message-editor'
                ? templatesHeaderState.resetting || !templatesHeaderState.canReset
                : false),
            onClick: () => window.dispatchEvent(new CustomEvent(TEMPLATES_RESET_EVENT)),
            group: 'modify',
          });
          trailingActions.push({
            id: 'templates-save',
            icon: templatesHeaderState.saving ? Loader2 : Save,
            label: templatesHeaderState.mode === 'material-editor'
              ? (templatesHeaderState.saving ? 'Publishing…' : 'Publish')
              : (templatesHeaderState.saving ? 'Saving…' : 'Save'),
            ariaLabel: templatesHeaderState.mode === 'material-editor' ? 'Publish template' : 'Save template',
            disabled: templatesHeaderState.saving
              || (templatesHeaderState.mode === 'material-editor'
                ? !templatesHeaderState.canPublish
                : !templatesHeaderState.dirty),
            onClick: () => window.dispatchEvent(new CustomEvent(TEMPLATES_SAVE_EVENT)),
            group: 'output',
          });
        }
        // Workflows settings page surfaces its own search + New + more here,
        // gated on the page emitting workflows:state-change with active=true.
        if (workflowsHeaderState?.active) {
          if (workflowsHeaderState.editorOpen) {
            // Editor mode: Save + Add step pills. Search is a list concern only.
            if (!workflowsHeaderState.isNew && (workflowsHeaderState.dirty || workflowsHeaderState.isSaving)) trailingActions.push({
              id: 'workflows-cancel',
              label: 'Cancel',
              ariaLabel: 'Cancel workflow changes',
              disabled: workflowsHeaderState.isSaving,
              onClick: () => window.dispatchEvent(new CustomEvent('workflows:cancel')),
              group: 'page',
            });
            if (workflowsHeaderState.isNew || workflowsHeaderState.dirty || workflowsHeaderState.isSaving) trailingActions.push({
              id: 'workflows-save',
              icon: workflowsHeaderState.isSaving ? Loader2 : Save,
              label: workflowsHeaderState.isNew ? 'Create' : undefined,
              ariaLabel: workflowsHeaderState.isSaving
                ? 'Saving workflow'
                : workflowsHeaderState.isNew
                ? 'Create workflow'
                : 'Save workflow',
              disabled: workflowsHeaderState.isSaving || !workflowsHeaderState.canSave,
              onClick: () => window.dispatchEvent(new CustomEvent('workflows:save')),
              group: 'page',
            });
            if (!workflowsHeaderState.isLocked) {
              trailingActions.push({
                id: 'workflows-add-step',
                icon: Plus,
                ariaLabel: 'Add step',
                onClick: () => window.dispatchEvent(new CustomEvent('workflows:add-step')),
                group: 'page',
              });
            }
          } else {
            // List mode: search + New + more
            leadingActions.push({
              id: 'workflows-search',
              icon: Search,
              ariaLabel: 'Search workflows',
              onClick: () => window.dispatchEvent(new CustomEvent('workflows:toggle-search')),
            });
            trailingActions.push({
              id: 'workflows-new',
              icon: Plus,
              ariaLabel: 'New workflow',
              onClick: () => window.dispatchEvent(new CustomEvent('workflows:create-new')),
              group: 'page',
            });
            trailingActions.push({
              id: 'workflows-more',
              icon: MoreHorizontal,
              ariaLabel: 'More workflow actions',
              onClick: () => window.dispatchEvent(new CustomEvent('workflows:open-more')),
              group: 'page',
            });
          }
        }
        // Trigger Catalog, the Workflows section's sibling in the settings
        // sidebar, surfaces the same shape of controls from here. Filters are
        // trailing: the leading edge is navigation only.
        if (triggerCatalogHeaderState?.active) {
          leadingActions.push({
            id: 'trigger-catalog-search',
            icon: Search,
            ariaLabel: 'Search triggers',
            onClick: () => window.dispatchEvent(new CustomEvent('trigger-catalog:toggle-search')),
          });
          const statusOptions = TRIGGER_CATALOG_STATUS_OPTIONS;
          const activeStatus = triggerCatalogHeaderState.statusFilter ?? 'all';
          const categoryOptions = triggerCatalogHeaderState.categoryOptions ?? [];
          const activeCategory = triggerCatalogHeaderState.categoryFilter ?? 'all';
          trailingActions.push({
            id: 'trigger-catalog-filter',
            icon: SlidersHorizontal,
            // Labelled only while narrowed, so the resting header stays quiet
            // and an active filter is impossible to miss.
            label: activeStatus === 'all' && activeCategory === 'all'
              ? undefined
              : [
                  statusOptions.find((o) => o.value === activeStatus)?.label,
                  categoryOptions.find((o) => o.value === activeCategory)?.label,
                ].filter((l) => l && l !== 'All statuses').join(' · ') || undefined,
            ariaLabel: 'Filter triggers',
            group: 'page',
            // One menu, two runs: statuses then categories, split by a rule so
            // the two independent axes don't read as one list of 18 choices.
            subActionDividerAfter: `trigger-catalog-status-${statusOptions[statusOptions.length - 1].value}`,
            subActions: [
              ...statusOptions.map((opt) => ({
                id: `trigger-catalog-status-${opt.value}`,
                label: opt.label,
                active: activeStatus === opt.value,
                trailingIcon: activeStatus === opt.value ? Check : undefined,
                onClick: () => window.dispatchEvent(
                  new CustomEvent('trigger-catalog:set-status', { detail: opt.value }),
                ),
              })),
              ...[{ value: 'all', label: 'All categories' }, ...categoryOptions].map((opt) => ({
                id: `trigger-catalog-category-${opt.value}`,
                label: opt.label,
                active: activeCategory === opt.value,
                trailingIcon: activeCategory === opt.value ? Check : undefined,
                onClick: () => window.dispatchEvent(
                  new CustomEvent('trigger-catalog:set-category', { detail: opt.value }),
                ),
              })),
            ],
          });
          trailingActions.push({
            id: 'trigger-catalog-new',
            icon: Plus,
            ariaLabel: 'Add trigger',
            onClick: () => window.dispatchEvent(new CustomEvent('trigger-catalog:create-new')),
            group: 'page',
          });
        }
        // RFI Register section: the same pills as the project tab, plus a
        // back chevron while an RFI is open, which Settings has no other way
        // to offer.
        if (activeRfiForm) {
          leadingActions.push({
            id: 'back-rfi-form',
            icon: ChevronLeft,
            ariaLabel: 'Back to RFIs',
            onClick: () => window.dispatchEvent(new CustomEvent(RFI_FORM_CLOSE_EVENT)),
          });
          buildRfiFormHeaderActions({ state: activeRfiForm, trailingActions });
        } else if (activeRfiRegister) {
          buildRfiRegisterHeaderActions({ state: activeRfiRegister, trailingActions });
        }
        break;
      case 'project-detail':
        // Drill-ins return to their owning list before the project-level Back
        // path is considered. Only a tab root may return to Dashboard, and only
        // Dashboard may leave the project for the Projects directory.
        if (onNavigate) {
          const onOverview = projectDetailTab === 'overview';
          const inApplicationForm = projectDetailApplicationOpen;
          // Form-open detection is tab-agnostic: the instruction/daywork forms
          // are now also opened from the Events hub, so back must close the form
          // wherever it is, not only on the (now-removed) instructions/dayworks tabs.
          const inDayworkForm = !!dayworkFormState;
          const inInstructionForm = !!instructionFormState;
          const inEventsDetail = projectDetailTab === 'events' && (!!eventsHeaderState?.isFormOpen || !!eventFormState);
          // Instruction/daywork forms are now opened from the Events hub; the
          // standalone tabs are gone, so label the chevron for the surface the
          // user actually returns to (events) rather than a de-nav'd tab.
          const inEventsContext = projectDetailTab === 'events';
          // Takeoff drawing open — takeoffCanvasState is broadcast by
          // whichever takeoff viewer is mounted, only while a sheet is open
          // (null in the gallery), so its presence is the drawing/gallery
          // signal. Every viewer owes this broadcast: without it there is no
          // chevron and no way back out of a drawing. Back here must return to
          // the sheet gallery, not bounce to Overview or leave the project —
          // mirrors the documents-folder priority.
          const inTakeoffDrawing =
            projectDetailTab === 'takeoff' && !!takeoffCanvasState;
          // Add Drawings uploader open (full page) — back closes it and
          // returns to the sheet gallery. Highest takeoff priority: it sits
          // above both the gallery and any open drawing.
          const inTakeoffUploader =
            projectDetailTab === 'takeoff' && !!takeoffHeaderState?.uploaderOpen;
          const inTakeoffUploaderFolder = inTakeoffUploader && takeoffUploaderDepth > 0;
          // Review step — back steps BACK through the flow (to the file
          // picker) rather than closing the uploader or stepping up a folder,
          // neither of which applies once processing has run. The uploader owns
          // where that lands (see its takeoff:upload-back-to-source listener),
          // so this and the in-view Back button can't disagree.
          const inTakeoffUploaderStepBack =
            inTakeoffUploader && takeoffUploaderStep === 'details';
          // Sheet details editor open (full page, gallery-only) — same
          // priority as the uploader: closing it must go back to the gallery,
          // not silently discard unsaved edits by navigating away.
          const inTakeoffSheetDetails =
            projectDetailTab === 'takeoff' && !!takeoffHeaderState?.sheetDetailsOpen;
          // H&S tab drilled past the hub grid (RAMS list/record/editor) —
          // back walks that stack down to the hub rather than leaving the
          // tab. Also the only way back out of a RAMS record: RAMSViewer's
          // onClose is never rendered in-view, so this chevron is it.
          const inHSDrill =
            projectDetailTab === 'hs' && !!hsHeaderState && hsHeaderState.view !== 'hub';
          // A drilled-in machine's back chevron is pushed by
          // buildPlantHeaderActions itself (same call the standalone /plant
          // screen makes) — this generic chevron must stand down there or
          // the header renders two. The register list (no machine open) has
          // no such pill, so the generic chevron is still what takes it back
          // to the H&S hub.
          const inHSRegisterDetail =
            inHSDrill && hsHeaderState?.section === 'register' && !!plantSelectedLabel;
          // A note open on mobile: back closes it to the list, not the project.
          const inNotesDetail =
            (projectDetailTab === 'notes' || projectDetailTab === 'tasks') && !!notesHeaderState?.detailOpen;
          // An RFI open in the register: back closes it to the list.
          const inRfiForm = projectDetailTab === 'rfis' && !!activeRfiForm;
          const inMembersDetail =
            projectDetailTab === 'team' && !!projectMembersHeaderState?.detailOpen;
          const inProjectEmailDetail =
            projectDetailTab === 'email' && projectEmailHeaderState.detailOpen;
          const inSitePhotoFullView =
            projectDetailTab === 'site_photos' && sitePhotosHeaderState.fullView;
          const inReport = !!projectReportIdFromTab(projectDetailTab) && !!reportHeaderState;
          const ariaLabel = inSitePhotoFullView
            ? 'Back to Site Photos'
            : inReport
            ? 'Back to reports'
            : inProjectEmailDetail
            ? 'Back to project mail'
            : inMembersDetail
            ? 'Back to members'
            : inNotesDetail
            ? 'Back to notes'
            : inRfiForm
            ? 'Back to RFIs'
            : inTakeoffUploader
            ? (inTakeoffUploaderStepBack
                ? 'Back to file picker'
                : inTakeoffUploaderFolder ? 'Back to parent folder' : 'Back to sheets')
            : inTakeoffSheetDetails
            ? 'Back to sheets'
            : inDayworkForm
            ? (inEventsContext ? 'Back to events' : 'Back to dayworks')
            : inInstructionForm
              ? (inEventsContext ? 'Back to events' : 'Back to instructions')
              : inEventsDetail
                ? 'Back to events'
                : inApplicationForm
                  ? 'Back to valuations'
                  : inTakeoffDrawing
                    ? 'Back to sheets'
                    : inHSDrill
                      ? (hsHeaderState?.section === 'register'
                          // The register doesn't report list/detail through
                          // hsHeaderState.view (always 'list' — ProjectHSTab
                          // marks the drill only, see headerActions/hs.ts),
                          // so read plantSelectedLabel instead. This chevron
                          // never actually renders while inHSRegisterDetail —
                          // buildPlantHeaderActions owns that state — but the
                          // label stays correct for the brief window before
                          // PLANT_STATE_EVENT catches up.
                          ? (plantSelectedLabel ? 'Back to Plant Register' : 'Back to H&S')
                          : hsHeaderState?.view === 'list'
                            ? 'Back to H&S'
                            : hsHeaderState?.section === 'toolbox'
                              ? 'Back to Toolbox Talks'
                              : hsHeaderState?.section === 'plant'
                                ? 'Back to Plant Checks'
                                : hsHeaderState?.section === 'near-miss'
                                  ? 'Back to Near Miss'
                                  : 'Back to RAMS')
                      : onOverview
                        ? 'Back to projects'
                        : 'Back to overview';
          // Folder-back takes priority on the documents tab so a single
          // chevron drives both folder-up navigation AND falling out to
          // the project overview at the root level — same affordance, no
          // duplicate pills.
          const inDocumentsFolder =
            projectDetailTab === 'documents'
            && !!documentsHeaderState
            && documentsHeaderState.canNavigateBack;
          const inDocumentsPreview =
            projectDetailTab === 'documents'
            && !!documentPreviewHeaderState;
          // Back belongs to the header at every depth. The rail's Dashboard
          // row remains a destination, not a substitute for the parent action.
          if (!inHSRegisterDetail) leadingActions.push({
            id: 'back-project-detail',
            icon: ChevronLeft,
            ariaLabel: inDocumentsPreview
              ? 'Back to documents'
              : inDocumentsFolder ? 'Back to parent folder' : ariaLabel,
            onClick: () => {
              if (inSitePhotoFullView) {
                window.location.assign(window.location.pathname);
              } else if (inReport) {
                window.dispatchEvent(new CustomEvent('reports:back-to-cards'));
              } else if (inProjectEmailDetail) {
                window.dispatchEvent(new CustomEvent('project-email:back-to-list'));
              } else if (inMembersDetail) {
                window.dispatchEvent(new CustomEvent('project-members:close-detail'));
              } else if (inNotesDetail) {
                window.dispatchEvent(new CustomEvent('project-notes:back-to-list'));
              } else if (inRfiForm) {
                window.dispatchEvent(new CustomEvent(RFI_FORM_CLOSE_EVENT));
              } else if (inTakeoffUploader) {
                // Page picker / review grid: step back through the flow (the
                // uploader decides which step). Otherwise: deeper than root
                // steps up one folder; at root, close the full-page uploader
                // (→ sheet gallery).
                window.dispatchEvent(new CustomEvent(
                  inTakeoffUploaderStepBack
                    ? 'takeoff:upload-back-to-source'
                    : inTakeoffUploaderFolder ? 'takeoff:upload-up' : 'takeoff:upload-close',
                ));
              } else if (inTakeoffSheetDetails) {
                // Same event the native macOS chevron triggers via the
                // TAKEOFF_CLOSE_SHEET_DETAILS_SENTINEL bridge — TakeoffView's
                // listener closes the editor without discarding it silently.
                window.dispatchEvent(new CustomEvent('takeoff:sheet-details-close'));
              } else if (inDayworkForm) {
                window.dispatchEvent(new CustomEvent('project-daywork:back'));
              } else if (inInstructionForm) {
                window.dispatchEvent(new CustomEvent('project-instruction:back'));
              } else if (inEventsDetail) {
                window.dispatchEvent(new CustomEvent('project-event:back'));
              } else if (inApplicationForm) {
                window.dispatchEvent(new CustomEvent('project-detail:close-application'));
              } else if (inDocumentsPreview) {
                window.dispatchEvent(new CustomEvent('documents:preview-close'));
              } else if (inDocumentsFolder) {
                // Direct call when DocumentToolbar has registered its
                // synchronous handler — avoids the event-listener round
                // trip and shaves a render cycle off the back tap. Falls
                // back to the event for safety.
                const direct = (window as unknown as { __documentsNavigateBack?: () => void })
                  .__documentsNavigateBack;
                if (direct) {
                  direct();
                } else {
                  window.dispatchEvent(new CustomEvent('documents:navigate-back'));
                }
              } else if (inTakeoffDrawing) {
                // Dispatches the same event the native macOS/iOS chevron
                // triggers (via the TAKEOFF_CLOSE_DRAWING_SENTINEL bridge in
                // AuthenticatedController) — TakeoffView's listener closes the
                // drawing. Browser history is not used: the app router owns
                // window.history and clobbers entries pushed elsewhere.
                window.dispatchEvent(new CustomEvent('takeoff:close-drawing'));
              } else if (inHSDrill) {
                window.dispatchEvent(new CustomEvent('hs:back'));
              } else if (onOverview) {
                onNavigate('projects');
              } else {
                window.dispatchEvent(new CustomEvent('project-detail:back-to-overview'));
              }
            },
          });
          // Project sections toggle — also a SIDEBAR TOGGLE, and on mobile
          // the ONLY way into Takeoff, Pricing, Claims and the rest: the
          // bottom bar keeps one fixed tab set on every screen, and the
          // drilled-in sidebar restricts itself to this project's sections
          // (AppSidebar's restrictToProjectSections). Mirrors the native
          // shells, which pair the back chevron with a sidebar toggle on a
          // project. Suppressed inside the full-page takeovers, where the
          // chevron's job is to escape the takeover first.
          const inProjectTakeover =
            inTakeoffUploader
            || inTakeoffSheetDetails
            || inTakeoffDrawing
            || inDocumentsPreview
            || inDayworkForm
            || inInstructionForm
            || inApplicationForm
            || inRfiForm
            || inSitePhotoFullView;
          if (!isDesktopViewport && !inProjectTakeover) {
            leadingActions.push({
              id: 'project-sections-toggle',
              icon: SidebarLeftIcon,
              ariaLabel: 'Project sections',
              onClick: () => setSidebarOpenMobile(true),
            });
          }
        }
        // BOQ pricing-page action set — delegated to
        // src/platform/navigation/headerActions/boq.ts.
        if (isBoqExportRoute && projectDetailTab === 'pricing') {
          const preparing = !!boqExportHeaderState?.isExporting;
          trailingActions.push({
            id: 'boq-export-download',
            label: preparing ? 'Preparing…' : 'Download Excel',
            ariaLabel: preparing ? 'Preparing Excel download' : 'Download Excel',
            group: 'output',
            disabled: !boqExportHeaderState?.canExport || preparing,
            onClick: () => window.dispatchEvent(new CustomEvent(BOQ_EXPORT_HEADER_EVENTS.download)),
          });
        } else if (boqHeaderState) {
          buildBOQHeaderActions({ boqHeaderState, isDesktopViewport, leadingActions, trailingActions });
        }
        // Documents preview — when a file viewer is open the header shows
        // only the save (PDF) or open-in-new-tab (non-PDF) action. The
        // normal documents pills are suppressed to keep the bar uncluttered.
        if (documentPreviewHeaderState) {
          if (documentPreviewHeaderState.canSave) {
            trailingActions.push({
              id: 'documents-preview-save',
              icon: documentPreviewHeaderState.isSaving ? Loader2 : Save,
              ariaLabel: documentPreviewHeaderState.isSaving
                ? 'Saving…'
                : documentPreviewHeaderState.lastSavedAt
                  ? `Saved ${documentPreviewHeaderState.lastSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} (tap to save again)`
                  : 'Save annotations',
              onClick: () => window.dispatchEvent(new CustomEvent('documents:preview-save')),
              group: 'page',
            });
          }
          if (!documentPreviewHeaderState.usesAdobe && documentPreviewHeaderState.proxyUrl) {
            trailingActions.push({
              id: 'documents-preview-open-tab',
              icon: Share2,
              ariaLabel: 'Open in new tab',
              onClick: () => window.dispatchEvent(new CustomEvent('documents:preview-open-new-tab')),
              group: 'page',
            });
          }
        }
        // Documents tab — delegated to
        // src/platform/navigation/headerActions/documents.ts.
        if (documentsHeaderState && !documentPreviewHeaderState) {
          buildDocumentsHeaderActions({
            documentsHeaderState,
            documentsSearchOpen,
            isDesktopViewport,
            setDocumentsSearchOpen,
            dismissDocumentsSearch,
            documentsSearchInputRef,
            leadingActions,
            trailingActions,
          });
          if (usesMaterialMobileChrome) {
            const searchIndex = leadingActions.findIndex(action => action.id === 'documents-search');
            if (searchIndex >= 0) leadingActions.splice(searchIndex, 1);
          }
        }
        // Takeoff tab — delegated to
        // src/platform/navigation/headerActions/takeoff.ts.
        if (takeoffHeaderState) {
          if (takeoffGallerySearchAvailable) {
            leadingActions.push({ id: 'takeoff-gallery-search', icon: Search, ariaLabel: 'Search drawings', onClick: () => setTakeoffSearchOpen(true) });
          }
          buildTakeoffHeaderActions({ takeoffHeaderState, takeoffCanvasState, takeoffPanelState, takeoffUploaderSelected, takeoffUploaderCanBrowse, takeoffUploaderDetails, leadingActions, trailingActions });
        }
        if (
          isDesktopViewport
          && projectDetailTab === 'procurement'
          && procurementHeaderState?.activeView === 'schedule'
        ) {
          const resourceLabels = {
            all: 'All resources',
            material: 'Materials',
            labour: 'Labour',
            plant: 'Plant',
          } as const;
          const groupLabels = {
            package: 'Package',
            trade: 'Trade',
            location: 'Location',
            merchant: 'Supplier',
          } as const;
          trailingActions.push({
            id: 'procurement-resource',
            icon: Layers,
            label: `${resourceLabels[procurementHeaderState.resourceTab]} ${procurementHeaderState.counts[procurementHeaderState.resourceTab]}`,
            ariaLabel: 'Choose procurement resource type',
            group: 'view',
            subActions: (Object.keys(resourceLabels) as Array<keyof typeof resourceLabels>).map((resource) => ({
              id: `procurement-resource-${resource}`,
              label: `${resourceLabels[resource]} (${procurementHeaderState.counts[resource]})`,
              active: procurementHeaderState.resourceTab === resource,
              onClick: () => window.dispatchEvent(new CustomEvent(PROCUREMENT_SET_RESOURCE_EVENT, {
                detail: { resource },
              })),
            })),
          });
          trailingActions.push({
            id: 'procurement-group',
            icon: LayoutGrid,
            label: groupLabels[procurementHeaderState.groupBy],
            ariaLabel: 'Group procurement schedule',
            group: 'view',
            subActions: (Object.keys(groupLabels) as Array<keyof typeof groupLabels>).map((groupBy) => ({
              id: `procurement-group-${groupBy}`,
              label: `Group by ${groupLabels[groupBy].toLowerCase()}`,
              active: procurementHeaderState.groupBy === groupBy,
              onClick: () => window.dispatchEvent(new CustomEvent(PROCUREMENT_SET_GROUP_EVENT, {
                detail: { groupBy },
              })),
            })),
          });
        }
        // Project Email keeps the message list quiet and lets the global
        // header own the actions that apply to the whole project mailbox.
        if (projectDetailTab === 'email') {
          if (isDesktopViewport) {
            leadingActions.push({
              id: 'project-email-search',
              icon: Search,
              ariaLabel: 'Search project mail',
              onClick: () => {
                if (projectEmailSearchOpen) {
                  dismissProjectEmailSearch();
                  return;
                }
                setProjectEmailSearchOpen(true);
                requestAnimationFrame(() => projectEmailSearchInputRef.current?.focus());
              },
            });
          }
          if (isDesktopViewport || !projectEmailHeaderState.detailOpen) {
            trailingActions.push({
              id: 'project-email-refresh',
              icon: RefreshCw,
              ariaLabel: projectEmailHeaderState.loading ? 'Refreshing project email' : 'Refresh project email',
              disabled: projectEmailHeaderState.loading,
              onClick: () => window.dispatchEvent(new CustomEvent('project-email:refresh')),
              group: 'page',
            });
          }
          if (projectEmailHeaderState.hasThreads && (isDesktopViewport || projectEmailHeaderState.detailOpen)) {
            trailingActions.push({
              id: 'project-email-reply',
              icon: Reply,
              ariaLabel: 'Reply to latest project email',
              onClick: () => window.dispatchEvent(new CustomEvent('project-email:reply')),
              group: 'page',
            });
            trailingActions.push({
              id: 'project-email-reply-all',
              icon: ReplyAll,
              ariaLabel: 'Reply all to latest project email',
              onClick: () => window.dispatchEvent(new CustomEvent('project-email:reply-all')),
              group: 'page',
            });
            trailingActions.push({
              id: 'project-email-forward',
              icon: Forward,
              ariaLabel: 'Forward latest project email',
              onClick: () => window.dispatchEvent(new CustomEvent('project-email:forward')),
              group: 'page',
            });
          }
          if (isDesktopViewport || !projectEmailHeaderState.detailOpen) {
            trailingActions.push({
              id: 'project-email-compose',
              icon: SquarePen,
              ariaLabel: 'New project communication',
              onClick: () => window.dispatchEvent(new CustomEvent('project-email:compose')),
              group: 'create',
            });
            if (isDesktopViewport && projectEmailHeaderState.hasThreads && !projectEmailHeaderState.isClient) {
              trailingActions.push({
                id: 'project-email-prepare-tender',
                icon: Sparkles,
                label: 'Prepare tender',
                ariaLabel: projectEmailHeaderState.loading ? 'Preparing tender package' : 'Prepare tender from latest project email',
                disabled: projectEmailHeaderState.loading,
                onClick: () => window.dispatchEvent(new CustomEvent('project-email:prepare-tender')),
                group: 'create',
              });
            }
          }
        }
        // Planner tab — delegated to
        // src/platform/navigation/headerActions/planner.ts. Only mounts when
        // the project detail tab is Planner AND the planner page has dispatched
        // at least one state-change broadcast (so we know the owner list /
        // zoom preset / canEdit / etc.).
        if (projectDetailTab === 'planner' && plannerHeaderState) {
          const editorOwnsHeader = plannerEditorHeaderState?.visible
            && plannerEditorHeaderState.presentation === 'native-header';
          if (editorOwnsHeader) {
            leadingActions.length = 0;
            trailingActions.length = 0;
            leadingActions.push({
              id: 'planner-editor-close',
              icon: X,
              ariaLabel: 'Close activity editor',
              onClick: () => window.dispatchEvent(new CustomEvent('planner:close-editor')),
            });
            if (plannerEditorHeaderState.canEdit) {
              trailingActions.push({
                id: 'planner-editor-save',
                icon: Check,
                label: plannerEditorHeaderState.saving ? 'Saving…' : 'Save',
                ariaLabel: plannerEditorHeaderState.saving ? 'Saving activity' : 'Save activity',
                disabled: plannerEditorHeaderState.saving || !plannerEditorHeaderState.canSubmit,
                onClick: () => window.dispatchEvent(new CustomEvent('planner:save-activity')),
                group: 'page',
              });
            }
          } else {
            buildPlannerHeaderActions({
              plannerHeaderState,
              isDesktopViewport,
              leadingActions,
              trailingActions,
            });
          }
        }
        if (projectDetailTab === 'site_photos' && !sitePhotosHeaderState.fullView) {
          const sitePhotosBusy = sitePhotosHeaderState.isPreparing || sitePhotosHeaderState.isUploading;
          trailingActions.push({
            id: 'site-photos-gallery',
            icon: LayoutGrid,
            desktopLabel: 'Gallery',
            ariaLabel: 'Gallery view',
            active: sitePhotosHeaderState.view === 'gallery',
            onClick: () => window.dispatchEvent(new CustomEvent('site-photos:set-view', { detail: { view: 'gallery' } })),
            group: 'view',
          });
          trailingActions.push({
            id: 'site-photos-map',
            icon: MapIcon,
            desktopLabel: 'Map',
            ariaLabel: 'Map view',
            active: sitePhotosHeaderState.view === 'map',
            onClick: () => window.dispatchEvent(new CustomEvent('site-photos:set-view', { detail: { view: 'map' } })),
            group: 'view',
          });
          if (!isDesktopViewport) {
            trailingActions.push({
              id: 'site-photos-take-photo',
              icon: sitePhotosBusy ? Loader2 : Camera,
              label: 'Take Photo',
              ariaLabel: sitePhotosBusy ? 'Preparing site photo' : 'Take Photo',
              disabled: sitePhotosBusy,
              group: 'create',
              onClick: () => window.dispatchEvent(new CustomEvent('site-photos:take-photo')),
            });
          }
          trailingActions.push({
            id: 'site-photos-choose-photo',
            icon: sitePhotosBusy ? Loader2 : isDesktopViewport ? Upload : Image,
            label: isDesktopViewport ? 'Upload Photo' : 'Choose from Library',
            ariaLabel: sitePhotosBusy
              ? 'Preparing site photo'
              : isDesktopViewport
                ? 'Upload Photo'
                : 'Choose from Library',
            disabled: sitePhotosBusy,
            group: 'create',
            onClick: () => window.dispatchEvent(new CustomEvent('site-photos:choose-photo')),
          });
        }
        if (projectDetailTab === 'overview' && projectOverviewHeaderState) {
          buildProjectOverviewHeaderActions({ state: projectOverviewHeaderState, trailingActions });
        }
        if (projectDetailTab === 'site-register' && siteRegisterHeaderState) {
          buildSiteRegisterHeaderActions({ state: siteRegisterHeaderState, isDesktopViewport, trailingActions });
        }
        if (activeRfiForm) {
          buildRfiFormHeaderActions({ state: activeRfiForm, trailingActions });
        } else if (activeRfiRegister) {
          buildRfiRegisterHeaderActions({ state: activeRfiRegister, trailingActions });
        }
        // Tab-specific trailing actions: on the Interim tab, the
        // header chrome depends on whether we're on the LIST view (no
        // application open) or the EDITOR (one is open). The list
        // gets a + pill to lodge a new application; the editor swaps
        // that for Save / Share / Submit pills bridged from the
        // InterimApplication form's `interim:state-change` event.
        if (projectDetailTab === 'interim') {
          if (projectDetailApplicationOpen && interimHeaderState) {
            if (
              interimHeaderState.activeSection === 'breakdown'
              && interimBreakdownGroupsState
              && interimBreakdownGroupsState.groupCount > 0
            ) {
              const bookingView = interimBreakdownGroupsState.viewMode ?? 'value';
              const bookingOptions: { value: InterimBookingView; label: string }[] = [
                { value: 'value', label: 'Value' }, { value: 'percentage', label: 'Percentage' }, { value: 'quantity', label: 'Quantity' },
              ];
              trailingActions.push({
                id: 'interim-booking-view', icon: ChevronDown,
                label: bookingOptions.find(option => option.value === bookingView)?.label,
                ariaLabel: `Booking view: ${bookingView}`, group: 'view',
                subActions: bookingOptions.map(option => ({
                  id: `interim-booking-${option.value}`, label: option.label,
                  active: bookingView === option.value,
                  trailingIcon: bookingView === option.value ? Check : undefined,
                  onClick: () => window.dispatchEvent(new CustomEvent(INTERIM_BREAKDOWN_SET_VIEW_EVENT, { detail: option.value })),
                })),
              });
              const allCollapsed = interimBreakdownGroupsState.allCollapsed;
              trailingActions.push({
                id: 'interim-toggle-breakdown-groups',
                icon: allCollapsed ? ChevronsUpDown : ChevronsDownUp,
                label: allCollapsed ? 'Expand all' : 'Collapse all',
                ariaLabel: allCollapsed ? 'Expand all breakdown groups' : 'Collapse all breakdown groups',
                onClick: () => window.dispatchEvent(
                  new CustomEvent(INTERIM_BREAKDOWN_TOGGLE_ALL_GROUPS_EVENT),
                ),
                group: 'view',
              });
            }
            if (interimHeaderState.canEditClaim && interimHeaderState.activeSection === 'variations') {
              trailingActions.push({
                id: 'interim-add-variation',
                icon: Plus,
                label: 'Add Variation',
                ariaLabel: 'Add variation',
                onClick: () => window.dispatchEvent(new CustomEvent('interim:add-variation')),
                group: 'create',
              });
            }
            if (interimHeaderState.canEditClaim && interimHeaderState.activeSection === 'materials') {
              trailingActions.push({
                id: 'interim-add-material',
                icon: Plus,
                label: 'Add Material',
                ariaLabel: 'Add material',
                onClick: () => window.dispatchEvent(new CustomEvent('interim:add-material')),
                group: 'create',
              });
            }
            if (interimHeaderState.canEditClaim && interimHeaderState.activeSection === 'invoice') {
              const notesOpen = interimHeaderState.invoiceMode === 'notes';
              trailingActions.push({
                id: 'interim-toggle-notes',
                icon: notesOpen ? Eye : Pencil,
                label: notesOpen ? 'Preview' : 'Edit Notes',
                ariaLabel: notesOpen ? 'Back to application preview' : 'Edit application notes',
                onClick: () => window.dispatchEvent(new CustomEvent('interim:set-invoice-mode', {
                  detail: { mode: notesOpen ? 'preview' : 'notes' },
                })),
                group: 'page',
              });
            }
            // Save — only for super_admin/internal; workspace_owner/admin
            // are read-only viewers and must not mutate the application. A
            // claim that has gone out records its payment outcome as it is
            // entered and has nothing else to save.
            if (interimHeaderState.canSave) {
              trailingActions.push({
                id: 'interim-save',
                icon: interimHeaderState.isSaving ? Loader2 : Save,
                label: interimHeaderState.saveLabel,
                ariaLabel: interimHeaderState.isSaving ? `Saving ${interimHeaderState.workflow === 'assess' ? 'assessment' : 'application'}` : interimHeaderState.saveLabel,
                disabled: interimHeaderState.isSaving,
                onClick: () => window.dispatchEvent(new CustomEvent('interim:save')),
                group: 'page',
              });
            }
            // Share — only available once Setup is complete (otherwise
            // there's nothing meaningful to share). Any user with view
            // access may share (they're sharing a read link, not editing).
            if (interimHeaderState.canShare) {
              trailingActions.push({
                id: 'interim-share',
                icon: Share2,
                ariaLabel: 'Share application',
                onClick: () => window.dispatchEvent(new CustomEvent('interim:share')),
                group: 'page',
              });
            }
            // Delete — only a draft that's never been submitted, and only
            // for the same roles that can edit one (see canDelete in
            // InterimApplication.tsx; the server enforces the same rule).
            if (interimHeaderState.canDelete) {
              trailingActions.push({
                id: 'interim-delete',
                icon: Trash2,
                ariaLabel: 'Delete draft application',
                disabled: interimHeaderState.isSaving,
                onClick: () => window.dispatchEvent(new CustomEvent('interim:delete')),
                group: 'page',
              });
            }
            // Submit — gated by canSubmit which already incorporates the
            // role check (canManageApplications && status conditions).
            if (interimHeaderState.canSubmit) {
              trailingActions.push({
                id: 'interim-submit',
                icon: Send,
                label: interimHeaderState.completeLabel,
                ariaLabel: interimHeaderState.completeLabel,
                disabled: interimHeaderState.isSaving,
                onClick: () => window.dispatchEvent(new CustomEvent('interim:submit')),
                group: 'page',
              });
            }
            // Export — available to any user who can view the application
            // once it has a persisted id (canExport). Offers PDF and Excel
            // via sub-actions, mirroring the BOQ export pill pattern.
            if (interimHeaderState.canExport) {
              trailingActions.push({
                id: 'interim-export',
                icon: Download,
                ariaLabel: 'Export',
                group: 'output' as const,
                subActions: [
                  {
                    id: 'interim-export-pdf',
                    label: 'PDF (.pdf)',
                    onClick: () =>
                      window.dispatchEvent(
                        new CustomEvent('interim:export', { detail: { format: 'pdf' } }),
                      ),
                  },
                  {
                    id: 'interim-export-excel',
                    label: 'Excel (.xlsx)',
                    onClick: () =>
                      window.dispatchEvent(
                        new CustomEvent('interim:export', { detail: { format: 'excel' } }),
                      ),
                  },
                ],
              });
            }
          } else if (interimListState?.canCreate) {
            trailingActions.push(buildValuationsCreateAction(interimListState.isCreating, interimListState.createLabel));
          }
        }
        // Instruction form Save — fires wherever the instruction form is open
        // (the Events hub reuses it), not only on the instructions tab. The
        // "+ New" list action stays tab-scoped.
        if (instructionFormState) {
          trailingActions.push({
            id: 'instruction-save',
            icon: instructionFormState.isSaving ? Loader2 : Save,
            ariaLabel: instructionFormState.isSaving ? 'Saving instruction' : 'Save instruction',
            disabled: instructionFormState.isSaving || !instructionFormState.canSave,
            onClick: () => window.dispatchEvent(new CustomEvent('project-instruction:save')),
            group: 'page',
          });
        } else if (projectDetailTab === 'instructions' && instructionsHeaderState && !instructionsHeaderState.isFormOpen) {
          trailingActions.push({
            id: 'instruction-create',
            icon: Plus,
            ariaLabel: 'New instruction',
            onClick: () => window.dispatchEvent(new CustomEvent('project-instruction:create-new')),
          });
        }
        // Daywork form Save/Submit/Download — fires wherever the daywork form is
        // open (Events hub included). The "+ New" list action stays tab-scoped.
        {
          if (dayworkFormState) {
            // Back chevron is owned by the project-detail back-stack above
            // (it routes to dayworks list when dayworkFormState is present).
            if (dayworkFormState.canDownload) {
              trailingActions.push({
                id: 'daywork-download',
                icon: Download,
                ariaLabel: 'Download PDF',
                onClick: () => window.dispatchEvent(new CustomEvent('project-daywork:download')),
                group: 'output',
              });
            }
            trailingActions.push({
              id: 'daywork-save',
              icon: dayworkFormState.isSaving ? Loader2 : Save,
              ariaLabel: dayworkFormState.isSaving ? 'Saving daywork' : 'Save draft',
              disabled: dayworkFormState.isSaving || !dayworkFormState.canSave,
              onClick: () => window.dispatchEvent(new CustomEvent('project-daywork:save')),
              group: 'page',
            });
            if (dayworkFormState.canSubmit) {
              trailingActions.push({
                id: 'daywork-submit',
                icon: dayworkFormState.isSubmitting ? Loader2 : Send,
                label: dayworkFormState.isSubmitting ? 'Submitting' : 'Submit',
                ariaLabel: 'Submit daywork to application',
                disabled: dayworkFormState.isSubmitting || dayworkFormState.isSaving,
                onClick: () => window.dispatchEvent(new CustomEvent('project-daywork:submit')),
                group: 'page',
              });
            }
          } else if (projectDetailTab === 'dayworks' && dayworksHeaderState && !dayworksHeaderState.isFormOpen) {
            trailingActions.push({
              id: 'daywork-create',
              icon: Plus,
              ariaLabel: 'New daywork',
              onClick: () => window.dispatchEvent(new CustomEvent('project-daywork:create-new')),
            });
          }
        }
        if (projectDetailTab === 'events' && eventFormState) trailingActions.push(...buildEventEditorHeaderActions(eventFormState));
        // Events tab — list view shows + new; form/detail view suppresses it.
        if (projectDetailTab === 'events' && eventsHeaderState && !eventsHeaderState.isFormOpen && eventsHeaderState.canCreate !== false) {
          buildEventsHeaderActions({ leadingActions, trailingActions });
        }
        if (projectDetailTab === 'activity' && activityHeaderState) {
          // Desktop only, as for Notes: the phone keeps the page's own field.
          if (isDesktopViewport) {
            leadingActions.push({
              id: 'project-activity-search',
              icon: Search,
              ariaLabel: 'Search activity',
              onClick: () => {
                setProjectActivitySearchOpen((prev) => {
                  const next = !prev;
                  if (next) {
                    requestAnimationFrame(() => projectActivitySearchInputRef.current?.focus());
                  } else {
                    setProjectActivitySearchValue('');
                    window.dispatchEvent(new CustomEvent('project-activity:set-query', { detail: { query: '' } }));
                  }
                  return next;
                });
              },
            });
          }
          const activityFilters = [
            ['all', 'All activity'],
            ['project_created', 'Project created'],
            ['status_update', 'Status updates'],
            ['contract_value_change', 'Contract value changes'],
            ['task_created', 'Tasks'],
            ['contract_item', 'Contract items'],
            ['interim_application', 'Interim applications'],
            ['document_upload', 'Documents'],
            ['email', 'Emails'],
            ['meeting', 'Meetings'],
            ['phone_call', 'Phone calls'],
            ['note', 'Notes'],
            ['comment', 'Comments'],
            ['agent_action', 'Agent actions'],
          ] as const;
          trailingActions.push({
            id: 'project-activity-filter',
            icon: SlidersHorizontal,
            label: activityHeaderState.typeFilter === 'all'
              ? undefined
              : activityFilters.find(([id]) => id === activityHeaderState.typeFilter)?.[1],
            ariaLabel: `Filter activity, ${activityHeaderState.count} events`,
            active: activityHeaderState.typeFilter !== 'all',
            badge: activityHeaderState.typeFilter !== 'all' ? activityHeaderState.count : 0,
            group: 'view',
            subActions: activityFilters.map(([id, label]) => ({
              id: `project-activity-${id}`,
              label,
              active: activityHeaderState.typeFilter === id,
              onClick: () => window.dispatchEvent(new CustomEvent('project-activity:set-filter', {
                detail: { filter: id },
              })),
            })),
          });
        }
        // Notes tab — same rule as Tasks: the page's primary act (and its
        // housekeeping) live in the header on mobile, where the in-page
        // toolbar had no room for them. Desktop keeps its own toolbar row.
        if ((projectDetailTab === 'notes' || projectDetailTab === 'tasks') && notesHeaderState) {
          // Desktop only: the phone keeps the page's own inset field, so a
          // header pill there would be the second search on one screen.
          if (!notesHeaderState?.detailOpen && isDesktopViewport) {
            leadingActions.push({
              id: 'project-notes-search',
              icon: Search,
              ariaLabel: 'Search notes',
              onClick: () => {
                setProjectNotesSearchOpen((prev) => {
                  const next = !prev;
                  if (next) {
                    requestAnimationFrame(() => projectNotesSearchInputRef.current?.focus());
                  } else {
                    setProjectNotesSearchValue('');
                    window.dispatchEvent(new CustomEvent('project-notes:set-query', { detail: { query: '' } }));
                  }
                  return next;
                });
              },
            });
          }
          if (notesHeaderState?.detailOpen && (notesHeaderState.mode === 'create' || notesHeaderState.mode === 'edit')) {
            // Writing one: Cancel discards and pairs with the primary action.
            // Cancel is a TEXT pill with no icon (house rule: never a symbol
            // and a word sharing one glass background).
            trailingActions.push({
              id: 'project-notes-cancel',
              label: 'Cancel',
              ariaLabel: 'Discard note',
              onClick: () => window.dispatchEvent(new CustomEvent('project-notes:cancel')),
              group: 'page',
            });
            trailingActions.push({
              id: 'project-notes-save',
              icon: Check,
              ariaLabel: notesHeaderState.mode === 'edit' ? 'Save note' : 'Create note',
              onClick: () => window.dispatchEvent(new CustomEvent('project-notes:save')),
              group: 'page',
            });
          } else if (notesHeaderState?.detailOpen && notesHeaderState.hasNote && notesHeaderState.mode === 'view') {
            // Reading a note: its own actions own the trailing edge.
            trailingActions.push({
              id: 'project-notes-pin',
              icon: Pin,
              ariaLabel: notesHeaderState.isPinned ? 'Unpin note' : 'Pin note to top',
              active: notesHeaderState.isPinned,
              onClick: () => window.dispatchEvent(new CustomEvent('project-notes:pin')),
              group: 'view',
            });
            trailingActions.push({
              id: 'project-notes-edit',
              icon: Pencil,
              ariaLabel: 'Edit note',
              onClick: () => window.dispatchEvent(new CustomEvent('project-notes:edit')),
              group: 'page',
            });
            trailingActions.push({
              id: 'project-notes-delete',
              icon: Trash2,
              ariaLabel: 'Delete note',
              onClick: () => window.dispatchEvent(new CustomEvent('project-notes:delete')),
              group: 'page',
            });
          } else if (!notesHeaderState?.detailOpen) {
            // Only when there IS something unread. A permanent glyph for a
            // once-in-a-while housekeeping act reads as a mystery button on a
            // screen where nothing is unread — "what's the tick for?" was the
            // reasonable response to it. The badge says how many.
            if ((notesHeaderState?.unreadCount ?? 0) > 0) {
              trailingActions.push({
                id: 'project-notes-mark-read',
                icon: CheckCheck,
                ariaLabel: `Mark ${notesHeaderState!.unreadCount} unread notes as read`,
                badge: notesHeaderState!.unreadCount,
                onClick: () => window.dispatchEvent(new CustomEvent('project-notes:mark-all-read')),
                group: 'view',
              });
            }
            trailingActions.push({
              id: 'project-notes-add',
              icon: Plus,
              ariaLabel: 'New note',
              onClick: () => window.dispatchEvent(new CustomEvent('project-notes:create-new')),
              group: 'page',
            });
          }
        }
        const notebookTasksAllowed = workspaceNavigation.authoritative
          ? workspaceNavigation.navigation?.project.some(item => item.id === 'tasks') === true
          : projectPermissions?.canViewTasks === true;
        if ((projectDetailTab === 'notes' || projectDetailTab === 'tasks') && notebookTasksAllowed) {
          trailingActions.push({
            id: 'project-notebook-tasks',
            icon: CheckSquare,
            ariaLabel: 'Tasks',
            active: projectDetailTab === 'tasks',
            onClick: () => {
              if (projectDetailTab === 'tasks') window.dispatchEvent(new Event('project-notebook:toggle-tasks'));
              else onProjectTabChange?.('tasks');
            },
            group: 'view',
          });
        }
        // H&S tab — hub grid drills into RAMS; + New RAMS pill only on the
        // RAMS list view. Delegated to headerActions/hs.ts. The register is
        // the odd section out: it reuses buildPlantHeaderActions verbatim
        // (same ctx the standalone /plant case builds below) so its pills
        // can never drift from the standalone screen's — see the
        // short-circuit note in headerActions/hs.ts. Its own back chevron
        // (when a machine is drilled in) is pushed inside that call; the
        // generic inHSDrill chevron above is suppressed for that state so
        // there is never more than one (see inHSRegisterDetail above).
        if (projectDetailTab === 'hs' && hsHeaderState) {
          if (hsHeaderState.section === 'register') {
            buildPlantHeaderActions({
              listVisible: isPlantList,
              selectedLabel: plantSelectedLabel,
              statusFilter: plantStatusFilter,
              examDueCount: plantExamDueCount,
              detailState: plantDetailState,
              onSelectStatus: (value) => {
                setPlantStatusFilter(value);
                window.dispatchEvent(new CustomEvent(PLANT_FILTER_EVENT, { detail: { status: value } }));
              },
              onToggleSearch: togglePlantSearch,
              leadingActions,
              trailingActions,
              // No-op override, not the shared pushNotificationsAction: every
              // project-detail tab already gets the bell from this case's own
              // trailing call below (buildHSHeaderActions never pushes one
              // itself either) — passing the real function here would push it
              // twice, since buildPlantHeaderActions also calls it standalone
              // for the un-nested /plant screen.
              pushNotificationsAction: () => {},
              pushBackAction,
            });
          } else {
            buildHSHeaderActions({
              hsHeaderState,
              hsEditorState: hsEditorHeaderState,
              hsDocumentState: hsDocumentHeaderState,
              leadingActions,
              trailingActions,
            });
          }
        }
        // Admin tab — Save / Cancel bridged from ProjectAdminTab's
        // `project-admin:state-change`. The page is always live, so both appear
        // only once something has changed and stand down after a save.
        if (isProjectAdminTab(projectDetailTab) && projectAdminHeaderState) {
          const adminDirty = projectAdminHeaderState.dirty;
          const adminBusy = projectAdminHeaderState.isSaving;
          if (adminDirty || adminBusy) {
            trailingActions.push({
              // Icon dropped: X + "Cancel" in one pill is the exact
              // symbol-plus-word combination Apple illustrates as incorrect.
              id: 'project-admin-cancel',
              label: 'Cancel',
              ariaLabel: 'Cancel changes',
              disabled: adminBusy,
              onClick: () => window.dispatchEvent(new CustomEvent('project-admin:cancel')),
              group: 'page',
            });
            trailingActions.push({
              id: 'project-admin-save',
              // A word, like Cancel beside it: the pair reads as one decision.
              label: adminBusy ? 'Saving' : 'Save',
              ariaLabel: adminBusy ? 'Saving' : 'Save changes',
              disabled: adminBusy,
              onClick: () => window.dispatchEvent(new CustomEvent('project-admin:save')),
              group: 'page',
            });
          }
        }
        // Members tab — search field + Add Member, bridged from
        // TeamAssignmentPanel's `project-members:state-change`. The search id is
        // in DESKTOP_GLOBAL_SEARCH_IDS so it renders as the persistent field.
        if (projectDetailTab === 'team' && projectMembersHeaderState) {
          // Adding people is a subview of the roster: the way back leads, never an X trailing. It goes
          // first so it outranks the project's "Back to overview" for the desktop back arrow too.
          if (projectMembersHeaderState.addOpen) {
            leadingActions.unshift({
              id: 'back-project-team',
              icon: ChevronLeft,
              ariaLabel: 'Back to team',
              onClick: () => window.dispatchEvent(new CustomEvent('project-members:toggle-add')),
            });
          }
          // The one header search serves both views: members on the roster, people to add in the subview.
          if (!projectMembersHeaderState.detailOpen) {
            leadingActions.push({
              id: 'project-members-search',
              icon: Search,
              ariaLabel: projectMembersHeaderState.addOpen ? 'Search people to add' : 'Filter members',
              onClick: () => {
                setProjectMembersSearchOpen((prev) => {
                  const next = !prev;
                  if (next) {
                    requestAnimationFrame(() => projectMembersSearchInputRef.current?.focus());
                  } else {
                    setProjectMembersSearchValue('');
                    window.dispatchEvent(new CustomEvent('project-members:set-filter', { detail: { query: '' } }));
                  }
                  return next;
                });
              },
            });
          }
          if (!projectMembersHeaderState.detailOpen && !projectMembersHeaderState.addOpen && projectMembersHeaderState.canManage) {
            trailingActions.push({
              id: 'project-members-add',
              icon: Plus,
              ariaLabel: 'Add member',
              onClick: () => window.dispatchEvent(new CustomEvent('project-members:toggle-add')),
              group: 'page',
            });
          }
        }
        if (projectPermissions?.readOnly === true) {
          trailingActions.splice(0, trailingActions.length, ...withoutProjectWriteActions(trailingActions));
        }
        pushNotificationsAction();
        break;
      case 'add-workspace':
        if (nativeBridge.isNative) pushBackAction('workspaces', 'Back to companies');
        trailingActions.push({
          id: 'workspace-create',
          emphasis: 'primary', label: workspaceCreating ? 'Creating…' : 'Create Workspace',
          ariaLabel: 'Create Workspace', disabled: workspaceCreating, group: 'create',
          onClick: () => window.dispatchEvent(new CustomEvent('workspaces:create-submit')),
        });
        pushNotificationsAction();
        break;
      case 'workspaces':
        // The list-pane toggle used to be the only way back to the directory
        // from an open workspace, since the chevron went to the dashboard.
        // With the toggle gone the chevron takes that job. The edit-user form
        // is one level deeper again, so while it's open the chevron returns to
        // the user list rather than closing the whole workspace.
        if (isWorkspaceInviteForm) {
          pushBackAction('workspaces', 'Back to users', () =>
            window.dispatchEvent(new CustomEvent(WORKSPACE_USERS_INVITE_CANCEL_EVENT)));
        } else if (isWorkspaceUserForm) {
          pushBackAction('workspaces', 'Back to users', () =>
            window.dispatchEvent(new CustomEvent(WORKSPACE_USER_FORM_CANCEL_EVENT)));
        } else if (!isDesktopViewport && documentsHeaderState?.scope === 'workspace' && documentsHeaderState.canNavigateBack) {
          // Inside a workspace documents folder the documents builder supplies the chevron (up one folder).
        } else if (workspaceDetailHeaderState) {
          // Client workspaces are opened from the Companies list in Contacts, so Back returns there.
          pushBackAction('contacts', 'Back to companies', () => {
            markReturnToCompanies();
            window.dispatchEvent(new CustomEvent('workspaces:close-detail'));
            onNavigate?.('contacts');
          });
        } else if (nativeBridge.isNative) {
          pushBackAction('dashboard', 'Back to dashboard');
        }
        if (usesMaterialMobileChrome && workspaceDetailHeaderState) {
          leadingActions.push({ id: 'workspace-sections-toggle', icon: SidebarLeftIcon, ariaLabel: 'Workspace sections', onClick: () => setSidebarOpenMobile(true) });
        }
        if (isWorkspaceResourceLibrary) {
          buildResourceLibraryHeaderActions({
            resourceLibraryHeaderState,
            rateItemFormState,
            isDesktopViewport,
            leadingActions,
            trailingActions,
            pushNotificationsAction,
          });
          if (usesMaterialMobileChrome) {
            const searchIndex = leadingActions.findIndex(action => action.id === 'resource-library-search');
            if (searchIndex >= 0) leadingActions.splice(searchIndex, 1);
          }
          break;
        }
        if ((isDesktopViewport || nativeBridge.isNative || workspaceDetailHeaderState) && documentsHeaderState?.scope !== 'workspace') {
        leadingActions.push({
          id: 'workspaces-search',
          icon: Search,
          ariaLabel: 'Search workspaces',
          onClick: () => window.dispatchEvent(new CustomEvent('workspaces:toggle-search')),
        });
        }
        // Directory-scoped, so it only belongs on the directory — same reason
        // "+ Add Workspace" is hidden below once a workspace is open. On a
        // detail screen it filters a list you can't see, and on the Users tab
        // it sat next to the role filter as a second, unrelated sliders pill.
        // Trailing because it's view configuration, not navigation.
        if (!workspaceDetailHeaderState) {
          trailingActions.push({
            id: 'workspaces-filter',
            icon: SlidersHorizontal,
            ariaLabel: 'Filter workspaces',
            badge: workspacesListHeaderState?.activeFilterCount,
            subActionDividerAfter: 'wc-all',
            subActions: [
              ...([['production', 'Production'], ['non-production', 'Demo & Test'], ['all', 'All workspace types']] as const).map(([value, label]) => ({
                id: `wc-${value}`, label,
                active: (workspacesListHeaderState?.classificationFilter ?? 'production') === value,
                onClick: () => window.dispatchEvent(new CustomEvent('workspaces:set-classification', { detail: { value } })),
              })),
              { id: 'wf-all',           label: 'All statuses',  active: workspacesListHeaderState?.filterStatus === 'all',                onClick: () => window.dispatchEvent(new CustomEvent('workspaces:set-filter', { detail: { value: 'all' } })) },
              { id: 'wf-active',        label: 'Active',          active: workspacesListHeaderState?.filterStatus === 'active',             onClick: () => window.dispatchEvent(new CustomEvent('workspaces:set-filter', { detail: { value: 'active' } })) },
              { id: 'wf-invited',       label: 'Invited',         active: workspacesListHeaderState?.filterStatus === 'invited',            onClick: () => window.dispatchEvent(new CustomEvent('workspaces:set-filter', { detail: { value: 'invited' } })) },
              { id: 'wf-pending',       label: 'Pending',         active: workspacesListHeaderState?.filterStatus === 'pending_invitation', onClick: () => window.dispatchEvent(new CustomEvent('workspaces:set-filter', { detail: { value: 'pending_invitation' } })) },
              { id: 'wf-inactive',      label: 'Inactive',        active: workspacesListHeaderState?.filterStatus === 'inactive',           onClick: () => window.dispatchEvent(new CustomEvent('workspaces:set-filter', { detail: { value: 'inactive' } })) },
              { id: 'wf-suspended',     label: 'Suspended',       active: workspacesListHeaderState?.filterStatus === 'suspended',          onClick: () => window.dispatchEvent(new CustomEvent('workspaces:set-filter', { detail: { value: 'suspended' } })) },
              { id: 'wf-overdue',       label: 'Payment Overdue', active: workspacesListHeaderState?.filterStatus === 'payment_overdue',    onClick: () => window.dispatchEvent(new CustomEvent('workspaces:set-filter', { detail: { value: 'payment_overdue' } })) },
            ],
            group: 'view',
          });
        }
        // Invoices sub-tab: status filter pill replaces the in-page Select
        // InvoiceList used to render (see `headerControlled` prop). subActions
        // double as the fallback rendering if the id-intercept below doesn't
        // apply (same graceful-degrade pattern as `boq-view`).
        if (isWorkspaceInvoicesTab) {
          const activeOption = INVOICE_STATUS_FILTER_OPTIONS.find(o => o.value === invoiceStatusFilter);
          trailingActions.push({
            id: 'workspace-invoices-filter',
            icon: SlidersHorizontal,
            label: activeOption?.label ?? 'All',
            ariaLabel: 'Filter invoices by status',
            group: 'view',
            subActions: INVOICE_STATUS_FILTER_OPTIONS.map(opt => ({
              id: `wif-${opt.value}`,
              label: opt.label,
              active: invoiceStatusFilter === opt.value,
              onClick: () => {
                setInvoiceStatusFilter(opt.value);
                window.dispatchEvent(new CustomEvent('workspace-invoices:filter', { detail: { status: opt.value } }));
              },
            })),
          });
        }
        // Users sub-tab: role filter pill + Add User, replacing the in-page
        // toolbar UserManagement renders outside `headerControlled` mode.
        // subActions double as the fallback rendering if the id-intercept
        // below doesn't apply, same graceful-degrade as the Invoices pill.
        pushWorkspaceUsersListActions();
        pushWorkspaceInviteActions();
        pushWorkspaceActivityActions();
        // Edit-user form: Save / Cancel replace the list pills. Same shape as
        // the workspace form's pair below — Cancel only once dirty, Save always
        // present but disabled while clean, both text pills so they read as a
        // pair rather than a symbol next to a word.
        if (isWorkspaceUserForm && !usersFormState?.inspectorActions) {
          if (usersFormState?.hasChanges) {
            trailingActions.push({
              id: 'workspace-user-form-cancel',
              label: 'Cancel',
              ariaLabel: 'Discard changes',
              onClick: () => window.dispatchEvent(new CustomEvent(WORKSPACE_USER_FORM_CANCEL_EVENT)),
              group: 'page',
            });
          }
          trailingActions.push({
            id: 'workspace-user-form-save',
            label: 'Save',
            ariaLabel: usersFormState?.hasChanges ? 'Save changes' : 'No changes to save',
            disabled: !usersFormState?.hasChanges,
            onClick: () => window.dispatchEvent(new CustomEvent(WORKSPACE_USER_FORM_SAVE_EVENT)),
            group: 'page',
          });
        }
        // Save / Cancel pills surface here too when a workspace is open in
        // the split-pane editor (same workspaceFormState bridge as the
        // standalone client-detail page below).
        if (workspaceDetailHeaderState && workspaceFormState) {
          if (workspaceFormState.hasChanges) {
            trailingActions.push({
              // Cancel is a text pill, never an X. An X reads as Close
              // (dismiss the view) rather than discard-my-edits, and Apple's
              // toolbar guidance is explicit that a symbol and a word must not
              // share one button. Pairs with the Save text pill beside it.
              id: 'workspace-cancel',
              label: 'Cancel',
              ariaLabel: 'Discard changes',
              disabled: workspaceFormState.isSaving,
              onClick: () => window.dispatchEvent(new CustomEvent('workspace:cancel')),
              group: 'page',
            });
          }
          trailingActions.push({
            id: 'workspace-save',
            icon: workspaceFormState.isSaving ? Loader2 : undefined,
            label: workspaceFormState.isSaving ? 'Saving' : 'Save',
            ariaLabel: workspaceFormState.isSaving
              ? 'Saving workspace'
              : workspaceFormState.hasChanges ? 'Save workspace' : 'No changes to save',
            disabled: workspaceFormState.isSaving || !workspaceFormState.hasChanges,
            onClick: () => window.dispatchEvent(new CustomEvent('workspace:save')),
            group: 'page',
          });
        }
        // The Add Workspace pill only makes sense on the directory itself.
        // Once the user drills into a specific workspace's edit pane, the
        // "+" pointing at "Add Workspace" reads as misplaced — they're
        // editing this workspace, not creating a new one. Hide it in that
        // context. To add another workspace, the user opens the list pane
        // (sidebar toggle) and uses the directory's + which still appears.
        if (!workspaceDetailHeaderState) {
          trailingActions.push({
            id: 'workspaces-add',
            icon: Plus,
            ariaLabel: 'Add workspace',
            onClick: () => window.dispatchEvent(new CustomEvent('workspaces:create-new')),
            group: 'page',
          });
        }
        pushNotificationsAction();
        break;
      case 'client-detail':
        if (usesMaterialMobileChrome) leadingActions.push({ id: 'workspace-sections-toggle', icon: SidebarLeftIcon, ariaLabel: 'Workspace sections', onClick: () => setSidebarOpenMobile(true) });
        pushBackAction('workspaces', 'Back to companies');
        if (workspaceFormState) {
          if (workspaceFormState.hasChanges) {
            trailingActions.push({
              // Cancel is a text pill, never an X. An X reads as Close
              // (dismiss the view) rather than discard-my-edits, and Apple's
              // toolbar guidance is explicit that a symbol and a word must not
              // share one button. Pairs with the Save text pill beside it.
              id: 'workspace-cancel',
              label: 'Cancel',
              ariaLabel: 'Discard changes',
              disabled: workspaceFormState.isSaving,
              onClick: () => window.dispatchEvent(new CustomEvent('workspace:cancel')),
              group: 'page',
            });
          }
          trailingActions.push({
            id: 'workspace-save',
            icon: workspaceFormState.isSaving ? Loader2 : undefined,
            label: workspaceFormState.isSaving ? 'Saving' : 'Save',
            ariaLabel: workspaceFormState.isSaving
              ? 'Saving workspace'
              : workspaceFormState.hasChanges ? 'Save workspace' : 'No changes to save',
            disabled: workspaceFormState.isSaving || !workspaceFormState.hasChanges,
            onClick: () => window.dispatchEvent(new CustomEvent('workspace:save')),
            group: 'page',
          });
        }
        pushNotificationsAction();
        break;
      case 'employers':
        if (usesMaterialMobileChrome && !isEmployersList) leadingActions.push({ id: 'employer-sections-toggle', icon: SidebarLeftIcon, ariaLabel: 'Employer sections', onClick: () => setSidebarOpenMobile(true) });
        // Delegated to buildEmployersHeaderActions — see
        // src/platform/navigation/headerActions/employers.ts.
        buildEmployersHeaderActions({
          listVisible: isEmployersList,
          selectedName: employerSelectedName,
          statusFilter: employersStatusFilter,
          formState: employerFormState,
          detailState: employerDetailState,
          onSelectStatus: (value) => {
            setEmployersStatusFilter(value);
            window.dispatchEvent(new CustomEvent(EMPLOYERS_FILTER_EVENT, { detail: { status: value } }));
          },
          onToggleSearch: toggleEmployersSearch,
          leadingActions,
          trailingActions,
          pushNotificationsAction,
          pushBackAction,
          navigateToContacts: () => { markReturnToCompanies(); onNavigate?.('contacts'); },
        });
        if (usesMaterialMobileChrome && isEmployersList) {
          const searchIndex = leadingActions.findIndex(action => action.id === 'employers-search');
          if (searchIndex >= 0) leadingActions.splice(searchIndex, 1);
        }
        break;
      case 'outreach':
        // The four sections have no header-controlled search or filters yet.
        // A drilled-in batch or prospect gets back (through the panel, so its
        // busy and unsaved-changes guards still run, then out through the
        // address), and the batch adds its two decisions.
        if (outreachApprovalsState?.isBatchList) {
          trailingActions.push({
            id: 'outreach-approvals-submit',
            label: 'Submit drafts for review',
            ariaLabel: 'Submit drafts for review',
            disabled: outreachApprovalsState.busy,
            onClick: () => window.dispatchEvent(new CustomEvent(OUTREACH_APPROVALS_SUBMIT_EVENT)),
            group: 'page',
          });
        } else if (outreachApprovalsState) {
          const batch = outreachApprovalsState;
          pushBackAction('outreach', 'Back to batches', () =>
            window.dispatchEvent(new CustomEvent(OUTREACH_APPROVALS_BACK_EVENT)));
          // Below lg there is no room for two text pills beside back and the
          // title, so the panel's meta strip keeps them there.
          if (isDesktopViewport && batch.hasTouches) {
            trailingActions.push({
              id: 'outreach-approvals-reject-all',
              label: 'Reject all',
              ariaLabel: 'Reject the whole batch',
              disabled: batch.busy || batch.pendingCount === 0,
              onClick: () => window.dispatchEvent(new CustomEvent(OUTREACH_APPROVALS_REJECT_ALL_EVENT)),
              group: 'page',
            });
            trailingActions.push({
              id: 'outreach-approvals-approve-all',
              label: 'Approve all',
              ariaLabel: 'Approve every eligible email in this batch',
              // Rightmost, but the last to collapse: if only one fits,
              // Reject all goes into the overflow, not the primary action.
              overflowPriority: 1,
              disabled: batch.busy || batch.approvableCount === 0,
              onClick: () => window.dispatchEvent(new CustomEvent(OUTREACH_APPROVALS_APPROVE_ALL_EVENT)),
              group: 'page',
            });
          }
        }
        if (prospectDetailState) {
          pushBackAction('outreach', 'Back to prospects', () =>
            window.dispatchEvent(new CustomEvent(PROSPECT_DETAIL_BACK_EVENT)));
          if (prospectDetailState.hasChanges && !prospectDetailState.saving) {
            trailingActions.push({
              id: 'prospect-detail-cancel',
              label: 'Cancel',
              ariaLabel: 'Discard changes',
              onClick: () => window.dispatchEvent(new CustomEvent(PROSPECT_DETAIL_CANCEL_EVENT)),
              group: 'page',
            });
          }
          if (prospectDetailState.hasChanges || prospectDetailState.saving) {
            trailingActions.push({
              id: 'prospect-detail-save',
              icon: prospectDetailState.saving ? Loader2 : undefined,
              label: prospectDetailState.saving ? 'Saving' : 'Save',
              ariaLabel: prospectDetailState.saving ? 'Saving prospect' : 'Save changes',
              disabled: prospectDetailState.saving,
              onClick: () => window.dispatchEvent(new CustomEvent(PROSPECT_DETAIL_SAVE_EVENT)),
              group: 'page',
            });
          }
        }
        pushNotificationsAction();
        break;
      case 'plant':
        // Delegated to buildPlantHeaderActions — see
        // src/platform/navigation/headerActions/plant.ts.
        buildPlantHeaderActions({
          listVisible: isPlantList,
          selectedLabel: plantSelectedLabel,
          statusFilter: plantStatusFilter,
          examDueCount: plantExamDueCount,
          detailState: plantDetailState,
          onSelectStatus: (value) => {
            setPlantStatusFilter(value);
            window.dispatchEvent(new CustomEvent(PLANT_FILTER_EVENT, { detail: { status: value } }));
          },
          onToggleSearch: togglePlantSearch,
          leadingActions,
          trailingActions,
          pushNotificationsAction,
          pushBackAction,
        });
        break;
      case 'user-form':
        pushBackAction('user-management', 'Back to user management');
        pushNotificationsAction();
        break;
      case 'add-project':
        // The header's back action always exits the flow. Previous / Continue
        // / Create live together in the form's own pinned footer, so their
        // availability never depends on cross-component event timing.
        pushBackAction('projects', 'Back to projects');
        pushNotificationsAction();
        break;
      case 'payroll':
      case 'tax':
        if (payrollTaxHeaderState?.profileOpen) {
          pushBackAction('payroll', 'Employees', () =>
            window.dispatchEvent(new CustomEvent('employee-profile:back')),
          );
          trailingActions.push({
            id: 'employee-profile-edit',
            icon: Pencil,
            label: 'Edit',
            ariaLabel: 'Edit employee',
            group: 'page',
            onClick: () => window.dispatchEvent(new CustomEvent('employee-profile:edit')),
          });
        } else {
          // These finance hubs replace the browser's product navigation with
          // their own section rows. Publish a back action so AppSidebar can
          // render the escape row above those sections. Phones keep their
          // existing Back-to-More behaviour via mobileHeaderConfig below.
          if (isDesktopViewport) {
            pushBackAction('dashboard', 'Back to dashboard');
          }
          if (payrollTaxFilterState?.statusOptions?.length) {
            const activeOpt = payrollTaxFilterState.statusOptions.find(
              (o) => o.value === payrollTaxFilterState!.statusFilter,
            );
            trailingActions.push({
              id: 'payroll-tax-filter',
              // A plain labelled dropdown showing the current choice (e.g. "Current"),
              // not a filter — it switches the view.
              icon: ChevronDown,
              label: activeOpt?.label ?? payrollTaxFilterState.statusOptions[0].label,
              ariaLabel: 'Switch view',
              group: 'page',
              subActions: payrollTaxFilterState.statusOptions.map((opt) => ({
                id: `payroll-tax-status-${opt.value}`,
                label: opt.label,
                active: payrollTaxFilterState!.statusFilter === opt.value,
                trailingIcon: payrollTaxFilterState!.statusFilter === opt.value ? Check : undefined,
                onClick: () => window.dispatchEvent(new CustomEvent('payroll-tax:set-status', { detail: opt.value })),
              })),
            });
          }
          if (payrollTaxHeaderState?.canCreate) {
            trailingActions.push({
              id: 'payroll-tax-create',
              icon: Plus,
              ariaLabel: payrollTaxHeaderState.createLabel,
              onClick: () => window.dispatchEvent(new CustomEvent('payroll-tax:create')),
              group: 'page',
            });
          }
        }
        pushNotificationsAction();
        break;
      case 'accounting':
        // Accounting uses the same drilled section sidebar as Payroll and
        // Tax, but has no view-specific header state of its own.
        if (isDesktopViewport) {
          pushBackAction('dashboard', 'Back to dashboard');
        }
        pushNotificationsAction();
        break;
      default:
        pushNotificationsAction();
        break;
    }


    if (!isDesktopViewport && !nativeBridge.isNative &&
      ['sales', 'purchases', 'payroll', 'accounting', 'tax', 'outreach'].includes(currentView ?? '')) {
      leadingActions.push({
        id: 'module-sections-toggle',
        icon: SidebarLeftIcon,
        ariaLabel: 'Section navigation',
        onClick: () => setSidebarOpenMobile(true),
        group: 'page',
      });
    }

    if (pageControls) {
      if (pageControls.search) (usesMaterialMobileChrome && !pageControlsSearchOpen ? trailingActions : leadingActions).push({
        id: 'page-controls-search', icon: Search, ariaLabel: pageControls.search.label,
        onClick: () => setPageControlsSearchOpen((open) => !open),
      });
      trailingActions.push(...pageControls.filters.map((filter) => ({
        id: `${pageControls.id}-${filter.id}`, icon: SlidersHorizontal,
        desktopLabel: filter.options.find((option) => option.value === filter.value)?.label ?? filter.label,
        ariaLabel: filter.label, group: 'view' as const,
        subActions: filter.options.map((option) => ({
          id: `${pageControls.id}-${filter.id}-${option.value}`, label: option.label,
          active: filter.value === option.value,
          onClick: () => dispatchPageHeaderControl(pageControls.id, { type: 'filter', id: filter.id, value: option.value }),
        })),
      })));
      if (pageControls.createLabel) trailingActions.push({
        id: `${pageControls.id}-create`, icon: Plus,
        desktopLabel: pageControls.createLabel, ariaLabel: pageControls.createLabel, group: 'page',
        onClick: () => dispatchPageHeaderControl(pageControls.id, { type: 'create' }),
      });
    }

    return { leadingActions, trailingActions };
  }, [
    triggerCatalogHeaderState,
    boqHeaderState,
    boqExportHeaderState,
    isBoqExportRoute,
    documentsHeaderState,
    documentPreviewHeaderState,
    documentsSearchOpen,
    takeoffHeaderState,
    takeoffGallerySearchAvailable,
    // takeoffCanvasState drives the canvas-derived pills (fit, sheet name,
    // scale, vector, isolate, invert, minimap). Without it here the memo
    // doesn't rebuild when the canvas broadcasts, so those pills only
    // appeared when takeoffHeaderState happened to churn at the same time.
    takeoffCanvasState,
    takeoffPanelState,
    // Uploader selection count + details-step field/count payload — both feed
    // buildTakeoffHeaderActions; without them the pills only refresh when
    // takeoffHeaderState happens to churn at the same time.
    takeoffUploaderSelected,
    takeoffUploaderCanBrowse,
    takeoffUploaderDetails,
    takeoffUploaderStep,
    employerFormState,
    // The profile's dirty/contactability state. Its own dep, not covered by
    // employerSelectedName: typing a change moves this while the selected
    // employer stays put, and the Cancel/Save pills key off it.
    employerDetailState,
    // Every value the employers case reads. The raw state object is tracked as
    // well as the two values derived from it: those only move when the directory
    // appears/disappears or a profile opens, so a broadcast that changed some
    // other field would otherwise leave the whole pill set frozen — for EVERY
    // view, not just this one (see the workspaceDetailHeaderState note below for
    // the reproduction).
    employersHeaderState,
    isEmployersList,
    employerSelectedName,
    // The filter VALUE renders as the pill's label, which the booleans above
    // don't carry.
    employersStatusFilter,
    toggleEmployersSearch,
    // The drilled-in prospect's dirty/saving state — Cancel/Save key off it
    // directly, so it has to be tracked here the same as employerDetailState.
    prospectDetailState,
    // A drilled-in batch: back, and the busy/count flags its pills key off.
    outreachApprovalsState,
    // Decides whether the batch pills go in the header or stay in the panel.
    isDesktopViewport,
    // Same reasoning as the employers block above, for the plant case.
    plantHeaderState,
    isPlantList,
    plantSelectedLabel,
    plantStatusFilter,
    plantExamDueCount,
    togglePlantSearch,
    plantDetailState,
    interimHeaderState,
    interimListState,
    interimBreakdownGroupsState,
    projectAdminHeaderState,
    projectMembersHeaderState,
    boqImporterState,
    boqImportProgress,
    projectsHeaderState,
    pipelineHeaderState,
    plannerHeaderState,
    plannerEditorHeaderState,
    workspaceFormState,
    profileDetailsFormState,
    profileSignatureState,
    profileActiveTab,
    onProfileTabChange,
    preferencesActiveTab,
    onPreferencesTabChange,
    workspacesListHeaderState,
    workspaceCreating,
    workspacesSearchOpen,
    workspacesSearchValue,
    // The memo reads workspaceDetailHeaderState RAW in four places — the back
    // action's target, the directory filter's visibility, the Save/Cancel gate,
    // and + Add Workspace's visibility — so it has to be tracked directly.
    // Deriving booleans for the two sub-tabs (below) used to stand in for this,
    // but those only flip when the tab is specifically Invoices or Users. Close
    // a workspace from Documents / Projects / Activity / Membership and neither
    // moves, so the memo never recomputed: the directory rendered underneath
    // while the header kept its "workspace open" chrome, missing + Add
    // Workspace and Filter, until some unrelated dep (a new notification, say)
    // happened to churn.
    workspaceDetailHeaderState,
    // Still listed: these also depend on currentView, and they carry the filter
    // VALUES, which workspaceDetailHeaderState does not.
    isWorkspaceInvoicesTab,
    invoiceStatusFilter,
    isSalesInvoicesTab,
    reportHeaderState,
    salesInvoiceStatusFilter,
    isWorkspaceUsersTab,
    usersRoleFilter,
    isWorkspaceActivityTab,
    workspaceActivityHeaderState,
    isWorkspaceInviteHeaderForm,
    usersHeaderState,
    // Drives the edit-user form's Save/Cancel pills AND the leading chevron's
    // target. Both derive from usersFormState, so the raw state is tracked
    // rather than only the derived boolean.
    isWorkspaceUserForm,
    usersFormState,
    // Bots section pills (New bot / Cancel / Save) and its back-chevron
    // branch both read these — same shape as the users tab above.
    isBotsTabOpen,
    botsHeaderState,
    payrollTaxHeaderState,
    payrollTaxFilterState,
    workflowsHeaderState,
    workflowsSearchOpen,
    workflowsSearchValue,
    templatesHeaderState,
    contactsHeaderState,
    contactsSearchOpen,
    contactsSearchValue,
    resourceLibraryHeaderState,
    resourceLibrarySearchOpen,
    resourceLibrarySearchValue,
    rateItemFormState,
    projectDetailTab,
    pageControls,
    pageControlsSearchOpen,
    procurementHeaderState,
    sitePhotosHeaderState,
    siteRegisterHeaderState,
    projectOverviewHeaderState,
    activeRfiRegister,
    activeRfiForm,
    projectEmailHeaderState,
    projectEmailSearchOpen,
    projectDetailApplicationOpen,
    dayworksHeaderState,
    dayworkFormState,
    instructionsHeaderState,
    instructionFormState,
    eventsHeaderState,
    eventFormState,
    activityHeaderState,
    projectActivitySearchOpen,
    notesHeaderState,
    projectNotesSearchOpen,
    projectPermissions,
    onProjectTabChange,
    workspaceNavigation.authoritative,
    workspaceNavigation.navigation,
    hsHeaderState,
    hsEditorHeaderState,
    hsDocumentHeaderState,
    calendarOverdueCount,
    hasRoomForCalendarLabels,
    hasRoomForCalendarPrimaryLabels,
    calendarHeaderState,
    tasksSelection,
    taskQueueOpen,
    onToggleTaskQueue,
    bookingPanelOpen,
    onToggleBooking,
    currentView,
    messagesHeaderState.isComposeMode,
    messagesHeaderState.mode,
    messagesComposeOpen,
    messagesConversationState,
    messagesMembersState,
    // Without these two the memo only picks up the latest desktop
    // conversation/compose state when some unrelated dep happens to churn at
    // the same time — the exact "only refreshes when X churns" trap the
    // comments elsewhere in this array warn about.
    desktopChatSelected,
    desktopComposeOpen,
    notificationsUnreadCount,
    onNavigate,
    onToggleNotifications,
    shouldRenderHeaderActions,
    isDesktopViewport,
    nativeBridge.isNative,
    nativeBridge.platform,
    // Opens the drilled-in sidebar from the project sections pill.
    setSidebarOpenMobile,
    // Email view state — without these, opening a thread doesn't switch
    // the trailing actions out of folder mode (so the user keeps seeing
    // Search/Filter instead of Archive/Reply).
    emailThreadOpen,
    emailThreadStarred,
    emailFilterMenuOpen,
    emailActiveFilters,
  ]);

  /** The phone tab bar's own destinations; everything else is overflow. */
  const PHONE_TAB_ROOTS = new Set<string>([
    'dashboard', 'projects', 'messages', 'notifications', 'more', 'search',
  ]);

  // Overflow views (Sales, Purchases, Payroll, Tax, Contacts...) are only
  // reachable through More on a phone, and the sidebar that would be the way
  // out is not on screen there — so they rendered with no back control at all.
  // Their own header builders never add one because on desktop they are
  // top-level sidebar destinations. Injected here rather than in each builder
  // so a view cannot be added later and silently arrive with no exit.
  const [settingsNavigationBack, setSettingsNavigationBack] = useState(getBrowserHeaderBackAction);
  useEffect(() => subscribeBrowserHeaderBackAction(setSettingsNavigationBack), []);
  const mobileHeaderConfig = useMemo(() => {
    if (settingsNavigationBack && ['profile','account','system-settings'].includes(currentView ?? '')) {
      return {...mobileHeaderConfigBase, leadingActions:[{
        id:'back-settings-categories',icon:ChevronLeft,ariaLabel:settingsNavigationBack.label,onClick:settingsNavigationBack.onClick,
      } as MobileHeaderAction, ...mobileHeaderConfigBase.leadingActions.filter(action => !action.id.startsWith('back') && !LARGE_TITLE_BACK_IDS.has(action.id))]};
    }
    if (!mobileHeaderConfigBase || isDesktopViewport) return mobileHeaderConfigBase;
    if (PHONE_TAB_ROOTS.has(currentView ?? '')) return mobileHeaderConfigBase;
    const leading = mobileHeaderConfigBase.leadingActions ?? [];
    const hasBack = leading.some(
      (action: MobileHeaderAction) =>
        action.id.startsWith('back') || LARGE_TITLE_BACK_IDS.has(action.id),
    );
    if (hasBack) return mobileHeaderConfigBase;
    return {
      ...mobileHeaderConfigBase,
      leadingActions: [
        {
          id: 'back-to-more',
          icon: ChevronLeft,
          ariaLabel: 'Back',
          onClick: () => onNavigate?.('more'),
        } as MobileHeaderAction,
        ...leading,
      ],
    };
  }, [mobileHeaderConfigBase, isDesktopViewport, currentView, onNavigate, settingsNavigationBack]);

  // A persistent header keeps each view's search-open state across navigation.
  // Only the morph actually offered by this view may take over the phone row;
  // the Projects/Messages searches are page-body fields on mobile and never
  // participate, even if their desktop search state remains open.
  const searchActiveOnMobile = hasActiveVisibleMobileSearch(
    mobileHeaderConfig.leadingActions,
    {
      'takeoff-gallery-search': takeoffGallerySearchAvailable && takeoffSearchOpen,
      'projects-search': projectsSearchOpen,
      'messages-search': messagesSearchOpen,
      'messages-members-search': membersSearchOpen,
      'messages-new': messagesComposeOpen,
      'boq-search': boqSearchOpen && !isBoqExportRoute,
      'boq-importer-search': boqImporterSearchOpen,
      'documents-search': documentsSearchOpen,
      'page-controls-search': pageControlsSearchOpen,
      'contacts-search': contactsSearchOpen,
      'resource-library-search': resourceLibrarySearchOpen,
      'workspaces-search': workspacesSearchOpen,
      'employers-search': employersSearchOpen,
      'plant-search': plantSearchOpen,
      'workspace-bots-search': !!isBotsListVisible && botsSearchOpen,
    },
    MOBILE_HIDDEN_LEADING_SEARCH_IDS,
  );

  const browserHeaderBackAction = useMemo<BrowserHeaderBackAction | null>(() => {
    if (nativeBridge.isNative) return null;
    if (isBoqExportRoute) {
      return {
        id: 'boq-back-to-pricing',
        label: 'Back to pricing',
        onClick: () => window.dispatchEvent(new CustomEvent('boq:close-export')),
      };
    }
    // Members drills in from a conversation: the arrow closes it (or leaves
    // add mode) before it ever touches the browser history.
    if (currentView === 'messages' && messagesMembersState) {
      return messagesMembersBackAction(messagesMembersState);
    }
    const headerBackAction = mobileHeaderConfig.leadingActions.find(
      (action) => action.icon === ChevronLeft && action.id !== 'calendar-prev' && action.onClick,
    );
    if (!headerBackAction?.onClick) return null;

    return {
      id: headerBackAction.id,
      label: headerBackAction.ariaLabel,
      onClick: () => headerBackAction.onClick?.(),
    };
  }, [isBoqExportRoute, currentView, messagesMembersState, mobileHeaderConfig.leadingActions, nativeBridge.isNative]);

  // While a morph search is active, only the search-mode pills survive.
  // For Documents: the unified options pill (view + sort) and the cancel X.
  // Other contexts hide trailing pills entirely during search.
  // Computed AFTER `mobileHeaderConfig` so it sees the freshly-built array.
  // (messages-compose-cancel never reaches this filter: the compose morph no
  // longer opens on mobile, so searchActiveOnMobile can't be true for it.)
  const visibleMobileTrailing = searchActiveOnMobile
    ? mobileHeaderConfig.trailingActions.filter(a =>
        a.id === 'documents-search-options'
        || a.id === 'documents-search-cancel')
    : mobileHeaderConfig.trailingActions;

  // Both the fit and the render work off this list, so a pill that draws
  // nothing on mobile can't quietly spend row budget.
  const visibleMobileLeading = useMemo(
    () => (searchActiveOnMobile
      ? mobileHeaderConfig.leadingActions.filter(a => MOBILE_SEARCH_ACTIVE_LEADING_IDS.has(a.id))
      : mobileHeaderConfig.leadingActions
    ).filter(a => !MOBILE_HIDDEN_LEADING_SEARCH_IDS.has(a.id)),
    [documentsSearchOpen, mobileHeaderConfig.leadingActions, searchActiveOnMobile],
  );

  // ── Mobile row overflow fit ───────────────────────────────────────────────
  // Content-box width of the main row, which is what the leading pills, the
  // compact title and the trailing pills have to share. Seeded from the
  // viewport so the very first paint fits properly instead of over-collapsing
  // for a frame.
  const [mainRowWidth, setMainRowWidth] = useState(() =>
    typeof window === 'undefined'
      ? 0
      : Math.max(0, window.innerWidth - MOBILE_ROW_PADDING_LEFT - MOBILE_ROW_PADDING_RIGHT));
  const mainRowRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const node = mainRowRef.current;
    if (!node) return;
    const observer = new ResizeObserver((entries) => {
      // Only whole-pixel changes can move a pill in or out of the row, so a
      // resize drag doesn't re-render on every sub-pixel step.
      const next = Math.floor(entries[0].contentRect.width);
      setMainRowWidth(prev => (prev === next ? prev : next));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [isDesktopViewport, shouldRenderMainRow]);

  useEffect(() => {
    if (currentView !== 'email' || !isDesktopViewport || nativeBridge.isNative) return;
    const focusEmailSearch = () => {
      requestAnimationFrame(() => emailSearchInputRef.current?.focus());
    };
    window.addEventListener('email:focus-search', focusEmailSearch);
    return () => window.removeEventListener('email:focus-search', focusEmailSearch);
  }, [currentView, isDesktopViewport, nativeBridge.isNative]);

  // Row width claimed by trailing pills that aren't MobileHeaderActions. The
  // fit can't collapse them, so it has to budget around them instead.
  const mobileTrailingReservedWidth = useMemo(() => {
    if (isDesktopViewport) return 0;
    let reserved = 0;
    // MessagesChatActionsPill decides its own visibility from a window event we
    // can't read here; an open conversation is the closest signal the header
    // has. Reserve the menu, optional call/huddle button and huddle chevron.
    if (currentView === 'messages' && messagesConversationState) {
      reserved += 2 * (HEADER_PILL_BASE + HEADER_CLUSTER_GAP);
      reserved += 20;
    }
    if (currentView === 'add-project' && addProjectEmployerState.available) {
      reserved += HEADER_PILL_BASE + HEADER_CLUSTER_GAP;
    }
    // Labelled pill ("Move 3 to…"), so it runs wider than a circle.
    if (boqHeaderState && boqHeaderState.selectedCount > 0) {
      reserved += 132 + HEADER_CLUSTER_GAP;
    }
    if (boqApprovalPill) reserved += HEADER_PILL_BASE + HEADER_CLUSTER_GAP;
    // QuantisAI is appended outside the fitted action list whenever this
    // trailing group renders. Support is hidden on mobile and spends no width.
    if (shouldRenderHeaderActions && (visibleMobileTrailing.length > 0 || reserved > 0)) {
      reserved += HEADER_PILL_BASE + HEADER_CLUSTER_GAP;
    }
    return reserved;
  }, [
    isDesktopViewport,
    currentView,
    isWorkspaceResourceLibrary,
    rateItemFormState,
    resourceLibraryPickerState,
    messagesConversationState,
    addProjectEmployerState.available,
    boqHeaderState,
    boqApprovalPill,
    shouldRenderHeaderActions,
    visibleMobileTrailing.length,
  ]);

  // Two passes, deliberately in this order: the trailing cluster is the one
  // that gets pushed off-screen, and its budget only needs the leading
  // cluster's pinned floor — a number no fit can change. Once trailing has a
  // real width, leading fits against what's actually left.
  const mobileRowFit = useMemo(() => {
    const leading = visibleMobileLeading;
    const trailing = visibleMobileTrailing.filter(a => !MOBILE_HIDDEN_ACTION_IDS.has(a.id));
    const titleReserve = !searchActiveOnMobile && resolvedPageTitle !== null
      ? COMPACT_TITLE_RESERVE
      : 0;
    const trailingFit = usesMaterialMobileChrome && !searchActiveOnMobile && trailing.length > 1
      ? { visible: trailing.filter((action) => action.id === 'page-controls-search'), collapsed: trailing.filter((action) => action.id !== 'page-controls-search') }
      : fitCluster(
      trailing,
      mainRowWidth
        - titleReserve
        - mobileTrailingReservedWidth
        - measureClusterFloor(leading, isPinnedLeadingAction),
      neverPinned,
    );
    const leadingFit = fitCluster(
      leading,
      mainRowWidth
        - titleReserve
        - mobileTrailingReservedWidth
        - measureFittedCluster(trailingFit),
      isPinnedLeadingAction,
    );
    return { leadingFit, trailingFit };
  }, [
    visibleMobileLeading,
    visibleMobileTrailing,
    usesMaterialMobileChrome,
    searchActiveOnMobile,
    resolvedPageTitle,
    mainRowWidth,
    mobileTrailingReservedWidth,
  ]);

  // One sheet serves both overflow pills — only one can be open at a time.
  const [overflowSheet, setOverflowSheet] = useState<'leading' | 'trailing' | 'desktop' | null>(null);
  const centredMobileTitleSideInset = 12 + Math.max(
    measureFittedCluster(mobileRowFit.leadingFit),
    measureFittedCluster(mobileRowFit.trailingFit) + mobileTrailingReservedWidth,
  );

  // Large title shows only on mobile top-level views — those where the leading
  // cluster has no back-chevron (pushBackAction / rate-item-back / messages-back)
  // and the BOQ importer overlay is not open. All other views (detail screens,
  // sub-forms, calendar, resource-library, etc.) keep the compact inline title.
  const showLargeTitle =
    !usesMaterialMobileChrome &&
    !isDesktopViewport &&
    !boqImporterState?.open &&
    !visibleMobileLeading.some(
      a => a.id.startsWith('back-') || LARGE_TITLE_BACK_IDS.has(a.id)
    );

  // When the pill row has nothing in it at rest (e.g. the PWA's Home, where
  // the bottom tab bar carries navigation), the 54px row is a dead strip
  // between the status bar and the large title — the title belongs IN the
  // header row ("that Home is too low.. it needs to be in the header"). The
  // row keeps its height — the scrolled-in compact title still lands there —
  // but the large title pulls up over the whole row and the published
  // clearance shrinks to match, so the content follows.
  const LARGE_TITLE_BARE_ROW_PULL = 54;
  // The large title rides IN the pill row, iOS-style compact-nav-bar-free
  // ("that Home is too low.. it needs to be in the header" / "it's on every
  // page with that header") — left-aligned title sharing the row with the
  // trailing pills. Only a view with VISIBLE leading pills keeps the title
  // on its own line below (they'd collide horizontally). Ids in
  // MOBILE_HIDDEN_ACTION_IDS never render on mobile, so they don't count.
  const rendersAny = (actions: Array<{ id: string }>) =>
    actions.some(a => !MOBILE_HIDDEN_ACTION_IDS.has(a.id));
  const largeTitleRidesRow =
    !isDesktopViewport &&
    !searchActiveOnMobile &&
    !rendersAny(mobileRowFit.leadingFit.visible);
  const trailingPillCount =
    mobileRowFit.trailingFit.visible.filter(a => !MOBILE_HIDDEN_ACTION_IDS.has(a.id)).length;

  // Expose --qs-large-title-clearance on the document root so that
  // AppLayout's contentInnerStyle can reserve the correct extra padding-top
  // (equal to the large-title height) without a re-render cascade. The
  // variable is set once per view transition (not per scroll frame).
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      '--qs-large-title-clearance',
      showLargeTitle
        ? `${Math.max(0, LARGE_TITLE_HEIGHT - (largeTitleRidesRow ? LARGE_TITLE_BARE_ROW_PULL : 0))}px`
        : '0px',
    );
    return () => {
      root.style.setProperty('--qs-large-title-clearance', '0px');
    };
  }, [showLargeTitle, largeTitleRidesRow]);

  // Drive the large-title ↔ compact-title crossfade from the content outlet's
  // scrollTop. All DOM writes go via refs — no React state, no re-renders on
  // the 60-120 Hz scroll path.
  useEffect(() => {
    const applyProgress = (progress: number) => {
      if (largeTitleRef.current) {
        const pixelRatio = window.devicePixelRatio || 1;
        const offset = Math.round(-12 * progress * pixelRatio) / pixelRatio;
        largeTitleRef.current.style.opacity = String(1 - progress);
        largeTitleRef.current.style.transform = `translate3d(0, ${offset}px, 0)`;
        largeTitleRef.current.style.willChange = progress > 0 && progress < 1
          ? 'opacity, transform'
          : '';
      }
      if (compactTitleRef.current) {
        compactTitleRef.current.style.opacity = String(progress);
      }
    };

    if (!showLargeTitle) {
      // Restore compact-title to full opacity when leaving large-title mode so
      // it doesn't stay transparent after navigating to a detail view.
      if (largeTitleRef.current) {
        largeTitleRef.current.style.opacity = '';
        largeTitleRef.current.style.transform = '';
        largeTitleRef.current.style.willChange = '';
      }
      if (compactTitleRef.current) {
        compactTitleRef.current.style.opacity = '';
      }
      return;
    }

    // Some views own their own scroll container rather than relying on the
    // generic app-content-outlet. Map view name → slot so we prefer the right
    // element without a chain of hardcoded fallbacks.
    const VIEW_SCROLL_SLOT: Record<string, string> = {
      messages: 'messages-chat-list',
      notifications: 'notifications-scroll',
    };
    const preferredSlot = currentView ? VIEW_SCROLL_SLOT[currentView] : undefined;
    const slotSelector = `[data-slot="${preferredSlot ?? 'app-content-outlet'}"]`;

    const clampProgress = (el: HTMLElement) =>
      Math.min(1, Math.max(0, el.scrollTop / LARGE_TITLE_HEIGHT));

    let rafId: number | null = null;
    // Listen on the window in the CAPTURE phase rather than on the element:
    // lazy-loaded views (NotificationsView, MessagesView) mount their scroll
    // container AFTER this effect runs, so querySelector-then-attach found
    // nothing and the large title never collapsed. Scroll events don't bubble
    // but they DO capture-propagate, so one window listener covers the
    // container whenever it appears — no mount-order coupling.
    const onScrollCapture = (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement) || !target.matches(slotSelector)) return;
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        applyProgress(clampProgress(target));
      });
    };

    // Apply immediately so the initial render reflects the correct state even
    // when the container has a non-zero scrollTop from a prior navigation.
    // The container may not exist yet (lazy chunk still loading) — start
    // expanded; the first scroll event corrects the progress.
    const initialEl = document.querySelector(slotSelector) as HTMLElement | null;
    applyProgress(initialEl ? clampProgress(initialEl) : 0);

    window.addEventListener('scroll', onScrollCapture, { passive: true, capture: true });
    return () => {
      window.removeEventListener('scroll', onScrollCapture, { capture: true });
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [showLargeTitle, currentView]);

  const hasContextualSubNav = showProjectSubNav || !!projectListHeaderProps;

  if (!shouldRenderMainRow && !hasContextualSubNav) {
    return null;
  }

  /** `shared` = this pill is inside a HeaderPillGroup capsule, so it must not
   *  draw its own glass — the group owns the background. An ACTIVE pill still
   *  paints its own brand fill on top, which is how a selected item reads
   *  inside a grouped set. */
  const renderMobileHeaderAction = (action: MobileHeaderAction, opts?: { shared?: boolean }) => {
    // Hidden on the mobile header per product decision — the notification bell,
    // the support/help button, the two desktop-only Projects actions and the
    // workspaces split-pane toggle. All stay in the action list so the desktop
    // render path (which also calls this function) still renders them, so the
    // guard must stay mobile-only. MOBILE_HIDDEN_ACTION_IDS is shared with the
    // overflow fit, which has to agree with this exactly.
    if (!isDesktopViewport && MOBILE_HIDDEN_ACTION_IDS.has(action.id)) return null;
    const displayLabel = isDesktopViewport ? (action.desktopLabel ?? action.label) : action.label;
    if (action.emphasis === 'primary' && !action.subActions?.length) {
      return <Button key={action.id} variant="outline" size="sm"
        data-header-action-id={action.id} data-no-window-drag="true"
        aria-label={action.ariaLabel} disabled={action.disabled}
        className="pointer-events-auto shrink-0"
        onClick={event => action.onClick?.(event)}>{displayLabel}</Button>;
    }
    const Icon = action.icon;
    const isActive = !!action.active;
    const isDisabled = !!action.disabled;
    // Narrow box + full-bleed hover chip for icon-only pills sharing a
    // capsule. Two 44px boxes butted together leave ~24px of dead capsule
    // between the glyphs, which reads as two distant buttons rather than one
    // grouped control. Only the horizontal box narrows — the row stays 44px
    // tall, and the neighbour it gives ground to is the other half of the
    // same control. See the sizing note below.
    const tightInGroup = !usesMaterialMobileChrome && !!opts?.shared && !displayLabel;
    const tightWidth = isDesktopViewport && !usesTabletSidebarLayout ? 36 : 44;
    const hasSubActions = !!action.subActions && action.subActions.length > 0;
    const pillButton = (
      <motion.button
        key={action.id}
        type="button"
        // motion's `layout` springs the pill's width when its label
        // appears / disappears (e.g. Auto-Price gaining "Auto-price 12"
        // when there's work to do). The bg + icon colours cross-fade
        // via CSS transitions below — motion can't tween CSS variables.
        layout
        transition={{ type: 'spring', stiffness: 380, damping: 32, mass: 0.4 }}
        data-header-action-id={action.id}
        data-no-window-drag="true"
        aria-pressed={action.active === undefined ? undefined : isActive}
        aria-expanded={action.expanded}
        aria-disabled={isDisabled || undefined}
        aria-haspopup={hasSubActions ? 'menu' : undefined}
        disabled={isDisabled}
        onMouseDown={(event) => {
          event.stopPropagation();
        }}
        onClick={(event) => {
          if (isDisabled) return;
          // When the pill has subActions, click is owned by the
          // PopoverTrigger wrapper — no own onClick.
          if (hasSubActions) return;
          action.onClick?.(event);
        }}
        className="group relative flex items-center justify-center transition-opacity disabled:opacity-40 disabled:active:opacity-40"
        // pointerEvents: 'auto' re-enables hit-testing inside the floating
        // header, which is `pointer-events: none` so its empty area passes
        // touches/scroll through to the content beneath. Icon-only pills
        // are perfect 44×44 circles (Apple Liquid Glass spec); pills
        // with a label grow horizontally and become true pill shapes.
        style={{
          height: 44,
          // Inside a shared capsule on desktop the box tightens to 36 so the
          // glyphs sit close enough to read as one control — at 44 the run had
          // MORE space between its own icons (44/52px) than between capsules
          // (30px), which inverts the grouping the capsule is there to convey.
          // Touch keeps 44: that is the HIG minimum and a pointer does not need
          // it. Height stays 44 either way, so the vertical target is intact.
          minWidth: tightInGroup ? tightWidth : 44,
          width: displayLabel ? 'auto' : (tightInGroup ? tightWidth : 44),
          padding: displayLabel ? '0 4px' : 0,
          borderRadius: usesMaterialMobileChrome ? 20 : 9999,
          pointerEvents: 'auto',
        }}
        aria-label={headerActionName(action.ariaLabel, action.badge)}
        title={!displayLabel && !isTouchRuntime && !usesTabletSidebarLayout ? action.ariaLabel : undefined}
        // Tactile feedback: a hair of growth on hover (1.04) and a
        // press-compress on tap (0.94) — gives the row life on a
        // pointer device without being noisy.
        whileHover={isDisabled || usesMaterialMobileChrome || isTouchRuntime || usesTabletSidebarLayout ? undefined : { scale: 1.04 }}
        whileTap={isDisabled ? undefined : { scale: usesMaterialMobileChrome ? 0.98 : 0.94 }}
      >
        {/* Suppressed inside a HeaderPillGroup, which draws one capsule for the
            whole run — except when active, where the brand fill still paints on
            top so a selected item reads within the group. */}
        {(!opts?.shared || isActive) && (
        <span
          aria-hidden="true"
          className="pointer-events-none"
          // Apple's pill BG layer. Active toggles paint in brand colour so
          // their pressed state reads at a glance. The colour cross-fades
          // via CSS transition when active flips (300ms, ease-out).
          //
          // The fill is 36 inside a 44 button, insetting 4px on every side to
          // hug the content span (which is already 36). The button keeps its
          // full 44pt hit area — that's the HIG minimum and what
          // HEADER_PILL_BASE reserves in the overflow fit — while the visible
          // circle lines up with the 36px search field and select pills beside
          // it. Growing the fill back to 44 makes those look undersized.
          style={{
            position: 'absolute',
            left: usesMaterialMobileChrome ? 2 : 4,
            right: usesMaterialMobileChrome ? 2 : 4,
            height: usesMaterialMobileChrome ? 40 : 36,
            top: usesMaterialMobileChrome ? 'calc(50% - 20px)' : 'calc(50% - 18px)',
            zIndex: 0,
            borderRadius: usesMaterialMobileChrome ? 20 : 296,
            background: isActive
              ? usesMaterialMobileChrome
                ? 'color-mix(in srgb, var(--header-foreground) 18%, transparent)'
                : 'var(--brand)'
              : usesMaterialMobileChrome
                ? 'transparent'
                : 'var(--header-pill-fill)',
            mixBlendMode: usesMaterialMobileChrome
              ? 'normal'
              : isActive
                ? ('normal' as any)
                : ('var(--header-pill-blend)' as any),
            backdropFilter: usesMaterialMobileChrome ? 'none' : 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: usesMaterialMobileChrome ? 'none' : 'blur(20px) saturate(180%)',
            boxShadow: usesMaterialMobileChrome
              ? 'none'
              : isActive
              ? '0 10px 28px color-mix(in srgb, var(--brand) 32%, transparent), 0 2px 6px rgba(0,0,0,0.08)'
              : '0px 8px 24px 0px rgba(0,0,0,0.08), var(--glass-inset, none)',
            transition: 'background 280ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 280ms cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        />
        )}
        {/* Hover chip. Sits above the capsule fill and below the icon, at the
            same 36/inset-4 geometry, so hovering shows the individual button's
            shape within a shared capsule — otherwise a run of three icons has
            no per-item feedback at all and you can't tell which one you're on.
            Skipped when active: the brand fill already owns that state. */}
        {!isActive && !isDisabled && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute opacity-0 transition-opacity duration-150 group-hover:opacity-100"
            style={{
              // The 4px inset hugs a 44 box down to the 36 fill. A tightened
              // in-group pill is already 36 wide, so the same inset would
              // squeeze the chip to 28 and it would read as a narrow oval
              // rather than the button's own circle.
              left: usesMaterialMobileChrome ? 2 : tightInGroup ? 0 : 4,
              right: usesMaterialMobileChrome ? 2 : tightInGroup ? 0 : 4,
              height: usesMaterialMobileChrome ? 40 : 36,
              top: usesMaterialMobileChrome ? 'calc(50% - 20px)' : 'calc(50% - 18px)',
              zIndex: 0,
              borderRadius: usesMaterialMobileChrome ? 20 : 296,
              background: usesMaterialMobileChrome
                ? 'color-mix(in srgb, var(--header-foreground) 12%, transparent)'
                : 'var(--header-pill-hover)',
              boxShadow: usesMaterialMobileChrome ? 'none' : 'var(--header-pill-hover-shadow)',
            }}
          />
        )}
        <motion.span
          layout
          className="relative flex items-center justify-center"
          // `zIndex: 1` keeps the icon / label stack above the BG layer
          // (which sits at z-index 0 — Apple's pill structure).
          style={{
            minWidth: 36,
            height: 36,
            paddingLeft: displayLabel ? 10 : 0,
            paddingRight: displayLabel ? 10 : 0,
            gap: displayLabel && Icon ? 6 : 0,
            zIndex: 1,
          }}
        >
          {Icon && (
            <Icon
              // The glyph itself answers the pointer, not just the chip behind
              // it: a small lift on hover, settling back on press so the
              // button's own 0.94 tap-compress still reads.
              className={cn(!isDisabled && !usesMaterialMobileChrome && 'group-hover:scale-110 group-active:scale-100')}
              style={{
                // 18 in a 36 fill — half. At 17-in-44 the glyph was 39% of the
                // circle and the pill read as mostly air.
                width: usesMaterialMobileChrome ? 24 : 18,
                height: usesMaterialMobileChrome ? 24 : 18,
                color: isActive
                  ? usesMaterialMobileChrome
                    ? 'var(--header-foreground)'
                    : 'white'
                  : usesMaterialMobileChrome
                    ? 'var(--header-foreground)'
                    : 'var(--foreground)',
                // `scale`, not `transform`: Tailwind v4 compiles scale-110 to
                // the standalone scale property, so transitioning transform
                // animated nothing and the glyph snapped.
                transition: 'color 280ms cubic-bezier(0.16, 1, 0.3, 1), scale 150ms ease-out',
              }}
              strokeWidth={2.25}
            />
          )}
          <AnimatePresence initial={false}>
            {displayLabel && (
              <motion.span
                key="label"
                layout
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ type: 'spring', stiffness: 380, damping: 32, mass: 0.4 }}
                className={cn(
                  'font-semibold whitespace-nowrap overflow-hidden',
                  usesMaterialMobileChrome ? 'font-sans' : 'font-apple',
                )}
                style={{
                  color: isActive
                    ? usesMaterialMobileChrome
                      ? 'var(--header-foreground)'
                      : 'white'
                    : usesMaterialMobileChrome
                      ? 'var(--header-foreground)'
                      : 'var(--foreground)',
                  fontSize: usesMaterialMobileChrome ? 14 : 'var(--header-pill-label-size, 13px)',
                  lineHeight: usesMaterialMobileChrome ? '20px' : '18px',
                  letterSpacing: usesMaterialMobileChrome ? '0.1px' : '-0.08px',
                  transition: 'color 280ms cubic-bezier(0.16, 1, 0.3, 1)',
                }}
              >
                {displayLabel}
              </motion.span>
            )}
          </AnimatePresence>
        </motion.span>
        {typeof action.badge === 'number' && action.badge > 0 && (
          <span
            data-slot="header-action-badge"
            aria-hidden="true"
            className="pointer-events-none absolute top-1.5 right-1.5 size-2 rounded-full"
            style={{ background: action.badgeTone === 'brand' ? 'var(--brand)' : 'var(--destructive)' }}
          />
        )}
      </motion.button>
    );

    if (!hasSubActions) return pillButton;

    // Pill opens a DropdownMenu of its sub-actions (see HeaderPopoverPill).
    return (
      <HeaderPopoverPill key={action.id} action={action}>
        {pillButton}
      </HeaderPopoverPill>
    );
  };

  // Renders a leading-area action with one special-case: the project-list
  // search pill morphs in place into a wider input pill when search is
  // active. Same glass-surface BG so it visually feels like the pill
  // grew rather than swapping to a different element. Animation uses
  // framer-motion's `layout` on the wrapper so the width transition is
  // smooth, and AnimatePresence cross-fades the icon button vs. the
  // input contents.

  // On desktop the morph pills are replaced by a single persistent search
  // field at the centre of the desktop header. These IDs are
  // filtered out of leading/trailing on desktop only; mobile is untouched.

  // Returns the search config for whichever view is currently active, or
  // null when the active view has no search. Resolution order matters: BOQ
  // importer takes priority, then BOQ pricing, then flat views.
  const getActiveSearchConfig = (): {
    value: string;
    inputRef: React.RefObject<HTMLInputElement | null>;
    onChange: (change: HeaderSearchChange) => void;
    onClear: () => void;
    placeholder: string;
    ariaLabel: string;
    /** Keep the full field below the roomy breakpoint instead of collapsing
     *  to a magnifier — for views whose trailing cluster leaves the room. */
    alwaysExpanded?: boolean;
  } | null => {
    if (pageControls?.search) return {
      value: pageControls.search.value, inputRef: pageControlsSearchRef,
      onChange: (event) => dispatchPageHeaderControl(pageControls.id, { type: 'search', value: getHeaderSearchValue(event) }),
      onClear: () => dispatchPageHeaderControl(pageControls.id, { type: 'search', value: '' }),
      placeholder: pageControls.search.placeholder, ariaLabel: pageControls.search.label,
    };
    // Workspace documents sit under a Companies record or Profile, so no project tab names them.
    if (documentsHeaderState?.scope === 'workspace' && !documentPreviewHeaderState) {
      return {
        value: documentsSearchValue,
        inputRef: documentsSearchInputRef,
        onChange: handleDocumentsSearchChange,
        onClear: dismissDocumentsSearch,
        placeholder: 'Search documents…',
        ariaLabel: 'Search documents',
      };
    }
    if (projectDetailTab === 'takeoff' && takeoffHeaderState && !takeoffCanvasState && !takeoffHeaderState.uploaderOpen && !takeoffHeaderState.sheetDetailsOpen) {
      return { value: takeoffSearchValue, inputRef: takeoffSearchInputRef, onChange: changeTakeoffSearch, onClear: clearTakeoffSearch, placeholder: 'Search drawings…', ariaLabel: 'Search drawings' };
    }
    if (boqImporterState?.open) {
      if (boqImporterState.currentStep !== 'map-review') return null;
      return {
        value: boqImporterSearchValue,
        inputRef: boqImporterSearchInputRef,
        onChange: handleBoqImporterSearchChange,
        onClear: dismissBoqImporterSearch,
        placeholder: 'Search items, ref, unit…',
        ariaLabel: 'Search imported rows',
      };
    }
    if (isDesktopViewport && boqHeaderState && projectDetailTab === 'pricing') {
      return {
        value: boqSearchValue,
        inputRef: boqSearchInputRef,
        onChange: handleBoqSearchChange,
        onClear: dismissBoqSearch,
        placeholder: boqHeaderState.searchPlaceholder ?? 'Search items…',
        ariaLabel: 'Search items',
      };
    }
    if (
      currentView === 'system-settings'
      && templatesHeaderState?.mode === 'library'
    ) {
      return {
        value: templatesHeaderState.searchQuery,
        inputRef: templatesSearchInputRef,
        onChange: handleTemplatesSearchChange,
        onClear: clearTemplatesSearch,
        placeholder: templatesHeaderState.section === 'layouts'
          ? 'Search export layouts…'
          : templatesHeaderState.section === 'email'
            ? 'Search email templates…'
            : templatesHeaderState.section === 'notifications'
              ? 'Search notifications…'
              : 'Search document templates…',
        ariaLabel: 'Search templates',
        alwaysExpanded: true,
      };
    }
    if (
      currentView === 'project-detail'
      && projectDetailTab === 'procurement'
      && procurementHeaderState?.activeView === 'schedule'
    ) {
      return {
        value: procurementHeaderState.searchQuery,
        inputRef: procurementSearchInputRef,
        onChange: handleProcurementSearchChange,
        onClear: clearProcurementSearch,
        placeholder: 'Search schedule…',
        ariaLabel: 'Search procurement schedule',
        alwaysExpanded: true,
      };
    }
    // Keyed on the home publishing itself, not the tab: an open report has nothing to filter.
    if (currentView === 'project-detail' && projectDetailTab === 'reports' && reportHeaderState?.searchable) {
      return {
        value: reportsSearchValue,
        inputRef: reportsSearchInputRef,
        onChange: (event) => window.dispatchEvent(new CustomEvent('reports:query-change', { detail: { query: getHeaderSearchValue(event) } })),
        onClear: () => window.dispatchEvent(new CustomEvent('reports:query-change', { detail: { query: '' } })),
        placeholder: 'Search reports',
        ariaLabel: 'Search reports',
        alwaysExpanded: true,
      };
    }
    if (activeRfiRegister && activeRfiRegister.counts.all > 0) {
      return {
        value: rfiSearchValue,
        inputRef: rfiSearchInputRef,
        onChange: (event) => window.dispatchEvent(new CustomEvent(RFI_SET_QUERY_EVENT, {
          detail: { query: getHeaderSearchValue(event) },
        })),
        onClear: () => window.dispatchEvent(new CustomEvent(RFI_SET_QUERY_EVENT, { detail: { query: '' } })),
        placeholder: 'Search RFIs…',
        ariaLabel: 'Search RFIs',
        alwaysExpanded: true,
      };
    }
    if (projectDetailTab === 'team' && projectMembersHeaderState) {
      return {
        value: projectMembersSearchValue,
        inputRef: projectMembersSearchInputRef,
        onChange: handleProjectMembersSearchChange,
        onClear: dismissProjectMembersSearch,
        placeholder: projectMembersHeaderState.addOpen ? 'Search people to add…' : 'Search members…',
        ariaLabel: projectMembersHeaderState.addOpen ? 'Search people to add' : 'Search project members',
      };
    }
    if (currentView === 'project-detail' && projectDetailTab === 'email') {
      return {
        value: projectEmailSearchValue,
        inputRef: projectEmailSearchInputRef,
        onChange: handleProjectEmailSearchChange,
        onClear: dismissProjectEmailSearch,
        placeholder: 'Search project mail…',
        ariaLabel: 'Search project mail',
      };
    }
    if (currentView === 'project-detail' && projectDetailTab === 'activity' && activityHeaderState) {
      return {
        value: projectActivitySearchValue,
        inputRef: projectActivitySearchInputRef,
        onChange: handleProjectActivitySearchChange,
        onClear: dismissProjectActivitySearch,
        placeholder: 'Search activity…',
        ariaLabel: 'Search activity',
        // The timeline's search is its primary control and the cluster
        // beside it is four pills; a magnifier that has to be clicked first
        // was the wrong trade here.
        alwaysExpanded: true,
      };
    }
    if (currentView === 'pipeline') {
      return {
        value: pipelineSearchValue,
        inputRef: pipelineSearchInputRef,
        onChange: handlePipelineSearchChange,
        onClear: dismissPipelineSearch,
        placeholder: 'Search deals…',
        ariaLabel: 'Search deals',
      };
    }
    if (currentView === 'documents' || (documentsHeaderState && projectDetailTab === 'documents' && !documentPreviewHeaderState)) {
      return {
        value: documentsSearchValue,
        inputRef: documentsSearchInputRef,
        onChange: handleDocumentsSearchChange,
        onClear: dismissDocumentsSearch,
        placeholder: 'Search project documents…',
        ariaLabel: 'Search project documents',
      };
    }
    if (currentView === 'contacts') {
      return {
        value: contactsSearchValue,
        inputRef: contactsSearchInputRef,
        onChange: handleContactsSearchChange,
        onClear: dismissContactsSearch,
        placeholder: 'Search companies…',
        ariaLabel: 'Search companies',
      };
    }
    if (currentView === 'resource-library' || isWorkspaceResourceLibrary) {
      return {
        value: resourceLibrarySearchValue,
        inputRef: resourceLibrarySearchInputRef,
        onChange: handleResourceLibrarySearchChange,
        onClear: dismissResourceLibrarySearch,
        placeholder: 'Search',
        ariaLabel: 'Search rate items',
      };
    }
    // Keyed on the LIST being visible, not the route: a profile or the add form
    // owns the whole content area, and a persistent field over either of those
    // filters nothing.
    if (isEmployersList) {
      return {
        value: employersSearchValue,
        inputRef: employersSearchInputRef,
        onChange: handleEmployersSearchChange,
        onClear: dismissEmployersSearch,
        placeholder: 'Search employers…',
        ariaLabel: 'Search employers',
      };
    }
    // Same reasoning as isEmployersList above: a drilled-in machine owns the
    // whole content area, so the field only appears over the table.
    if (isPlantList) {
      return {
        value: plantSearchValue,
        inputRef: plantSearchInputRef,
        onChange: handlePlantSearchChange,
        onClear: dismissPlantSearch,
        placeholder: 'Search plant register…',
        ariaLabel: 'Search plant register',
      };
    }
    if (isWorkspaceInvoicesTab) {
      return {
        value: invoiceSearchValue,
        inputRef: invoiceSearchInputRef,
        onChange: handleInvoiceSearchChange,
        onClear: dismissInvoiceSearch,
        placeholder: 'Search invoices…',
        ariaLabel: 'Search invoices',
      };
    }
    if (isSalesInvoicesTab) {
      return {
        value: salesInvoiceSearchValue,
        inputRef: salesInvoiceSearchInputRef,
        onChange: handleSalesInvoiceSearchChange,
        onClear: dismissSalesInvoiceSearch,
        placeholder: 'Search invoices…',
        ariaLabel: 'Search invoices',
      };
    }
    if (isBotsListVisible) {
      return {
        value: botsSearchValue,
        inputRef: botsSearchInputRef,
        onChange: handleBotsSearchChange,
        onClear: clearBotsSearch,
        placeholder: 'Search AI agents…',
        ariaLabel: 'Search AI agents',
      };
    }
    if (isWorkspaceUsersTab) {
      return {
        value: usersSearchValue,
        inputRef: usersSearchInputRef,
        onChange: handleUsersSearchChange,
        onClear: clearUsersSearch,
        placeholder: 'Search users…',
        ariaLabel: 'Search users',
      };
    }
    if (isWorkspaceActivityTab) {
      return {
        value: workspaceActivitySearchValue,
        inputRef: workspacesSearchInputRef,
        onChange: handleWorkspaceActivitySearchChange,
        onClear: clearWorkspaceActivitySearch,
        placeholder: 'Search activity…',
        ariaLabel: 'Search workspace activity',
      };
    }
    if (currentView === 'workspaces') {
      return {
        value: workspacesSearchValue,
        inputRef: workspacesSearchInputRef,
        onChange: handleWorkspacesSearchChange,
        onClear: dismissWorkspacesSearch,
        placeholder: 'Search workspaces…',
        ariaLabel: 'Search workspaces',
      };
    }
    // Keyed off the page's published state, NOT a view id: Workflows and the
    // Trigger Catalog are sections of `system-settings`, so the `workflows`
    // view this once tested for does not exist and the field never rendered.
    if (workflowsHeaderState?.active && !workflowsHeaderState.editorOpen) {
      return {
        value: workflowsSearchValue,
        inputRef: workflowsSearchInputRef,
        onChange: handleWorkflowsSearchChange,
        onClear: dismissWorkflowsSearch,
        placeholder: 'Search workflows…',
        ariaLabel: 'Search workflows',
      };
    }
    if (triggerCatalogHeaderState?.active) {
      return {
        value: triggerCatalogSearchValue,
        inputRef: triggerCatalogSearchInputRef,
        onChange: handleTriggerCatalogSearchChange,
        onClear: dismissTriggerCatalogSearch,
        placeholder: 'Search triggers…',
        ariaLabel: 'Search triggers',
      };
    }
    if (currentView === 'messages' && messagesMembersState?.adding) {
      return {
        value: membersQuery,
        inputRef: membersSearchInputRef,
        onChange: handleMembersSearchChange,
        onClear: dismissMembersSearch,
        placeholder: 'Search people…',
        ariaLabel: 'Search people',
        alwaysExpanded: true,
      };
    }
    if (currentView === 'messages') {
      return {
        value: messagesSearchValue,
        inputRef: messagesSearchInputRef,
        onChange: handleMessagesSearchChange,
        onClear: dismissMessagesSearch,
        placeholder: 'Search messages…',
        ariaLabel: 'Search messages',
      };
    }
    if (currentView === 'email' && !nativeBridge.isNative) {
      return {
        value: emailSearchValue,
        inputRef: emailSearchInputRef,
        onChange: handleEmailSearchChange,
        onClear: clearEmailSearch,
        placeholder: 'Search mail…',
        ariaLabel: 'Search mail',
      };
    }
    if (currentView === 'projects') {
      return {
        value: projectsSearchValue,
        inputRef: projectsSearchInputRef,
        onChange: handleProjectsSearchChange,
        onClear: dismissProjectsSearch,
        placeholder: 'Search projects…',
        ariaLabel: 'Search projects',
      };
    }
    return null;
  };

  // Ensure the per-view *SearchOpen flag is true on desktop so the existing
  // search pipeline (which may gate on the open flag) runs when the user
  // types into the persistent field.
  useEffect(() => {
    if (!isDesktopViewport) return;
    if (boqImporterState?.open) {
      setBoqImporterSearchOpen(boqImporterState.currentStep === 'map-review');
      return;
    }
    if (boqHeaderState && projectDetailTab === 'pricing') {
      setBoqSearchOpen(true);
      return;
    }
    if (currentView === 'project-detail' && projectDetailTab === 'email') {
      setProjectEmailSearchOpen(true);
      return;
    }
    if (currentView === 'documents' || documentsHeaderState?.scope === 'workspace') { setDocumentsSearchOpen(true); return; }
    if (currentView === 'contacts') { setContactsSearchOpen(true); return; }
    if (currentView === 'resource-library' || isWorkspaceResourceLibrary) { setResourceLibrarySearchOpen(true); return; }
    // Keyed on the derived boolean, not on currentView, so it re-fires when the
    // form closes and the directory comes back — the reset effect that turned
    // the flag off keys on the same value, and guards that disagree about one
    // boolean leave it stuck false for the rest of the session.
    if (isEmployersList) { setEmployersSearchOpen(true); return; }
    if (currentView === 'workspaces') { setWorkspacesSearchOpen(true); return; }
    if (currentView === 'workflows') { setWorkflowsSearchOpen(true); return; }
    if (currentView === 'messages') { setMessagesSearchOpen(true); return; }
    if (currentView === 'projects') { setProjectsSearchOpen(true); return; }
  }, [
    isDesktopViewport,
    currentView,
    projectDetailTab,
    boqImporterState?.open,
    boqImporterState?.currentStep,
    // presence-only — re-run when BOQ header appears/disappears, not on every value change
    !!boqHeaderState,
    isEmployersList,
    isWorkspaceResourceLibrary,
    documentsHeaderState?.scope,
  ]);

  // Every desktop view searches permitted records while retaining its own query controls.
  const renderGlobalSearchField = () => {
    if (!isDesktopViewport) return null;
    const scopedConfig = getActiveSearchConfig();
    const cfg = scopedConfig ?? {
      value: navigationSearch.scope === navigationSearchScope ? navigationSearch.value : '',
      inputRef: navigationSearchInputRef,
      onChange: (change: HeaderSearchChange) => setNavigationSearch({ scope: navigationSearchScope, value: getHeaderSearchValue(change) }),
      onClear: () => setNavigationSearch({ scope: navigationSearchScope, value: '' }),
      placeholder: 'Search Quantis…',
      ariaLabel: 'Search Quantis',
    };
    const destinations = scopedConfig ? undefined : [
      ...(onNavigate ? recentSearchMainItems.map(item => {
        const Icon = item.icon ?? Search;
        return { id: `main:${item.id}`, label: item.label, icon: <Icon className="h-4 w-4" />,
          onSelect: () => onNavigate(item.id) };
      }) : []),
      ...(isProjectDetailView && recentSearchMainIds.includes('projects') && onProjectTabChange ? projectTabItems.map(item => {
        const Icon = item.icon ?? Search;
        return { id: `project:${item.id}`, label: item.label, description: projectTabProps?.projectName,
          icon: <Icon className="h-4 w-4" />, onSelect: () => onProjectTabChange(item.id) };
      }) : []),
    ];
    const quickFilters: DesktopHeaderSearchFilter[] = (scopedConfig ? pageControls?.filters.map(filter => ({
      ...filter,
      onValueChange: (value: string) => dispatchPageHeaderControl(pageControls.id, { type: 'filter', id: filter.id, value }),
    })) : undefined) ?? [];
    if (cfg.ariaLabel === 'Search drawings' && takeoffHeaderState?.gallery) {
      quickFilters.push({
        id: 'takeoff-status', label: 'Drawing status', value: takeoffHeaderState.gallery.status,
        options: GALLERY_FILTERS.map(([value, label]) => ({ value, label })),
        onValueChange: value => window.dispatchEvent(new CustomEvent('takeoff:gallery-change', { detail: { field: 'status', value } })),
      });
    }
    // Add members: the field only filters the people on the page, so no
    // QuantisAI Ask row, no global results and no recent places.
    if (currentView === 'messages' && messagesMembersState?.adding) {
      return <DesktopHeaderSearch
        scopeKey="messages-members"
        scopeLabel="People"
        value={cfg.value}
        inputRef={cfg.inputRef}
        onValueChange={value => cfg.onChange(value)}
        onClear={cfg.onClear}
        placeholder={cfg.placeholder}
        ariaLabel={cfg.ariaLabel}
        searchScope="messages-members"
        filters={[]}
      />;
    }
    return <GlobalDesktopHeaderSearch
      key={searchHistoryOwner}
      actorId={user?.id}
      workspaceId={activeWorkspaceId}
      scopeKey={JSON.stringify([searchHistoryOwner, currentView, projectTabProps?.projectId, projectDetailTab, cfg.ariaLabel, pageControls?.id])}
      scopeLabel="Projects and tasks"
      value={cfg.value}
      inputRef={cfg.inputRef}
      onValueChange={value => cfg.onChange(value)}
      onClear={cfg.onClear}
      placeholder="Search Quantis…"
      ariaLabel="Search Quantis"
      searchScope="global"
      filters={quickFilters}
      destinations={destinations}
      recentPlaces={recentSearchPlaces.map(place => {
        const item = place.route.projectId ? projectTabItems.find(item => item.id === place.route.tab)
          : recentSearchMainItems.find(item => item.id === place.route.view);
        const Icon = item?.icon ?? Search;
        return { id: place.id, label: item?.label ?? place.label, description: place.description,
          icon: <Icon className="h-4 w-4" />,
          onSelect: () => place.route.projectId ? onProjectTabChange?.(place.route.tab) : onNavigate?.(place.route.view) };
      })}
    />;
  };

  // Shared morph renderer for any "search-pill expands into input"
  // pattern. Used by both `projects-search` and `messages-search` so the
  // animation is identical and lives in one place. Adding a third
  // morphing search elsewhere is `id-config` away.
  //
  // Mobile / tablet (< 1024px): when open, the pill expands to fill the
  // remainder of the header row (Apple HIG search-presentation pattern —
  // Mail, Notes, Files take the entire nav bar over for search). The other
  // leading/trailing pills are hidden via the `searchActiveOnMobile` flag
  // on the main row so the search input claims the full width and the
  // user gets the keyboard right under their thumb.
  const renderSearchMorphPill = (
    action: MobileHeaderAction,
    config: {
      isOpen: boolean;
      value: string;
      inputRef: React.RefObject<HTMLInputElement | null>;
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
      onDismiss: () => void;
      placeholder: string;
      ariaLabel: string;
      /** When true, the in-input affordance is split: a mic glyph when
       *  the field is empty (decorative SF Symbols stand-in), an X that
       *  clears the value (without dismissing) when there's text. The
       *  caller is expected to render an external Cancel button. iOS
       *  Files / Mail use this layout. Defaults to false (legacy:
       *  internal X dismisses entirely). */
      splitDismiss?: boolean;
      /** Voice dictation wiring. When provided the mic glyph becomes a
       *  live button; omit for features that don't use dictation. */
      onMicClick?: () => void;
      isMicListening?: boolean;
      isMicSupported?: boolean;
      /** Override the closed-state icon. Defaults to the Search glyph.
       *  Use when the pill's hit-target should show a different affordance
       *  (e.g. SquarePen for the compose morph). */
      closedIcon?: React.ComponentType<{ style?: React.CSSProperties; strokeWidth?: number | string }>;
    },
  ) => (
    <motion.div
      key={action.id}
      layout
      animate={{
        width: config.isOpen
          ? (isDesktopViewport ? 240 : '100%')
          : 44,
      }}
      transition={{ duration: 0.25, ease: [0.32, 0.72, 0, 1] }}
      className={cn(
        'relative h-11',
        // On mobile, the wrapper grows to fill its parent (the leading
        // cluster, which becomes flex-1 while search is active). 100%
        // width then makes the input span every pixel up to the trailing
        // pills, irrespective of how many trailing pills are present.
        config.isOpen && !isDesktopViewport ? 'flex-1 min-w-0' : 'flex-shrink-0',
      )}
      style={{ pointerEvents: 'auto', maxWidth: '100%' }}
    >
      <span
        aria-hidden="true"
        className="pointer-events-none"
        // Same recipe as `renderMobileHeaderAction` — see comments
        // there. Dark-mode swap goes through `--header-pill-fill` /
        // `--header-pill-blend` in `theme.css`.
        style={{
          position: 'absolute',
          left: 4,
          right: 4,
          height: 36,
          top: 'calc(50% - 18px)',
          zIndex: 0,
          borderRadius: 1000,
          background: 'var(--header-pill-fill)',
          mixBlendMode: 'var(--header-pill-blend)' as any,
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          boxShadow: '0px 8px 24px 0px rgba(0,0,0,0.08), var(--glass-inset, none)',
        }}
      />
      {/* Both the icon-button and the input are ALWAYS mounted so the
          icon's click handler can synchronously focus the input ref —
          the only way iOS WebKit will accept programmatic focus, since
          it must happen inside the user-gesture call stack. The two
          children are crossfaded via opacity rather than mount/unmount,
          so the ref never goes null. */}
      <div className="relative z-[1] flex h-full items-center" style={{ width: '100%' }}>
        <Search
          aria-hidden="true"
          style={{
            width: 16,
            height: 16,
            position: 'absolute',
            left: 14,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--muted-foreground)',
            opacity: config.isOpen ? 1 : 0,
            transition: 'opacity 0.15s',
            pointerEvents: 'none',
            zIndex: 2,
          }}
          strokeWidth={2.25}
        />
        <input
          ref={config.inputRef as React.RefObject<HTMLInputElement>}
          value={config.value}
          onChange={config.onChange}
          onKeyDown={(e) => {
            if (e.key === 'Escape') config.onDismiss();
          }}
          placeholder={config.placeholder}
          aria-label={config.ariaLabel}
          tabIndex={config.isOpen ? 0 : -1}
          className="absolute inset-0 bg-transparent border-0 outline-none font-apple text-foreground placeholder:text-muted-foreground"
          style={{
            paddingLeft: 38,
            paddingRight: 36,
            fontSize: 14,
            height: 44,
            minWidth: 0,
            opacity: config.isOpen ? 1 : 0,
            pointerEvents: config.isOpen ? 'auto' : 'none',
            transition: 'opacity 0.15s',
            zIndex: 2,
          }}
        />
        {/* In-input affordance. Two modes:
              - splitDismiss=false (default): X always shown when open,
                click dismisses search entirely.
              - splitDismiss=true: mic glyph when empty (decorative,
                SF Symbols stand-in), X when value present that ONLY
                clears text. External Cancel button handles dismissal. */}
        {config.splitDismiss ? (
          config.value ? (
            <button
              type="button"
              onClick={() => {
                // Synthesize a change event to clear via the same path
                // typing uses, so any debounced consumers stay in sync.
                const target = (config.inputRef as React.RefObject<HTMLInputElement>).current;
                if (target) {
                  const proto = window.HTMLInputElement.prototype;
                  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
                  setter?.call(target, '');
                  target.dispatchEvent(new Event('input', { bubbles: true }));
                }
                // Belt + braces — also fire the React-friendly change
                // path in case dispatchEvent doesn't trip the bound
                // onChange (some test setups).
                config.onChange({
                  target: { value: '' },
                } as unknown as React.ChangeEvent<HTMLInputElement>);
                target?.focus();
              }}
              aria-label="Clear search"
              tabIndex={config.isOpen ? 0 : -1}
              className="absolute top-1/2 -translate-y-1/2 flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
              style={{
                right: 6,
                opacity: config.isOpen ? 1 : 0,
                pointerEvents: config.isOpen ? 'auto' : 'none',
                transition: 'opacity 0.15s',
                zIndex: 3,
              }}
            >
              <X style={{ width: 14, height: 14 }} strokeWidth={2.5} />
            </button>
          ) : config.onMicClick && config.isMicSupported ? (
            <button
              type="button"
              aria-label="Search by voice"
              tabIndex={config.isOpen ? 0 : -1}
              onClick={config.onMicClick}
              className={cn(
                'absolute top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center',
                config.isMicListening ? 'text-brand' : 'text-foreground'
              )}
              style={{
                right: 0,
                opacity: config.isOpen ? 1 : 0,
                pointerEvents: config.isOpen ? 'auto' : 'none',
                transition: 'opacity 0.15s',
                zIndex: 3,
              }}
            >
              <svg width="16" height="20" viewBox="0 0 16 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M8 1.25c-1.66 0-3 1.34-3 3v4.5c0 1.66 1.34 3 3 3s3-1.34 3-3v-4.5c0-1.66-1.34-3-3-3zM3.5 8.25a.9.9 0 0 0-1.8 0 6.3 6.3 0 0 0 5.4 6.24V16H5.4a.9.9 0 0 0 0 1.8h5.2a.9.9 0 0 0 0-1.8H8.9v-1.51a6.3 6.3 0 0 0 5.4-6.24.9.9 0 0 0-1.8 0 4.5 4.5 0 0 1-9 0z" />
              </svg>
            </button>
          ) : (
            <span
              aria-hidden="true"
              className="absolute top-1/2 -translate-y-1/2 flex h-7 w-7 items-center justify-center text-foreground"
              style={{
                right: 6,
                opacity: config.isOpen ? 1 : 0,
                transition: 'opacity 0.15s',
                pointerEvents: 'none',
                zIndex: 3,
              }}
            >
              <svg width="16" height="20" viewBox="0 0 16 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M8 1.25c-1.66 0-3 1.34-3 3v4.5c0 1.66 1.34 3 3 3s3-1.34 3-3v-4.5c0-1.66-1.34-3-3-3zM3.5 8.25a.9.9 0 0 0-1.8 0 6.3 6.3 0 0 0 5.4 6.24V16H5.4a.9.9 0 0 0 0 1.8h5.2a.9.9 0 0 0 0-1.8H8.9v-1.51a6.3 6.3 0 0 0 5.4-6.24.9.9 0 0 0-1.8 0 4.5 4.5 0 0 1-9 0z" />
              </svg>
            </span>
          )
        ) : (
          <button
            type="button"
            onClick={config.onDismiss}
            aria-label="Close search"
            tabIndex={config.isOpen ? 0 : -1}
            className="absolute top-1/2 -translate-y-1/2 flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
            style={{
              right: 6,
              opacity: config.isOpen ? 1 : 0,
              pointerEvents: config.isOpen ? 'auto' : 'none',
              transition: 'opacity 0.15s',
              zIndex: 3,
            }}
          >
            <X style={{ width: 14, height: 14 }} strokeWidth={2.5} />
          </button>
        )}
        <button
          type="button"
          onClick={(event) => {
            // Focus FIRST, synchronously, while we're still in the user
            // gesture context iOS WebKit requires. Then dispatch the
            // toggle so the icon fades out and the input fades in.
            (config.inputRef as React.RefObject<HTMLInputElement>).current?.focus();
            action.onClick?.(event);
          }}
          aria-label={action.ariaLabel}
          tabIndex={config.isOpen ? -1 : 0}
          className="absolute inset-0 flex items-center justify-center active:opacity-75"
          style={{
            opacity: config.isOpen ? 0 : 1,
            pointerEvents: config.isOpen ? 'none' : 'auto',
            transition: 'opacity 0.15s',
            zIndex: 1,
          }}
        >
          {config.closedIcon
            ? <config.closedIcon style={{ width: 17, height: 17, color: 'var(--foreground)' }} strokeWidth={2.25} />
            : <Search style={{ width: 17, height: 17, color: 'var(--foreground)' }} strokeWidth={2.25} />
          }
        </button>
      </div>
    </motion.div>
  );

  const renderLeadingAction = (action: MobileHeaderAction) => {
    if (action.id === 'page-controls-search' && pageControls?.search && usesMaterialMobileChrome && !pageControlsSearchOpen) return renderMobileHeaderAction(action);
    if (action.id === 'page-controls-search' && pageControls?.search) return renderSearchMorphPill(action, {
      isOpen: pageControlsSearchOpen, value: pageControls.search.value, inputRef: pageControlsSearchRef,
      onChange: (event) => dispatchPageHeaderControl(pageControls.id, { type: 'search', value: event.target.value }),
      onDismiss: () => { setPageControlsSearchOpen(false); dispatchPageHeaderControl(pageControls.id, { type: 'search', value: '' }); },
      placeholder: pageControls.search.placeholder, ariaLabel: pageControls.search.label,
    });
    if (action.id === 'takeoff-gallery-search') return renderSearchMorphPill(action, {
      isOpen: takeoffSearchOpen, value: takeoffSearchValue, inputRef: takeoffSearchInputRef,
      onChange: changeTakeoffSearch, onDismiss: clearTakeoffSearch, placeholder: 'Search drawings…', ariaLabel: 'Search drawings',
    });
    if (action.id === 'contacts-search' && currentView === 'contacts') {
      return renderSearchMorphPill(action, {
        isOpen: contactsSearchOpen,
        value: contactsSearchValue,
        inputRef: contactsSearchInputRef,
        onChange: handleContactsSearchChange,
        onDismiss: dismissContactsSearch,
        placeholder: 'Search companies…',
        ariaLabel: 'Search companies',
      });
    }
    if (action.id === 'resource-library-search' && (currentView === 'resource-library' || isWorkspaceResourceLibrary)) {
      return renderSearchMorphPill(action, {
        isOpen: resourceLibrarySearchOpen,
        value: resourceLibrarySearchValue,
        inputRef: resourceLibrarySearchInputRef,
        onChange: handleResourceLibrarySearchChange,
        onDismiss: dismissResourceLibrarySearch,
        placeholder: 'Search',
        ariaLabel: action.ariaLabel,
      });
    }
    // The Site Register's day pill: the standard pill, with a calendar under
    // it, since a month grid cannot be a flat subActions menu.
    if (action.id === SITE_REGISTER_DAY_PILL_ID && siteRegisterHeaderState) {
      return (
        <SiteRegisterDayPicker
          key={action.id}
          dayKey={siteRegisterHeaderState.dayKey}
          todayKey={siteRegisterHeaderState.todayKey}
          onSelectDay={requestSiteRegisterDay}
          renderTrigger={(toggle) => renderMobileHeaderAction({ ...action, onClick: toggle })}
        />
      );
    }
    // Merchant category + brand are too long for a flat subActions menu, so
    // this pill owns its own panel of native selects.
    if (
      action.id === 'resource-library-merchant-filter' &&
      resourceLibraryHeaderState?.kind === 'merchant'
    ) {
      return (
        <MerchantFilterPill
          key={action.id}
          action={action}
          state={resourceLibraryHeaderState}
        />
      );
    }
    if (action.id === 'workspace-bots-search' && isBotsListVisible) {
      return renderSearchMorphPill(action, {
        isOpen: botsSearchOpen,
        value: botsSearchValue,
        inputRef: botsSearchInputRef,
        onChange: handleBotsSearchChange,
        onDismiss: dismissBotsSearch,
        placeholder: 'Search AI agents…',
        ariaLabel: 'Search AI agents',
      });
    }
    if (action.id === 'workspaces-search' && currentView === 'workspaces') {
      // Mobile counterpart of the getActiveSearchConfig retarget above. Shares
      // workspacesSearchInputRef because the toggle handler focuses that ref
      // and only one of these two variants is ever mounted.
      if (isWorkspaceUsersTab) {
        return renderSearchMorphPill(action, {
          isOpen: workspacesSearchOpen,
          value: usersSearchValue,
          inputRef: workspacesSearchInputRef,
          onChange: handleUsersSearchChange,
          onDismiss: dismissUsersSearchPill,
          placeholder: 'Search users…',
          ariaLabel: 'Search users',
        });
      }
      if (isWorkspaceActivityTab) {
        return renderSearchMorphPill(action, {
          isOpen: workspacesSearchOpen,
          value: workspaceActivitySearchValue,
          inputRef: workspacesSearchInputRef,
          onChange: handleWorkspaceActivitySearchChange,
          onDismiss: dismissWorkspaceActivitySearch,
          placeholder: 'Search activity…',
          ariaLabel: 'Search workspace activity',
        });
      }
      return renderSearchMorphPill(action, {
        isOpen: workspacesSearchOpen,
        value: workspacesSearchValue,
        inputRef: workspacesSearchInputRef,
        onChange: handleWorkspacesSearchChange,
        onDismiss: dismissWorkspacesSearch,
        placeholder: 'Search workspaces…',
        ariaLabel: 'Search workspaces',
      });
    }
    // Gated on the same derived boolean the builder pushes the pill behind. If
    // the two ever disagree the fallthrough draws a plain glass magnifier that
    // toggles a morph with no input behind it.
    if (action.id === 'employers-search' && isEmployersList) {
      return renderSearchMorphPill(action, {
        isOpen: employersSearchOpen,
        value: employersSearchValue,
        inputRef: employersSearchInputRef,
        onChange: handleEmployersSearchChange,
        onDismiss: dismissEmployersSearch,
        placeholder: 'Search employers…',
        ariaLabel: 'Search employers',
      });
    }
    if (action.id === 'plant-search' && isPlantList) {
      return renderSearchMorphPill(action, {
        isOpen: plantSearchOpen,
        value: plantSearchValue,
        inputRef: plantSearchInputRef,
        onChange: handlePlantSearchChange,
        onDismiss: dismissPlantSearch,
        placeholder: 'Search plant register…',
        ariaLabel: 'Search plant register',
      });
    }
    if (action.id === 'workflows-search') {
      return renderSearchMorphPill(action, {
        isOpen: workflowsSearchOpen,
        value: workflowsSearchValue,
        inputRef: workflowsSearchInputRef,
        onChange: handleWorkflowsSearchChange,
        onDismiss: dismissWorkflowsSearch,
        placeholder: 'Search workflows…',
        ariaLabel: 'Search workflows',
      });
    }
    if (action.id === 'trigger-catalog-search') {
      return renderSearchMorphPill(action, {
        isOpen: triggerCatalogSearchOpen,
        value: triggerCatalogSearchValue,
        inputRef: triggerCatalogSearchInputRef,
        onChange: handleTriggerCatalogSearchChange,
        onDismiss: dismissTriggerCatalogSearch,
        placeholder: 'Search triggers…',
        ariaLabel: 'Search triggers',
      });
    }
    if (action.id === 'projects-search' && currentView === 'projects') {
      // On mobile the search icon button is replaced by a persistent search
      // bar rendered directly below the header (see JSX below). The morph
      // pill only lives on desktop where `projects-search` is already in
      // DESKTOP_GLOBAL_SEARCH_IDS and would be filtered from leading actions
      // anyway — this guard is belt-and-braces for the mobile path.
      if (!isDesktopViewport) return null;
      return renderSearchMorphPill(action, {
        isOpen: projectsSearchOpen,
        value: projectsSearchValue,
        inputRef: projectsSearchInputRef,
        onChange: handleProjectsSearchChange,
        onDismiss: dismissProjectsSearch,
        placeholder: 'Search projects…',
        ariaLabel: 'Search projects',
      });
    }
    if (action.id === 'project-notes-search' && (projectDetailTab === 'notes' || projectDetailTab === 'tasks')) {
      if (!isDesktopViewport) return null;
      return renderSearchMorphPill(action, {
        isOpen: projectNotesSearchOpen,
        value: projectNotesSearchValue,
        inputRef: projectNotesSearchInputRef,
        onChange: handleProjectNotesSearchChange,
        onDismiss: dismissProjectNotesSearch,
        placeholder: 'Search notes…',
        ariaLabel: 'Search notes',
      });
    }
    if (action.id === 'project-activity-search' && projectDetailTab === 'activity') {
      if (!isDesktopViewport) return null;
      return renderSearchMorphPill(action, {
        isOpen: projectActivitySearchOpen,
        value: projectActivitySearchValue,
        inputRef: projectActivitySearchInputRef,
        onChange: handleProjectActivitySearchChange,
        onDismiss: dismissProjectActivitySearch,
        placeholder: 'Search activity…',
        ariaLabel: 'Search activity',
      });
    }
    if (action.id === 'project-members-search' && projectDetailTab === 'team') {
      if (!isDesktopViewport) return null;
      return renderSearchMorphPill(action, {
        isOpen: projectMembersSearchOpen,
        value: projectMembersSearchValue,
        inputRef: projectMembersSearchInputRef,
        onChange: handleProjectMembersSearchChange,
        onDismiss: dismissProjectMembersSearch,
        placeholder: projectMembersHeaderState?.addOpen ? 'Search people to add…' : 'Search members…',
        ariaLabel: projectMembersHeaderState?.addOpen ? 'Search people to add' : 'Search project members',
      });
    }
    if (action.id === 'messages-members-search' && currentView === 'messages') {
      if (isDesktopViewport) return null;
      return renderSearchMorphPill(action, {
        isOpen: membersSearchOpen,
        value: membersQuery,
        inputRef: membersSearchInputRef,
        onChange: handleMembersSearchChange,
        onDismiss: dismissMembersSearch,
        placeholder: 'Search people…',
        ariaLabel: 'Search people',
      });
    }
    if (action.id === 'messages-search' && currentView === 'messages') {
      // On mobile the search icon is replaced by a persistent search bar rendered
      // at the top of the ChatListPanel scroll content (mirrors the Projects pattern).
      // The morph pill only lives on desktop where it's already in
      // DESKTOP_GLOBAL_SEARCH_IDS and filtered from leading actions there.
      if (!isDesktopViewport) return null;
      return renderSearchMorphPill(action, {
        isOpen: messagesSearchOpen,
        value: messagesSearchValue,
        inputRef: messagesSearchInputRef,
        onChange: handleMessagesSearchChange,
        onDismiss: dismissMessagesSearch,
        placeholder: 'Search messages…',
        ariaLabel: 'Search messages',
      });
    }
    if (action.id === 'messages-new' && currentView === 'messages') {
      return renderSearchMorphPill(action, {
        isOpen: messagesComposeOpen,
        value: messagesComposeValue,
        inputRef: messagesComposeInputRef,
        onChange: handleMessagesComposeChange,
        onDismiss: dismissMessagesCompose,
        placeholder: 'To: name or project',
        ariaLabel: 'New message to a person or project',
        splitDismiss: true,
        closedIcon: SquarePen,
      });
    }
    if (action.id === 'boq-search') {
      return renderSearchMorphPill(action, {
        isOpen: boqSearchOpen,
        value: boqSearchValue,
        inputRef: boqSearchInputRef,
        onChange: handleBoqSearchChange,
        onDismiss: dismissBoqSearch,
        placeholder: boqHeaderState?.searchPlaceholder ?? 'Search items…',
        ariaLabel: 'Search items',
      });
    }
    if (action.id === 'boq-importer-search' && boqImporterState?.open) {
      return renderSearchMorphPill(action, {
        isOpen: boqImporterSearchOpen,
        value: boqImporterSearchValue,
        inputRef: boqImporterSearchInputRef,
        onChange: handleBoqImporterSearchChange,
        onDismiss: dismissBoqImporterSearch,
        placeholder: 'Search items, ref, unit…',
        ariaLabel: 'Search imported rows',
      });
    }
    if (action.id === 'documents-search') {
      // Documents search stays in the main header on every viewport.
      // Mobile uses split dismiss; desktop keeps the single-X morph.
      return renderSearchMorphPill(action, {
        isOpen: documentsSearchOpen,
        value: documentsSearchValue,
        inputRef: documentsSearchInputRef,
        onChange: handleDocumentsSearchChange,
        onDismiss: dismissDocumentsSearch,
        placeholder: 'Search project documents…',
        ariaLabel: 'Search project documents',
        splitDismiss: !isDesktopViewport,
        onMicClick: documentsDictation.start,
        isMicListening: documentsDictation.isListening,
        isMicSupported: documentsDictation.isSupported,
      });
    }
    // Bills / view switcher — a real native <select> (OS-drawn popup), not a
    // hand-styled Radix menu. Bills and roll-up views as grouped options; the
    // closed control shows the active view, so it doubles as the title context.
    if (action.id === 'boq-view' && boqHeaderState) {
      const viewBills = boqHeaderState.bills ?? [];
      const viewTabs = boqHeaderState.summaryTabs ?? [];
      return (
        <HeaderPillSelect
          key={action.id}
          value={boqHeaderState.activeBillId ?? ''}
          ariaLabel="Bill or view"
          onChange={(billId) =>
            window.dispatchEvent(new CustomEvent('boq:select-bill', { detail: { billId } }))
          }
        >
          {viewBills.length > 0 && (
            <optgroup label="Bills">
              {viewBills.map((b) => (
                <option key={b.id} value={b.id}>{b.label}</option>
              ))}
            </optgroup>
          )}
          {viewTabs.length > 0 && (
            <optgroup label="Views">
              {viewTabs.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </optgroup>
          )}
        </HeaderPillSelect>
      );
    }
    // Invoices sub-tab status filter — native <select> pill so it reads as
    // part of the header chrome rather than a dropdown menu, matching the
    // in-page Select it replaces.
    if (action.id === 'workspace-invoices-filter' && isWorkspaceInvoicesTab) {
      return (
        <HeaderPillSelect
          key={action.id}
          value={invoiceStatusFilter}
          ariaLabel="Filter invoices by status"
          onChange={(status) => {
            setInvoiceStatusFilter(status);
            window.dispatchEvent(new CustomEvent('workspace-invoices:filter', { detail: { status } }));
          }}
        >
          {INVOICE_STATUS_FILTER_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </HeaderPillSelect>
      );
    }
    // Sales hub Invoices section status filter — same treatment as the
    // workspace-detail one above, over the same window events.
    if (action.id === 'sales-invoices-filter' && isSalesInvoicesTab) {
      return (
        <HeaderPillSelect
          key={action.id}
          value={salesInvoiceStatusFilter}
          ariaLabel="Filter invoices by status"
          onChange={(status) => {
            setSalesInvoiceStatusFilter(status);
            window.dispatchEvent(new CustomEvent('workspace-invoices:filter', { detail: { status } }));
          }}
        >
          {INVOICE_STATUS_FILTER_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </HeaderPillSelect>
      );
    }
    if (action.id === 'workspace-users-filter' && isWorkspaceUsersTab) {
      return (
        <HeaderPillSelect
          key={action.id}
          value={usersRoleFilter}
          ariaLabel="Filter users by role"
          icon={SlidersHorizontal}
          variant="toolbar"
          onChange={(role) => {
            setUsersRoleFilter(role);
            window.dispatchEvent(new CustomEvent(WORKSPACE_USERS_FILTER_EVENT, { detail: { role } }));
          }}
        >
          {USER_ROLE_FILTER_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </HeaderPillSelect>
      );
    }
    if (action.id === 'workspace-activity-filter' && isWorkspaceActivityTab) {
      const selectedCategory = workspaceActivityHeaderState?.selectedCategory ?? 'all';
      return (
        <HeaderPillSelect
          key={action.id}
          value={selectedCategory}
          ariaLabel="Filter workspace activity"
          icon={SlidersHorizontal}
          variant="toolbar"
          onChange={(category) => window.dispatchEvent(new CustomEvent(
            WORKSPACE_ACTIVITY_FILTER_EVENT,
            { detail: { category } },
          ))}
        >
          {WORKSPACE_ACTIVITY_CATEGORY_OPTIONS.map(option => (
            <option key={option.id} value={option.id}>{option.label}</option>
          ))}
        </HeaderPillSelect>
      );
    }
    // Employers status filter — native <select> pill. This also gives the filter a visible
    // active state: the in-page dropdown it replaces showed no checked marker,
    // so once open the user could not see which status was applied.
    if (action.id === 'employers-filter' && isEmployersList) {
      return (
        <HeaderPillSelect
          key={action.id}
          value={employersStatusFilter}
          ariaLabel="Filter employers by status"
          onChange={(status) => {
            setEmployersStatusFilter(status as EmployerStatusFilter);
            window.dispatchEvent(new CustomEvent(EMPLOYERS_FILTER_EVENT, { detail: { status } }));
          }}
        >
          {EMPLOYER_STATUS_FILTER_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </HeaderPillSelect>
      );
    }
    // Plant register status filter — same native <select> treatment as the
    // three filters above.
    if (action.id === 'plant-filter' && isPlantList) {
      return (
        <HeaderPillSelect
          key={action.id}
          value={plantStatusFilter}
          ariaLabel="Filter plant register by status"
          onChange={(status) => {
            setPlantStatusFilter(status as PlantStatusFilter);
            window.dispatchEvent(new CustomEvent(PLANT_FILTER_EVENT, { detail: { status } }));
          }}
        >
          {plantFilterOptions(plantExamDueCount).map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </HeaderPillSelect>
      );
    }
    return renderMobileHeaderAction(action);
  };

  const desktopSearchAvailable = isDesktopViewport;
  // Where the history arrows show, a page's own Back (Back to projects, Back to pricing) rides the
  // Back arrow instead of a second chevron beside the brand.
  const desktopHistoryShown = desktopSearchAvailable && !isNativeShell();

  const desktopTrailingActions = mobileHeaderConfig.trailingActions.filter(
    (action) => action.id !== 'notifications'
      && !DESKTOP_GLOBAL_SEARCH_IDS.has(action.id)
      && !(currentView === 'projects' && action.id === 'projects-collapse-all'),
  );
  const desktopQuantisAIAction: MobileHeaderAction = {
    id: 'quantis-ai',
    icon: Sparkles,
    ariaLabel: 'QuantisAI',
    onClick: onToggleSI ?? si.toggle,
    expanded: si.open,
    group: 'app',
  };
  const desktopPickerWidth = desktopSearchLayout.viewportWidth < 1280 ? 128 : 160;
  // Keep a usable centred field and app controls; lower-priority page actions
  // use the same overflow sheet as the compact header when the window narrows.
  let desktopReservedWidth = shouldRenderHeaderActions ? (desktopSearchAvailable ? 164 : 216) : 0;
  const desktopAdjacentAIWidth = shouldRenderHeaderActions && desktopSearchAvailable ? 52 : 0;
  if (currentView === 'messages' && messagesConversationState) desktopReservedWidth += 120;
  if (currentView === 'add-project' && addProjectEmployerState.available) desktopReservedWidth += 52;
  if (boqHeaderState && boqHeaderState.selectedCount > 0) desktopReservedWidth += 144;
  if (boqApprovalPill) desktopReservedWidth += 120;
  const desktopTrailingFit = desktopSearchAvailable
    ? fitDesktopHeaderActions(desktopTrailingActions, desktopSearchLayout.viewportWidth / 2 - 120 - desktopAdjacentAIWidth - desktopReservedWidth, TRAILING_INTERCEPT_IDS, desktopSearchLayout.viewportWidth < 1280, desktopPickerWidth)
    : { visible: desktopTrailingActions, collapsed: [] };
  const overflowSheetActions = overflowSheet === 'leading'
    ? mobileRowFit.leadingFit.collapsed
    : overflowSheet === 'trailing'
      ? mobileRowFit.trailingFit.collapsed
      : overflowSheet === 'desktop'
        ? desktopTrailingFit.collapsed
        : [];

  return (
    <Fragment>
    <header
      data-slot="unified-header"
      data-mobile-interface={usesMaterialMobileChrome ? 'material' : undefined}
      className="w-full flex flex-col font-apple"
      style={{
        background: usesMaterialMobileChrome
          ? 'var(--header-surface-bg)'
          : usesFloatingMobileDashboardHeader
            ? 'transparent'
            : 'var(--header-surface-bg)',
        // Material elevation belongs on AppLayout's outer header so it wraps
        // the iOS safe area and toolbar as one surface. A shadow here starts
        // below the safe area and creates a visible hairline between them.
        boxShadow: undefined,
      }}
    >
      {/* SVG goo filter — applied to the bell↔close + messages cluster
          so the entering and exiting pills blend into the messages pill
          like two liquid blobs joining and separating. Lives once in the
          header tree and is referenced by `filter: url(#qs-header-goo)`
          on the merge zone wrapper. */}
      <svg aria-hidden="true" focusable="false" width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <filter id="qs-header-goo">
            <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="blur" />
            {/* Alpha matrix tuned for low-opacity glass pills. With the
                Apple `systemFill` opacities (≈ 0.20 alpha) the pills sit
                at the same visual weight as the rest of the chrome —
                much lower than typical goo recipes (`18 -7` thresholds
                at A=0.39, which clips them out). `18 -2` thresholds at
                A≈0.11 instead, so 0.20 alpha pills stay solid through
                the merge while the gaussian blur edges still composite
                into the goo blob effect. */}
            <feColorMatrix
              in="blur"
              mode="matrix"
              values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -2"
              result="goo"
            />
            <feComposite in="SourceGraphic" in2="goo" operator="atop" />
          </filter>
        </defs>
      </svg>
      {/* ── Main row ─────────────────────────────────────────────────── */}
      <HeaderOverflowSheet
        open={overflowSheet !== null}
        onOpenChange={(open) => setOverflowSheet(open ? overflowSheet : null)}
        title="More actions"
        items={flattenOverflowActions(overflowSheetActions)}
      />

      {shouldRenderMainRow && (
        !isDesktopViewport ? (
          <>
          {/* ── Mobile: Figma node 193:2557 — Toolbar - Top (iPhone) ──
              When ANY morph search is open, the row goes into HIG
              search-presentation mode: title + trailing pills hide so the
              search input claims the full width and the keyboard rises
              right under it (Mail / Notes / Files pattern). */}
          <section
            ref={mainRowRef}
            data-slot="unified-header-main-row"
            className={cn(
              'relative flex justify-between flex-shrink-0',
              usesMaterialMobileChrome ? 'items-center' : 'items-start',
            )}
            style={{
              height: usesMaterialMobileChrome ? 64 : 54,
              paddingLeft: usesMaterialMobileChrome ? 8 : MOBILE_ROW_PADDING_LEFT,
              paddingRight: usesMaterialMobileChrome ? 12 : MOBILE_ROW_PADDING_RIGHT,
              paddingBottom: usesMaterialMobileChrome ? 0 : 10,
            }}
          >
            {/* Tighter intra-cluster gap on mobile (4px vs 8px). When a
                morph search is active, leading actions other than the
                search input itself hide so the search has the whole row
                — Apple HIG search-presentation pattern (Mail / Notes /
                Files: tapping search clears the nav bar of back chevrons
                and contextual pills). */}
            <div
              className={cn(
                'flex items-center gap-1',
                usesMaterialMobileChrome ? 'min-w-0' : 'min-w-[44px]',
                // While a morph search is active on mobile the cluster
                // grows to fill the row so the input expands edge-to-
                // edge against the trailing pills (filter / sort /
                // cancel). justify-between on the parent still anchors
                // those pills to the right.
                searchActiveOnMobile ? 'flex-1 mr-2' : 'flex-shrink-0',
              )}
            >
              {/* Runs of adjacent icon-only pills in the same group share one
                  glass capsule, same as the trailing cluster — back plus the
                  project-sections toggle read as one navigation control, not
                  two loose buttons. Self-chrome pills (search morphs, native
                  selects) stay outside any capsule. */}
              {visibleMobileLeading.length > 0
                ? groupHeaderRuns(mobileRowFit.leadingFit.visible).flatMap((run, runIdx) => {
                    const nodes = run.map((action) =>
                      LEADING_SELF_CHROME_IDS.has(action.id)
                        ? renderLeadingAction(action)
                        : renderMobileHeaderAction(action, { shared: run.length > 1 }),
                    );
                    const rendered = run.length === 1 || usesMaterialMobileChrome
                      ? nodes
                      : [<HeaderPillGroup key={`grp-${run[0].id}`}>{nodes}</HeaderPillGroup>];
                    return runIdx === 0
                      ? rendered
                      : [
                          <div
                            key={`gap-${run[0].id}`}
                            aria-hidden="true"
                            style={{ width: 6, flexShrink: 0 }}
                          />,
                          ...rendered,
                        ];
                  })
                : usesMaterialMobileChrome
                  ? null
                  : <div style={{ width: 44, height: 44, flexShrink: 0 }} />}
              {/* Surplus leading pills — collapsed into one pill that opens a
                  sheet listing them. Rendered through renderMobileHeaderAction
                  with no subActions so it gets the standard glass pill without
                  the DropdownMenu those normally trigger. */}
              {mobileRowFit.leadingFit.collapsed.length > 0 && renderMobileHeaderAction({
                id: 'header-leading-overflow',
                icon: MoreHorizontal,
                ariaLabel: 'More actions',
                onClick: () => setOverflowSheet('leading'),
                badge: sumOverflowBadges(mobileRowFit.leadingFit.collapsed),
                badgeTone: overflowBadgeTone(mobileRowFit.leadingFit.collapsed),
                group: 'page',
              })}
            </div>

            {/* Title — hidden while a morph search is open so the
                expanded search input doesn't compete for the centre.
                Resource Library's library switcher now rides the
                trailing edge as a compact pill rather than replacing
                the centred page title. */}
            {!searchActiveOnMobile && resolvedPageTitle !== null && (
              <div
                ref={compactTitleRef}
                className={cn(
                  'flex min-w-0 flex-col justify-center px-2 pointer-events-none select-none items-center text-center',
                  usesMaterialMobileChrome && !(currentView === 'messages' && messagesConversationState) ? 'absolute' : 'flex-1',
                )}
                // Material's compact app-bar title is geometrically centred,
                // independent of asymmetric edge actions. Its width reserves
                // the larger fitted action cluster on BOTH sides, so centring
                // cannot put a long title underneath a back or create pill.
                // Conversation titles use the space between their actual
                // controls, keeping long names readable beside call and menu.
                // The non-Material path remains an in-flow flex child because
                // its large-title collapse follows the native toolbar model.
                // When the large title is active this starts at opacity 0
                // (at scrollTop=0); the scroll listener drives it toward 1.
                style={{
                  height: projectScreenLabel ? 54 : 44,
                  opacity: showLargeTitle ? 0 : undefined,
                  ...(usesMaterialMobileChrome && !(currentView === 'messages' && messagesConversationState) ? {
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: `max(0px, calc(100% - ${centredMobileTitleSideInset * 2}px))`,
                  } : null),
                }}
              >
                {projectScreenLabel && (
                  <span className="text-[11px] leading-[14px] opacity-70 max-w-full truncate">
                    {projectScreenLabel}
                  </span>
                )}
                {/* max-w-full is load-bearing, not belt-and-braces. The column
                    parent centres rather than stretches its children, so these
                    spans size to fit-content over unbreakable nowrap text —
                    without the clamp a long title paints straight out of the
                    parent and over both clusters, which is the exact bug the
                    in-flow parent was meant to end. */}
                <span
                  className={cn(
                    'font-semibold max-w-full',
                    isProjectDetailView ? 'line-clamp-2 whitespace-normal break-words' : 'truncate',
                    usesMaterialMobileChrome ? 'font-sans' : 'font-apple',
                  )}
                  style={{
                    // Keep short Material titles at the standard 20px, but
                    // step longer project/document titles down just enough to
                    // remain complete between asymmetric header actions.
                    fontSize: isProjectDetailView ? 15 : usesMaterialMobileChrome
                      ? resolvedPageTitle.length > 16
                        ? 'clamp(1.0625rem, 4.6vw, 1.125rem)'
                        : 20
                      : 17,
                    lineHeight: isProjectDetailView ? '19px' : usesMaterialMobileChrome ? '24px' : '22px',
                    letterSpacing: usesMaterialMobileChrome ? 0 : '-0.43px',
                    color: usesMaterialMobileChrome
                      ? 'var(--header-foreground)'
                      : 'var(--foreground)',
                  }}
                >
                  {resolvedPageTitle || 'Quantis'}
                </span>
                {/* Conversation subtitle — typing / presence / member count */}
                {currentView === 'messages' && messagesMembersState && (
                  <span className={cn('truncate max-w-full text-[11px] leading-[14px] mt-px', usesMaterialMobileChrome ? 'font-sans text-header-muted-foreground' : 'font-apple text-muted-foreground')}>
                    {messagesMembersState.subtitle}
                  </span>
                )}
                {!messagesMembersState && !isDesktopViewport && messagesConversationState?.subtitle && (
                  <span
                    className={cn(
                      'truncate max-w-full',
                      usesMaterialMobileChrome ? 'font-sans' : 'font-apple',
                    )}
                    style={{
                      fontSize: 11,
                      lineHeight: '14px',
                      color: usesMaterialMobileChrome
                        ? 'var(--header-muted-foreground)'
                        : 'var(--muted-foreground)',
                      marginTop: 1,
                    }}
                  >
                    {messagesConversationState.subtitle}
                  </span>
                )}
                {templatesHeaderSubtitle && (
                  <span
                    className={cn(
                      'truncate max-w-full',
                      usesMaterialMobileChrome ? 'font-sans' : 'font-apple',
                    )}
                    style={{
                      fontSize: 11,
                      lineHeight: '14px',
                      color: usesMaterialMobileChrome
                        ? 'var(--header-muted-foreground)'
                        : 'var(--muted-foreground)',
                      marginTop: 1,
                    }}
                  >
                    {templatesHeaderSubtitle}
                  </span>
                )}
                {sitePhotosHeaderSubtitle && (
                  <span
                    className={cn(
                      'truncate max-w-full',
                      usesMaterialMobileChrome ? 'font-sans' : 'font-apple',
                    )}
                    style={{
                      fontSize: 11,
                      lineHeight: '14px',
                      color: usesMaterialMobileChrome
                        ? 'var(--header-muted-foreground)'
                        : 'var(--muted-foreground)',
                      marginTop: 1,
                    }}
                  >
                    {sitePhotosHeaderSubtitle}
                  </span>
                )}
                {rfiHeaderSubtitle && (
                  <span
                    className={cn(
                      'truncate max-w-full',
                      usesMaterialMobileChrome ? 'font-sans' : 'font-apple',
                    )}
                    style={{
                      fontSize: 11,
                      lineHeight: '14px',
                      color: usesMaterialMobileChrome
                        ? 'var(--header-muted-foreground)'
                        : 'var(--muted-foreground)',
                      marginTop: 1,
                    }}
                  >
                    {rfiHeaderSubtitle}
                  </span>
                )}
                {projectDetailTab === 'email' && projectEmailHeaderState.detailOpen && projectEmailHeaderState.subtitle && (
                  <span
                    className={cn(
                      'truncate max-w-full',
                      usesMaterialMobileChrome ? 'font-sans' : 'font-apple',
                    )}
                    style={{
                      fontSize: 11,
                      lineHeight: '14px',
                      color: usesMaterialMobileChrome
                        ? 'var(--header-muted-foreground)'
                        : 'var(--muted-foreground)',
                      marginTop: 1,
                    }}
                  >
                    {projectEmailHeaderState.subtitle}
                  </span>
                )}
                {projectDetailTab === 'takeoff' && takeoffCanvasState?.sheetName && pageTitle && (
                  <span
                    className={cn(
                      'truncate max-w-full',
                      usesMaterialMobileChrome ? 'font-sans' : 'font-apple',
                    )}
                    style={{
                      fontSize: 11,
                      lineHeight: '14px',
                      color: usesMaterialMobileChrome
                        ? 'var(--header-muted-foreground)'
                        : 'var(--muted-foreground)',
                      marginTop: 1,
                    }}
                  >
                    {pageTitle}
                  </span>
                )}
                {interimIdentityLabel && (
                  <span
                    className={cn(
                      'truncate max-w-full',
                      usesMaterialMobileChrome ? 'font-sans' : 'font-apple',
                    )}
                    style={{
                      fontSize: 11,
                      lineHeight: '14px',
                      color: usesMaterialMobileChrome
                        ? 'var(--header-muted-foreground)'
                        : 'var(--muted-foreground)',
                      marginTop: 1,
                    }}
                  >
                    {interimIdentityLabel}
                  </span>
                )}
              </div>
            )}

            {/* Trailing placeholder — mirrors the leading cluster's empty
                slot. Now that the title sits between the two clusters
                rather than on top of them, a view with pills on the left
                and nothing on the right (Add Workspace) would otherwise
                push the title hard against the trailing edge. */}
            {!usesMaterialMobileChrome && !searchActiveOnMobile && resolvedPageTitle !== null && visibleMobileTrailing.length === 0 && mobileTrailingReservedWidth === 0 && (
              <div aria-hidden="true" style={{ width: 44, height: 44, flexShrink: 0 }} />
            )}

            {/* Mirrors the leading cluster's tighter gap on mobile. When
                a morph search is active we filter trailing pills down to
                the search-mode-only ones (sort/view + cancel) so the
                row reads as Apple's search-bar presentation. Other
                contexts hide trailing pills entirely during search. */}
            {(visibleMobileTrailing.length > 0 || mobileTrailingReservedWidth > 0) && (
              // No flex-shrink-0: let the cluster give ground before the title
              // does. It can only ever shrink to its min-content, and icon
              // pills contribute their full 44pt, so this never squashes a
              // touch target — it only reclaims slack from labelled pills that
              // can ellipsise. The fit below is what actually keeps the row
              // honest; this is the safety valve under it.
              <div className="flex items-center gap-1">
                {/* Render trailing actions clustered by group with
                    generous air between groups — no hairlines, no
                    dividers. Apple toolbars don't draw lines between
                    groups; they use space. The strip should read as a
                    curated set of tools, with grouping conveyed
                    entirely through proximity. */}
                {groupHeaderRuns(mobileRowFit.trailingFit.visible).map((run) => {
                  // Same intercept as the desktop path below — without it the
                  // merchant filter renders inert here (no onClick, no
                  // subActions, its panel supplied entirely by the intercept).
                  // Intercepted pills draw their own chrome, so they never join
                  // a shared capsule.
                  const nodes = run.map((action) =>
                    TRAILING_INTERCEPT_IDS.has(action.id)
                      ? renderLeadingAction(action)
                      : renderMobileHeaderAction(action, { shared: run.length > 1 }),
                  );
                  if (run.length === 1 || usesMaterialMobileChrome) return nodes;
                  return <HeaderPillGroup key={`grp-${run[0].id}`}>{nodes}</HeaderPillGroup>;
                })}
                {/* Surplus trailing pills. Views like the Resource Library rate
                    list ship five of these alongside the library picker, which
                    no leading-side cap can make room for. */}
                {mobileRowFit.trailingFit.collapsed.length > 0 && renderMobileHeaderAction({
                  id: 'header-trailing-overflow',
                  icon: MoreVertical,
                  ariaLabel: 'More actions',
                  onClick: () => setOverflowSheet('trailing'),
                  badge: sumOverflowBadges(mobileRowFit.trailingFit.collapsed),
                  badgeTone: overflowBadgeTone(mobileRowFit.trailingFit.collapsed),
                  group: 'page',
                })}
                {/* Conversation kebab — only renders when a chat is open
                    (state arrives via `messages:chat-actions-state`). Sits
                    to the RIGHT of the global trailing pills (filter, bell)
                    so it reads as the conversation-scoped affordance, not
                    another global one. */}
                {currentView === 'messages' && <MessagesChatActionsPill color={usesMaterialMobileChrome ? 'var(--header-foreground)' : 'var(--foreground)'} />}
                {/* UserPlus pill — Employer Details step ONLY. */}
                {currentView === 'add-project' && addProjectEmployerState.available && (
                  <AddProjectEmployerPill
                    hasContractor={addProjectEmployerState.hasContractor}
                    inactiveRoles={addProjectEmployerState.inactiveRoles}
                  />
                )}
                {/* Move-to-bill pill — BOQ pricing only, surfaces while the
                    user has rows multi-selected. Picker can't fit in an icon
                    pill so we use a popover with the bills list. */}
                {boqHeaderState && boqHeaderState.selectedCount > 0 && (
                  <BOQMoveSelectionPill
                    selectedCount={boqHeaderState.selectedCount}
                    bills={boqHeaderState.bills}
                  />
                )}
                {boqApprovalPill}
                {shouldRenderHeaderActions && renderMobileHeaderAction({
                  id: 'quantis-ai',
                  icon: Sparkles,
                  ariaLabel: 'QuantisAI',
                  onClick: onToggleSI ?? si.toggle,
                  expanded: si.open,
                  group: 'app',
                })}
                {/* Support (?) pill — always present on mobile trailing edge */}
                {shouldRenderHeaderActions && renderMobileHeaderAction({
                  id: 'support',
                  icon: LifeBuoy,
                  ariaLabel: 'Support',
                  // Toggle, not open: the panel is a non-modal sheet whose outside-click
                  // dismissal is deliberately disabled, so the pill stays clickable while
                  // it is open and is the way back out.
                  onClick: () => setSupportModalOpen((prev) => !prev),
                  group: 'app',
                })}
              </div>
            )}
          </section>


          {/* iOS-style large title — visible at scrollTop=0, collapses as the user
              scrolls up. Rendered only on top-level views (no back-chevron in the
              leading cluster). The scroll listener drives opacity and translateY
              directly on largeTitleRef without triggering React re-renders. The
              extra clearance for this element is published via --qs-large-title-clearance
              so AppLayout's contentInnerStyle reserves the matching padding-top. */}
          {showLargeTitle && resolvedPageTitle !== null && !searchActiveOnMobile && (
            <div
              ref={largeTitleRef}
              data-slot="unified-header-large-title"
              aria-hidden="true"
              style={{
                height: LARGE_TITLE_HEIGHT,
                display: 'flex',
                alignItems: 'flex-end',
                paddingLeft: 16,
                // Reserve the trailing cluster's footprint so a long title
                // ellipsizes before running under the pills it shares the
                // row with (44px pill + 4px gap each, plus edge padding).
                paddingRight: largeTitleRidesRow && trailingPillCount > 0
                  ? 16 + trailingPillCount * 48
                  : 16,
                paddingBottom: 11,
                // Ride up into the pill row — see largeTitleRidesRow.
                marginTop: largeTitleRidesRow ? -LARGE_TITLE_BARE_ROW_PULL : undefined,
                overflow: 'hidden',
              }}
            >
              <span
                className="font-bold font-apple"
                style={{
                  fontSize: 34,
                  lineHeight: '41px',
                  letterSpacing: '0.37px',
                  color: 'var(--foreground)',
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {resolvedPageTitle || 'Quantis'}
              </span>
            </div>
          )}

</>
        ) : (
          /* ── Desktop: glass-pill action buttons matching mobile ── */
          <section
            ref={desktopSearchLayout.rowRef}
            data-slot="unified-header-main-row"
            data-quantis-window-drag="true"
            onMouseDown={handleHeaderDrag}
            className="relative flex items-center px-4 md:px-6 gap-3"
            style={{ minHeight: 'var(--qs-web-header-height, 64px)', background: 'transparent', color: 'var(--foreground)' }}
          >
            {/* ── Leading: sidebar trigger + parent back action + contextual
                pills (search/filters). Group-aware spacing matches the
                trailing edge: 8px within a cluster, ~22px between
                clusters. */}
            <div
              ref={desktopSearchLayout.leadingRef}
              data-slot="unified-header-leading"
              data-no-window-drag="true"
              className="flex items-center gap-2 flex-shrink-0"
            >
              {shouldRenderDesktopSidebarToggle
                ? renderMobileHeaderAction({
                    id: 'app-sidebar-toggle',
                    icon: SidebarLeftIcon,
                    ariaLabel: 'Show navigation sidebar',
                    onClick: () => window.dispatchEvent(new CustomEvent('app-sidebar:toggle-collapsed')),
                    group: 'app',
                  })
                : null}
              {collapsibleSectionRail.available && renderMobileHeaderAction({
                id: 'project-navigation-toggle',
                icon: SidebarLeftIcon,
                ariaLabel: collapsibleSectionRail.collapsed ? 'Expand project navigation' : 'Collapse project navigation',
                expanded: !collapsibleSectionRail.collapsed,
                onClick: () => window.dispatchEvent(new CustomEvent('section-rail-collapse:toggle')),
                group: 'app',
              })}
              {shouldRenderSecondRailToggle
                ? renderMobileHeaderAction({
                    id: 'section-rail-toggle',
                    icon: SidebarLeftIcon,
                    ariaLabel: sectionRail.hidden ? 'Show section navigation' : 'Hide section navigation',
                    onClick: () => window.dispatchEvent(new CustomEvent('section-rail:toggle')),
                    expanded: !sectionRail.hidden,
                    group: 'app',
                  })
                : null}
              {browserHeaderBackAction && !desktopHistoryShown && (
                <button
                  type="button"
                  data-header-action-id={browserHeaderBackAction.id}
                  data-no-window-drag="true"
                  onClick={browserHeaderBackAction.onClick}
                  aria-label={browserHeaderBackAction.label}
                  title={browserHeaderBackAction.label}
                  className="flex size-9 shrink-0 items-center justify-center rounded-lg text-foreground transition-colors hover:bg-white/10 focus-visible:outline-1 focus-visible:outline-offset-1 focus-visible:outline-foreground"
                >
                  <ChevronLeft className="size-5 shrink-0" />
                </button>
              )}
              <div className="flex items-center gap-2">
                {mobileHeaderConfig.leadingActions.filter(
                  (action) => !DESKTOP_GLOBAL_SEARCH_IDS.has(action.id)
                    && !(browserHeaderBackAction && action.icon === ChevronLeft && action.id !== 'calendar-prev'),
                ).flatMap((action, idx, arr) => {
                  const prevGroup = idx > 0 ? (arr[idx - 1].group ?? 'page') : null;
                  const thisGroup = action.group ?? 'page';
                  const showGap = prevGroup !== null && prevGroup !== thisGroup;
                  const node = renderLeadingAction(action);
                  return showGap
                    ? [
                        <div
                          key={`lead-gap-${action.id}`}
                          aria-hidden="true"
                          style={{ width: 6, flexShrink: 0 }}
                        />,
                        node,
                      ]
                    : [node];
                })}
              </div>
            </div>

            {/* ── Title & Subtitle: page anchor. Left-aligned next to
                leading actions (macOS Big Sur+ toolbar pattern).
                Uses resolvedPageTitle so view-specific overrides
                (e.g. the calendar date label) reach the desktop bar.
                Resource Library keeps the static "Resource Library"
                title here; the library switcher lives in the trailing
                actions area as a compact pill. */}
            {resolvedPageTitle !== null && (
              <div
                style={{ maxWidth: desktopSearchAvailable ? 'var(--qs-header-title-max-width)' : undefined }}
                data-slot="unified-header-title"
                data-projects-title={currentView === 'projects' ? 'true' : undefined}
                className="flex flex-col min-w-0 flex-shrink mr-2"
              >
                <span
                  className="font-semibold font-apple whitespace-nowrap truncate"
                  style={{
                    fontSize: 17,
                    lineHeight: '22px',
                    letterSpacing: '-0.43px',
                    color: 'var(--foreground)',
                  }}
                >
                  {currentView === 'messages' && messagesMembersState?.context ? (
                    <MessagesMembersBreadcrumb context={messagesMembersState.context} title={resolvedPageTitle ?? ''} />
                  ) : (() => {
                    // A screen that titles itself (a report, the RFI register, an open file) still
                    // names its project beside that title, as every other project screen does.
                    const screenOwnsTitle = isProjectDetailView && !!pageTitle && !!resolvedPageTitle && resolvedPageTitle !== pageTitle;
                    const screenLabel = screenOwnsTitle ? resolvedPageTitle : projectScreenLabel;
                    const [name, ref] = splitTitleRef((screenOwnsTitle ? pageTitle : resolvedPageTitle) || 'Quantis');
                    return (
                      <>
                        {screenLabel ? (
                          <>
                            <span>{screenLabel}</span>
                            <span aria-hidden="true" className="mx-4 inline-block h-3.5 border-l border-current align-middle opacity-20" />
                            <span className="text-[13px] font-semibold tracking-normal">
                              {name}{ref && <span className="ml-1.5 font-semibold opacity-75 tabular-nums">{ref}</span>}
                            </span>
                          </>
                        ) : (
                          <>
                            {name}
                            {ref && <span className="ml-1.5 font-semibold opacity-75 tabular-nums">{ref}</span>}
                          </>
                        )}
                        {interimIdentityLabel && (
                          <span className="ml-3 text-[11px] font-normal tracking-normal opacity-70 tabular-nums">
                            {interimIdentityLabel}
                          </span>
                        )}
                      </>
                    );
                  })()}
                </span>
                {!interimIdentityLabel && (projectDetailTab === 'pricing' ? boqHeaderState?.activeBillLabel : ((currentView === 'messages' && messagesMembersState?.subtitle) || templatesHeaderSubtitle || sitePhotosHeaderSubtitle || rfiHeaderSubtitle || pageSubtitle || (projectDetailTab === 'takeoff' && takeoffCanvasState?.sheetName && pageTitle) || ((currentView === 'payroll' || currentView === 'tax') && payrollTaxHeaderState?.sectionSubtitle) || (projectDetailTab === 'team' && projectMembersHeaderState?.hasMembers))) && (
                  <span
                    className="font-apple whitespace-nowrap truncate"
                    style={{
                      fontSize: 11,
                      lineHeight: '14px',
                      color: 'var(--muted-foreground)',
                      marginTop: 1,
                    }}
                  >
                    {projectDetailTab === 'pricing'
                      ? boqHeaderState?.activeBillLabel
                      : currentView === 'messages' && messagesMembersState?.subtitle
                      ? messagesMembersState.subtitle
                      : templatesHeaderSubtitle
                      ? templatesHeaderSubtitle
                      : sitePhotosHeaderSubtitle
                      ? sitePhotosHeaderSubtitle
                      : rfiHeaderSubtitle
                      ? rfiHeaderSubtitle
                      : projectDetailTab === 'takeoff' && takeoffCanvasState?.sheetName && pageTitle
                      ? pageTitle
                      : (currentView === 'payroll' || currentView === 'tax') && payrollTaxHeaderState?.sectionSubtitle
                      ? payrollTaxHeaderState.sectionSubtitle
                      : projectDetailTab === 'team' && projectMembersHeaderState?.hasMembers
                      ? `${projectMembersHeaderState.count} ${projectMembersHeaderState.count === 1 ? 'member' : 'members'}`
                      : pageSubtitle}
                  </span>
                )}
              </div>
            )}

            {desktopSearchAvailable && (
              <div
                data-slot="unified-header-centre"
                data-no-window-drag="true"
                className="absolute top-1/2 z-10 flex -translate-x-1/2 -translate-y-1/2 justify-center"
                style={{
                  left: 'var(--qs-header-search-left, 50%)',
                  width: 'min(480px, var(--qs-header-search-width, 480px))',
                  pointerEvents: 'none',
                }}
              >
                {desktopHistoryShown && (
                  // The native shells own Back themselves; the browser gets the cloud shell's pair.
                  <div
                    className="absolute top-1/2 -translate-y-1/2"
                    style={{ right: 'calc(100% + 8px)', pointerEvents: 'auto' }}
                  ><DesktopHistoryControls backAction={browserHeaderBackAction} /></div>
                )}
                {renderGlobalSearchField()}
                {shouldRenderHeaderActions && (
                  <div
                    data-slot="unified-header-search-ai"
                    className="absolute top-1/2 -translate-y-1/2"
                    style={{ left: 'calc(100% + 8px)', pointerEvents: 'auto' }}
                  >{renderMobileHeaderAction(desktopQuantisAIAction)}</div>
                )}
              </div>
            )}

            {/* Trailing actions — right side. Notifications and Support keep
                the page actions visible while their inspector is open. */}
            <nav
              ref={desktopSearchLayout.actionsRef}
              data-slot="unified-header-actions"
              data-projects-actions={currentView === 'projects' ? 'true' : undefined}
              data-no-window-drag="true"
              className="flex items-center gap-2 ml-auto shrink-0"
              aria-label="Header Actions"
            >
              {/* Page actions stay available while any inspector is open. */}
              {(() => {
                const items = desktopTrailingFit.visible;
                // Runs of adjacent icon-only pills in the same group share one
                // glass capsule; a labelled pill or a group change starts a new
                // one, with a 6px spacer between capsules. That spacer is now
                // the ONLY thing separating groups, since the capsule edge
                // already draws the boundary the old per-pill gap was faking.
                return groupHeaderRuns(items).flatMap((run, runIdx) => {
                  // See TRAILING_INTERCEPT_IDS. Intercepted pills draw their own
                  // chrome (native selects, the merchant panel), so they never
                  // join a shared capsule.
                  const nodes = run.map((action) =>
                    TRAILING_INTERCEPT_IDS.has(action.id)
                      ? <div
                          key={action.id}
                          data-slot="desktop-header-picker"
                          style={{ '--qs-header-picker-width': `${desktopPickerWidth}px`, maxWidth: desktopPickerWidth } as React.CSSProperties}
                        >{renderLeadingAction(action)}</div>
                      : renderMobileHeaderAction(action, { shared: run.length > 1 }),
                  );
                  const rendered = run.length === 1
                    ? nodes
                    : [<HeaderPillGroup key={`grp-${run[0].id}`}>{nodes}</HeaderPillGroup>];
                  return runIdx === 0
                    ? rendered
                    : [
                        <div
                          key={`gap-${run[0].id}`}
                          aria-hidden="true"
                          style={{ width: 6, flexShrink: 0 }}
                        />,
                        ...rendered,
                      ];
                });
              })()}
              {desktopTrailingFit.collapsed.length > 0 && renderMobileHeaderAction({
                id: 'desktop-header-overflow',
                icon: MoreHorizontal,
                ariaLabel: 'More page actions',
                onClick: () => setOverflowSheet('desktop'),
                badge: sumOverflowBadges(desktopTrailingFit.collapsed) || undefined,
                badgeTone: overflowBadgeTone(desktopTrailingFit.collapsed),
              })}
              {/* UserPlus pill — Employer Details step ONLY. See
                  the mobile-section comment above. */}
              {currentView === 'add-project'
                && addProjectEmployerState.available
                && (
                  <AddProjectEmployerPill
                    hasContractor={addProjectEmployerState.hasContractor}
                    inactiveRoles={addProjectEmployerState.inactiveRoles}
                  />
                )}
              {/* Move-to-bill pill — BOQ pricing only, while a selection
                  exists. Same reasoning as the mobile section above. */}
              {boqHeaderState && boqHeaderState.selectedCount > 0 && (
                <BOQMoveSelectionPill
                  selectedCount={boqHeaderState.selectedCount}
                  bills={boqHeaderState.bills}
                />
              )}
              {boqApprovalPill}
              {/* Conversation kebab pill — visible whenever a chat is
                  selected on the messages view. Sits *after* the global
                  trailing pills (filter) so it groups visually with the
                  conversation context, not the page-level chrome. */}
              {currentView === 'messages' && (
                <MessagesChatActionsPill />
              )}

              {/* ── Pill cluster (formerly goo merge zone) ──
                  Inspector buttons remain in this group with their active state.
                  The SVG goo filter that produced the "two blobs merge"
                  effect was disabled because its color-matrix amplifies
                  alpha (0.20 → 1.0) without amplifying the pre-multiplied
                  RGB, so low-opacity glass pills came out rendered at
                  near-opaque dark grey — visually heavier than the rest
                  of the chrome. AnimatePresence still cross-fades the
                  pills with a scale + opacity transition, so the swap is
                  smooth even without the liquid morph. */}
              {/* App-wide cluster (Messages + Notifications) sits as
                  its own group on the trailing edge. Same 8px within-
                  cluster spacing as the page actions, plus 14px of
                  margin-left so the parent's 8px gap totals ~22px
                  between page-actions and app-actions clusters —
                  matches the within-vs-between rhythm everywhere
                  else. */}
              {/* One shared glass capsule for the global trio, the way Liquid
                  Glass groups a run of symbol buttons. `relative` so the fill
                  below can span the run; each pill inside is rendered with
                  shared:true so it does not draw its own. Collapses to nothing
                  when every child is gated off — the inset fill goes negative
                  and clamps to zero width. */}
              {/* No gap: these three share a capsule, and an 8px gap on top of
                  each 44 box spaced them 52px apart — looser than any other
                  grouped run and the airiest thing in the bar. */}
              <div data-slot="header-pill-group" className="relative flex items-center" style={{ marginLeft: railReplacesHeaderShortcuts ? 0 : 14 }}>
                <span
                  aria-hidden="true"
                  className="pointer-events-none"
                  style={{
                    position: 'absolute', left: 4, right: 4, height: 36,
                    top: 'calc(50% - 18px)', zIndex: 0, borderRadius: 296,
                    background: 'var(--header-pill-fill)',
                    mixBlendMode: 'var(--header-pill-blend)' as any,
                    backdropFilter: 'blur(20px) saturate(180%)',
                    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                    boxShadow: '0px 8px 24px 0px rgba(0,0,0,0.08), var(--glass-inset, none)',
                  }}
                />
                {shouldRenderHeaderActions && !desktopSearchAvailable && renderMobileHeaderAction(desktopQuantisAIAction, { shared: true })}
                <AnimatePresence initial={false}>
                  {shouldRenderHeaderActions && onToggleMessages && (
                    <motion.div
                      key="messages"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.34, 1.56, 0.64, 1] }}
                      style={{ originX: 1, originY: 0.5 }}
                    >
                      {renderMobileHeaderAction({
                        id: 'messages',
                        icon: MessageSquare,
                        ariaLabel: 'Messages',
                        onClick: () => onToggleMessages(),
                        badge: messagesUnreadCount,
                        expanded: Boolean(messagePanelOpen),
                      }, { shared: true })}
                    </motion.div>
                  )}
                  {shouldRenderHeaderActions && !railReplacesHeaderShortcuts && (
                    <motion.div
                      key="support-help"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.34, 1.56, 0.64, 1] }}
                      style={{ originX: 1, originY: 0.5 }}
                    >
                      {renderMobileHeaderAction({
                        id: 'support',
                        icon: LifeBuoy,
                        ariaLabel: 'Support',
                        // Toggle, not open - see the note on the other copy.
                        onClick: () => onToggleSupport?.(),
                        group: 'app',
                        expanded: supportInspectorOpen,
                      }, { shared: true })}
                    </motion.div>
                  )}
                  {/* Desktop-gated like the messages block: on mobile the
                      'notifications' action renders null (bottom nav owns it),
                      so mounting a dead Popover.Root there is pure waste. */}
                  {isDesktopViewport && !railReplacesHeaderShortcuts &&
                    mobileHeaderConfig.trailingActions
                      .filter((action) => action.id === 'notifications')
                      .map((action) => (
                        <motion.div
                          key="notif-toggle-bell"
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0, opacity: 0 }}
                          transition={{ duration: 0.35, ease: [0.34, 1.56, 0.64, 1] }}
                          style={{ originX: 0, originY: 0.5 }}
                        >
                          {renderMobileHeaderAction({ ...action, expanded: notificationOpen }, { shared: true })}
                        </motion.div>
                      ))}
                </AnimatePresence>
              </div>

            </nav>

          </section>
        )
      )}

      {/* ── Project list filters ─────────────────────────────────────── */}
      {projectListHeaderProps && (
        <section
          data-slot="unified-header-project-filters"
          className="w-full px-4 md:px-6 py-3"
          style={{ background: 'var(--header-filter-bg)' }}
        >
          <div className="flex items-center justify-between gap-3 w-full">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {shouldShowProjectListSearch ? (
                <Popover
                  open={projectListHeaderProps.searchOpen && projectListHeaderProps.matchingProjects.length > 0}
                  onOpenChange={projectListHeaderProps.setSearchOpen}
                >
                  <PopoverTrigger asChild>
                    <div className="relative flex-1 md:w-[300px] min-w-0">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
                      <Input
                        data-quantis-search-target="true"
                        placeholder="Search projects..."
                        value={projectListHeaderProps.searchQuery}
                        onChange={(e) => {
                          projectListHeaderProps.setSearchQuery(e.target.value);
                          projectListHeaderProps.setSearchOpen(true);
                        }}
                        className="pl-9 h-10 bg-glass-bg border-glass-border rounded-xl transition-all font-apple text-[length:var(--text-search)] focus-visible:border-glass-border focus-visible:ring-0"
                      />
                    </div>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] p-0 border-glass-border bg-glass-bg-strong backdrop-blur-xl"
                    align="start"
                  >
                    <Command className="bg-transparent">
                      <CommandList>
                        <CommandEmpty className="font-apple text-[length:var(--text-menu)] py-4 text-center">
                          No projects found.
                        </CommandEmpty>
                        <CommandGroup>
                          {projectListHeaderProps.matchingProjects.map((project) => {
                            const StageIcon = getStageIcon(project.stage);
                            return (
                              <CommandItem
                                key={project.id}
                                onSelect={() => projectListHeaderProps.onViewProject(project.id, project.name)}
                                className="cursor-pointer p-2 font-apple hover:bg-glass-bg-subtle transition-colors"
                              >
                                <StageIcon className="w-4 h-4 mr-2 text-brand" />
                                <span className="truncate text-[length:var(--text-menu)] font-medium">{project.name}</span>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              ) : null}

              <div className="hidden md:block">
                <Popover open={clientFilterOpen} onOpenChange={setClientFilterOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="h-10 rounded-xl border-glass-border bg-glass-bg hover:bg-glass-bg-strong font-apple text-[length:var(--text-menu)] font-medium transition-colors"
                    >
                      <Building2 className="mr-2 h-4 w-4" />
                      {projectListHeaderProps.clientFilter !== 'all'
                        ? projectListHeaderProps.allClients.find(
                            (c) => c.id === projectListHeaderProps.clientFilter
                          )?.name
                        : 'Filter by Client'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[240px] p-0 border-glass-border bg-glass-bg-strong backdrop-blur-xl">
                    <Command className="bg-transparent">
                      <CommandInput placeholder="Search clients..." className="font-apple" />
                      <CommandList>
                        <CommandItem
                          className="font-apple text-[length:var(--text-menu)]"
                          onSelect={() => { projectListHeaderProps.setClientFilter('all'); setClientFilterOpen(false); }}
                        >
                          All Clients
                        </CommandItem>
                        {projectListHeaderProps.allClients.map((client) => (
                          <CommandItem
                            className="font-apple text-[length:var(--text-menu)]"
                            key={client.id}
                            onSelect={() => { projectListHeaderProps.setClientFilter(client.id); setClientFilterOpen(false); }}
                          >
                            {client.name}
                          </CommandItem>
                        ))}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={projectListHeaderProps.onAddProject}
                className="p-2 text-foreground hover:text-brand transition-all active:scale-90 cursor-pointer"
              >
                <Plus className="w-6 h-6" />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ── Project detail tab sub-nav ───────────────────────────────── */}
      {showProjectSubNav && (
        <section
          data-slot="unified-header-project-tabs"
          className="flex items-center gap-1 px-4 md:px-6 pb-1.5 overflow-x-auto border-t border-border/50"
        >
          {nativeBridge.isNative && (
            <>
              <button
                type="button"
                onClick={() => onNavigate?.('projects')}
                className="flex items-center gap-1 mr-2 px-2 py-1.5 rounded-lg text-nav-item-text hover:text-nav-item-text-hover hover:bg-nav-item-hover-bg transition-colors font-apple shrink-0"
                style={{ fontSize: 'var(--text-label)' }}
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                Projects
              </button>
              <div className="w-px h-4 bg-border shrink-0 mr-1" />
            </>
          )}
          {projectTabItems.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => onProjectTabChange?.(tab.id)}
              className={cn(
                'relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap font-apple font-medium shrink-0',
                'text-[length:var(--text-label)]',
                projectActiveTab === tab.id
                  ? 'bg-nav-item-active-bg text-nav-item-active-text'
                  : 'text-nav-item-text hover:text-nav-item-text-hover hover:bg-nav-item-hover-bg'
              )}
            >
              {tab.icon && <tab.icon className="w-[14px] h-[14px] shrink-0" />}
              {tab.label}
              {typeof tab.badge === 'number' && tab.badge > 0 && (
                <span className="ml-0.5 text-[10px] font-bold leading-none tabular-nums text-brand">
                  {tab.badge > 99 ? '99+' : tab.badge}
                </span>
              )}
            </button>
          ))}
        </section>
      )}

      {/* Email filter popover — anchored to the top-right of the header
          so it visually attaches to the SlidersHorizontal icon there.
          Click-outside dismisses; selections persist across reloads. */}
      {emailFilterMenuOpen && currentView === 'email' && !emailThreadOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setEmailFilterMenuOpen(false)}
            aria-hidden
          />
          <div
            className="absolute right-4 md:right-6 top-[58px] z-50 w-56 rounded-lg border border-border/70 bg-background overflow-hidden"
            style={{ boxShadow: '0 12px 24px -8px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)' }}
          >
            <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80">
              Filter by
            </div>
            {EMAIL_FILTERS.map((f) => {
              const on = emailActiveFilters.has(f.id);
              return (
                <button
                  key={f.id}
                  onClick={() => toggleEmailFilter(f.id)}
                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-foreground/5 text-[13px] text-left"
                >
                  <span className="text-foreground/90">{f.label}</span>
                  <span
                    className={`h-4 w-4 rounded border ${on ? 'bg-primary border-primary text-primary-foreground' : 'border-border bg-background'} inline-flex items-center justify-center`}
                  >
                    {on && (
                      <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M3 8.5l3 3 7-7" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </span>
                </button>
              );
            })}
            {emailActiveFilters.size > 0 && (
              <div className="border-t border-border/60 px-3 py-2">
                <button
                  onClick={() => {
                    setEmailActiveFilters(new Set());
                    persistFilters(new Set());
                    broadcastFilters(new Set());
                  }}
                  className="text-[12px] text-muted-foreground hover:text-foreground"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </header>

    <SupportModal open={supportModalOpen} onClose={() => setSupportModalOpen(false)} />
    </Fragment>
  );
}
